import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiRefreshCw, FiUsers, FiLock, FiEyeOff, FiCalendar, FiBookOpen, FiMessageSquare, FiArrowUp, FiArrowDown, FiCheck, FiX, FiEye, FiUserPlus, FiCheckCircle, FiChevronLeft, FiChevronRight, FiDownload, FiFileText } from "react-icons/fi";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, AreaChart, Area } from "recharts";
import { collection, getDocs, query, where, doc, setDoc, serverTimestamp, runTransaction, limit, orderBy, updateDoc, getDoc } from "firebase/firestore";
import { db, auth } from "../../firebase";
import { initializeApp, getApp } from "firebase/app";
import { EmailAuthProvider, reauthenticateWithCredential, getAuth } from "firebase/auth";
import { generateStaffId, approvalService } from "../../services/firestoreService";
import { useLanguage } from "../../contexts/LanguageContext";
import "../AdminPage.css";
import Portal from "../../components/Portal";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import logoAuraVit from "../../images/auravit_logo.png";
import * as XLSX from "xlsx";

// Mock data for dashboard
// eslint-disable-next-line no-unused-vars
const mockStats = {
  totalUsers: {
    value: 12540,
    change: 8.5,
    isPositive: true
  },
  completedAppointments: {
    value: 3420,
    change: 12,
    isPositive: true
  },
  publishedContent: {
    value: 892,
    change: 15,
    isPositive: true
  },
  resolvedChats: {
    value: 2156,
    change: 6.2,
    isPositive: true
  }
};

// eslint-disable-next-line no-unused-vars
const mockUserDistribution = [
  { name: "Patients", value: 6540, color: "#3b82f6" },
  { name: "Medical Professionals", value: 1890, color: "#10b981" },
  { name: "Content Curators", value: 2340, color: "#f59e0b" },
  { name: "Peer Educators", value: 1770, color: "#8b5cf6" }
];

// Mock data for Content tab
// eslint-disable-next-line no-unused-vars
const mockContentStats = {
  liveContent: {
    value: 156,
    change: 8,
    isPositive: true
  },
  liveAnnouncements: {
    value: 23,
    change: 15,
    isPositive: true
  },
  pendingReview: {
    value: 12,
    change: -20,
    isPositive: false
  },
  archivedContent: {
    value: 89,
    change: 5,
    isPositive: true
  }
};

// Mock data for Appointments tab
// eslint-disable-next-line no-unused-vars
const mockMedicalProfessionals = [
  { name: "Dr. Sarah Johnson", completedAppointments: 45, service: "HIV Testing" },
  { name: "Dr. Michael Chen", completedAppointments: 38, service: "Reproductive Consultation" },
  { name: "Dr. Emily Brown", completedAppointments: 35, service: "Family Planning" },
  { name: "Dr. David Lee", completedAppointments: 32, service: "HIV Testing" },
  { name: "Dr. Maria Garcia", completedAppointments: 28, service: "Reproductive Consultation" }
];

// eslint-disable-next-line no-unused-vars
const mockAppointmentStatus = [
  { service: "Reproductive Consultation", pending: 8, confirmed: 15, accepted: 12 },
  { service: "HIV Testing", pending: 12, confirmed: 20, accepted: 18 },
  { service: "Family Planning", pending: 6, confirmed: 10, accepted: 8 }
];

// eslint-disable-next-line no-unused-vars
const mockWeeklyAppointmentTrends = [
  { week: "W1", completed: 45, cancelledByUser: 5, cancelledByMedical: 3 },
  { week: "W2", completed: 52, cancelledByUser: 7, cancelledByMedical: 4 },
  { week: "W3", completed: 48, cancelledByUser: 6, cancelledByMedical: 3 },
  { week: "W4", completed: 55, cancelledByUser: 8, cancelledByMedical: 5 },
  { week: "W5", completed: 50, cancelledByUser: 7, cancelledByMedical: 4 },
  { week: "W6", completed: 58, cancelledByUser: 9, cancelledByMedical: 5 },
  { week: "W7", completed: 54, cancelledByUser: 8, cancelledByMedical: 4 }
];

// eslint-disable-next-line no-unused-vars
const mockTodayAppointments = {
  total: 24,
  pending: 8,
  confirmed: 10,
  completed: 6
};

// eslint-disable-next-line no-unused-vars
const mockTodaySchedule = [
  { patient: "Ana Santos", service: "HIV Testing", time: "09:00 AM", status: "Confirmed" },
  { patient: "John Cruz", service: "Reproductive Consultation", time: "10:30 AM", status: "Pending" },
  { patient: "Maria Reyes", service: "Family Planning", time: "11:00 AM", status: "Confirmed" },
  { patient: "Carlos Mendoza", service: "HIV Testing", time: "02:00 PM", status: "Completed" },
  { patient: "Elena Garcia", service: "Reproductive Consultation", time: "03:30 PM", status: "Pending" }
];

// eslint-disable-next-line no-unused-vars
const mockServiceDemand = [
  { service: "Reproductive Consultation", demand: 85 },
  { service: "HIV Testing", demand: 72 },
  { service: "Family Planning", demand: 58 }
];

// eslint-disable-next-line no-unused-vars
const mockPeakHours = [
  { day: "Mon", hour: "8AM", value: 3 },
  { day: "Mon", hour: "9AM", value: 8 },
  { day: "Mon", hour: "10AM", value: 12 },
  { day: "Mon", hour: "11AM", value: 10 },
  { day: "Mon", hour: "12PM", value: 6 },
  { day: "Mon", hour: "1PM", value: 5 },
  { day: "Mon", hour: "2PM", value: 7 },
  { day: "Mon", hour: "3PM", value: 9 },
  { day: "Mon", hour: "4PM", value: 8 },
  { day: "Mon", hour: "5PM", value: 4 },
  { day: "Tue", hour: "8AM", value: 4 },
  { day: "Tue", hour: "9AM", value: 9 },
  { day: "Tue", hour: "10AM", value: 14 },
  { day: "Tue", hour: "11AM", value: 11 },
  { day: "Tue", hour: "12PM", value: 7 },
  { day: "Tue", hour: "1PM", value: 6 },
  { day: "Tue", hour: "2PM", value: 8 },
  { day: "Tue", hour: "3PM", value: 10 },
  { day: "Tue", hour: "4PM", value: 9 },
  { day: "Tue", hour: "5PM", value: 5 },
  { day: "Wed", hour: "8AM", value: 5 },
  { day: "Wed", hour: "9AM", value: 10 },
  { day: "Wed", hour: "10AM", value: 15 },
  { day: "Wed", hour: "11AM", value: 12 },
  { day: "Wed", hour: "12PM", value: 8 },
  { day: "Wed", hour: "1PM", value: 7 },
  { day: "Wed", hour: "2PM", value: 9 },
  { day: "Wed", hour: "3PM", value: 11 },
  { day: "Wed", hour: "4PM", value: 10 },
  { day: "Wed", hour: "5PM", value: 6 },
  { day: "Thu", hour: "8AM", value: 4 },
  { day: "Thu", hour: "9AM", value: 8 },
  { day: "Thu", hour: "10AM", value: 13 },
  { day: "Thu", hour: "11AM", value: 11 },
  { day: "Thu", hour: "12PM", value: 6 },
  { day: "Thu", hour: "1PM", value: 5 },
  { day: "Thu", hour: "2PM", value: 7 },
  { day: "Thu", hour: "3PM", value: 9 },
  { day: "Thu", hour: "4PM", value: 8 },
  { day: "Thu", hour: "5PM", value: 4 },
  { day: "Fri", hour: "8AM", value: 3 },
  { day: "Fri", hour: "9AM", value: 7 },
  { day: "Fri", hour: "10AM", value: 11 },
  { day: "Fri", hour: "11AM", value: 9 },
  { day: "Fri", hour: "12PM", value: 5 },
  { day: "Fri", hour: "1PM", value: 4 },
  { day: "Fri", hour: "2PM", value: 6 },
  { day: "Fri", hour: "3PM", value: 8 },
  { day: "Fri", hour: "4PM", value: 7 },
  { day: "Fri", hour: "5PM", value: 3 }
];

// ---------------- Export Modal ----------------
function ExportModal({ isOpen, onClose, onExport, isExporting, activeTab }) {
  const [selectedFormat, setSelectedFormat] = useState(null);

  useEffect(() => {
    const scrollContainer = document.querySelector('.admin-aura-content');
    if (!isOpen) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousContainerOverflow = scrollContainer?.style.overflow ?? '';

    document.body.style.overflow = 'hidden';
    if (scrollContainer) {
      scrollContainer.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      if (scrollContainer) {
        scrollContainer.style.overflow = previousContainerOverflow;
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setSelectedFormat(null);
    }
  }, [isOpen]);

  const handleExport = (format) => {
    setSelectedFormat(format);
    onExport(format);
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay export-modal-overlay">
        <div className="modal-content export-modal-content">
          <div className="modal-header">
            <h3 className="modal-title-white">Export Report</h3>
            <button className="modal-close" onClick={onClose}>
              <FiX />
            </button>
          </div>

          <div className="export-modal-body">
            <p className="export-modal-subtitle">Select export format for {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} tab</p>

            <div className="export-format-list">
              <button
                className={`export-format-btn ${selectedFormat === 'pdf' ? 'selected' : ''}`}
                onClick={() => handleExport('pdf')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap pdf">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">PDF Report</span>
                  <span className="export-format-desc">Presentation-ready report</span>
                </div>
              </button>

              <button
                className={`export-format-btn ${selectedFormat === 'excel' ? 'selected' : ''}`}
                onClick={() => handleExport('excel')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap excel">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">Excel .xlsx</span>
                  <span className="export-format-desc">Structured analytics workbook</span>
                </div>
              </button>

              <button
                className={`export-format-btn ${selectedFormat === 'csv' ? 'selected' : ''}`}
                onClick={() => handleExport('csv')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap csv">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">CSV .csv</span>
                  <span className="export-format-desc">Raw structured data</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}

// ---------------- Approve Modal ----------------
function ApproveModal({ isOpen, onClose, onConfirm, approval }) {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
      setIsLoading(false);
      onClose();
    } catch (error) {
      console.error('Error approving content:', error);
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <button className="modal-close modal-close-top-right" onClick={onClose}>
          <FiX />
        </button>
        <div className="modal-header">
          <h3 className="modal-title-white">Approve Content</h3>
        </div>
        
        <div className="modal-body">
          <div className="modal-icon" style={{ 
            width: "64px", 
            height: "64px", 
            borderRadius: "50%", 
            background: "#d1fae5", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            margin: "0 auto 16px"
          }}>
            <FiCheckCircle size={32} color="#059669" />
          </div>
          
          <p className="modal-subtitle">Are you sure you want to approve this content?</p>
          
          {approval && (
            <div className="content-preview" style={{ 
              background: "#f9fafb", 
              padding: "16px", 
              borderRadius: "8px", 
              marginTop: "16px",
              marginBottom: "16px",
              border: "1px solid #e5e7eb"
            }}>
              <div style={{ fontWeight: "600", marginBottom: "12px", fontSize: "16px" }}>{approval.title}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "8px" }}>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  <strong>Creator:</strong> {approval.author}
                </div>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  <strong>Category:</strong> {approval.category}
                </div>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  <strong>Submitted Date:</strong> {approval.submittedAt ? new Date(approval.submittedAt.toDate()).toLocaleDateString() : "N/A"}
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="modal-footer">
          <button 
            className="modal-btn modal-btn-cancel" 
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button 
            className="modal-btn modal-btn-confirm" 
            onClick={handleConfirm}
            disabled={isLoading}
            style={{
              backgroundColor: "#28a745",
              borderColor: "#28a745",
              color: "white",
              minHeight: "40px",
              height: "40px",
              maxHeight: "40px",
              padding: "8px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              overflow: "hidden",
              width: "auto",
              boxSizing: "border-box"
            }}
          >
            {isLoading ? (
              <span className="loading-spinner loading-spinner-small"></span>
            ) : (
              <>
                <FiCheckCircle style={{ marginRight: '4px' }} />
                Approve
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Cache duration for dashboard data (5 minutes)
const DASHBOARD_CACHE_DURATION = 5 * 60 * 1000;

// ---------------- Rejection Modal ----------------
function RejectionModal({ isOpen, onClose, onConfirm, approval }) {
  const [rejectionReason, setRejectionReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const maxCharacters = 100;

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rejectionReason.trim()) {
      setError("Rejection reason is required");
      return;
    }
    
    setIsLoading(true);
    setError("");
    
    try {
      await onConfirm(rejectionReason.trim());
      setRejectionReason("");
      setIsLoading(false);
      onClose();
    } catch (err) {
      setError("Failed to reject content. Please try again.");
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setRejectionReason("");
    setError("");
    setIsLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <button className="modal-close modal-close-top-right" onClick={handleClose}>
          <FiX />
        </button>
        <div className="modal-header">
          <h3 className="modal-title-white">Reject Content</h3>
        </div>
        
        {approval && (
          <div className="content-preview" style={{ 
            background: "#f9fafb", 
            padding: "16px", 
            borderRadius: "8px", 
            marginTop: "16px",
            marginBottom: "16px",
            border: "1px solid #e5e7eb"
          }}>
            <div style={{ fontWeight: "600", marginBottom: "12px", fontSize: "16px" }}>{approval.title}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "8px" }}>
              <div style={{ fontSize: "14px", color: "#6b7280" }}>
                <strong>Creator:</strong> {approval.author}
              </div>
              <div style={{ fontSize: "14px", color: "#6b7280" }}>
                <strong>Category:</strong> {approval.category}
              </div>
              <div style={{ fontSize: "14px", color: "#6b7280" }}>
                <strong>Submitted Date:</strong> {approval.submittedAt ? new Date(approval.submittedAt.toDate()).toLocaleDateString() : "N/A"}
              </div>
            </div>
          </div>
        )}
        
        <p className="modal-subtitle">Please provide a reason for rejection:</p>
        
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-field">
            <div className="textarea-wrapper">
              <textarea
                value={rejectionReason}
                onChange={(e) => {
                  setRejectionReason(e.target.value);
                  setError("");
                }}
                placeholder="Enter rejection reason..."
                className="modal-textarea full-width"
                required
                rows={4}
                disabled={isLoading}
                maxLength={maxCharacters}
              />
              <div className="character-counter">
                {rejectionReason.length}/{maxCharacters}
              </div>
            </div>
          </div>
          {error && (
            <div className="form-error" style={{ color: "#dc2626", fontSize: "14px", marginTop: "8px" }}>
              {error}
            </div>
          )}
          <div className="modal-footer">
            <button 
              type="button" 
              className="modal-btn modal-btn-cancel" 
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className={`modal-btn modal-btn-confirm reject-btn ${isLoading ? "loading" : ""}`}
              disabled={isLoading}
              style={{
                backgroundColor: "#dc2626",
                borderColor: "#dc2626",
                color: "white",
                minHeight: "40px",
                height: "40px",
                maxHeight: "40px",
                padding: "8px 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                overflow: "hidden",
                width: "auto",
                boxSizing: "border-box"
              }}
            >
              {isLoading ? (
                <span className="loading-spinner loading-spinner-small"></span>
              ) : (
                <>
                  <FiX style={{ marginRight: '4px' }} />
                  Reject
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [showCreateStaff, setShowCreateStaff] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [currentApproval, setCurrentApproval] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [cache, setCache] = useState({
    timestamp: null,
    users: null,
    approvals: null,
    chats: null,
    escalatedChats: null,
    published: null,
    assignServices: null
  });
  const [realStats, setRealStats] = useState({
    totalUsers: 0,
    totalUsersLastMonth: 0,
    userGrowthPercent: null,
    activeUsers: 0,
    inactiveUsers: 0,
    completedAppointments: 0,
    publishedContent: 0,
    deactivatedAccounts: 0,
    resolvedChats: 0,
    userDistribution: {
      patients: 0,
      medicalProfessionals: 0,
      contentCurators: 0,
      peerEducators: 0
    },
    medicalCoverage: {
      hivTesting: 0,
      reproductiveConsultation: 0,
      familyPlanning: 0,
      
    },
    genderDistribution: {
      female: 0,
      male: 0
    },
    patientAssistance: {
      minor: 0,
      senior: 0,
      pwd: 0,
      nrnw: 0,
      other: 0
    },
    contentStats: {
      liveContent: 0,
      liveAnnouncements: 0,
      pendingReview: 0,
      archivedContent: 0
    },
    pendingApprovals: [],
    topPerformingContent: [],
    topEngagedTopics: [],
    chatCategories: [
      { name: "HIV Awareness", value: 0, color: "#3b82f6" },
      { name: "HIV Screening/Testing", value: 0, color: "#10b981" },
      { name: "Birth Control/Implant", value: 0, color: "#f59e0b" },
      { name: "Contraception", value: 0, color: "#ef4444" },
      { name: "Family Planning", value: 0, color: "#8b5cf6" },
      { name: "Gender-Based Violence", value: 0, color: "#ec4899" },
      { name: "Menstrual Health & Hygiene", value: 0, color: "#06b6d4" }
    ],
    hasChatDataToday: false,
    escalationStatus: [
      { category: "Pending Review", pending: 0, underAssessment: 0, resolved: 0 },
      { category: "Under Assessment", pending: 0, underAssessment: 0, resolved: 0 },
      { category: "Resolved", pending: 0, underAssessment: 0, resolved: 0 }
    ],
    topMedicalProfessionals: [],
    appointmentStatusOverview: [],
    weeklyAppointmentTrends: [],
    todayAppointments: {
      total: 0,
      pending: 0,
      confirmed: 0,
      completed: 0
    },
    todaySchedule: [],
    serviceDemand: [],
    peakHours: []
  });
  const [activityTrendsData, setActivityTrendsData] = useState([]);
  const [userRegistrationData, setUserRegistrationData] = useState([]);
  const [contentEngagementData, setContentEngagementData] = useState([]);
  const [contentPublishingData, setContentPublishingData] = useState([]);
  const [chatActivityData, setChatActivityData] = useState([]);
  const [topPeerEducatorsData, setTopPeerEducatorsData] = useState([]);
  const [currentWeekRange, setCurrentWeekRange] = useState('');
  
  // Individual loading states for each section
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingActivityTrends, setLoadingActivityTrends] = useState(true);
  const [loadingUserDistribution, setLoadingUserDistribution] = useState(true);
  const [loadingMedicalCoverage, setLoadingMedicalCoverage] = useState(true);
  const [loadingUserRegistration, setLoadingUserRegistration] = useState(true);
  const [loadingGenderDistribution, setLoadingGenderDistribution] = useState(true);
  const [loadingPatientAssistance, setLoadingPatientAssistance] = useState(true);
  const [loadingContentStats, setLoadingContentStats] = useState(true);
  const [loadingTopPerforming, setLoadingTopPerforming] = useState(true);
  const [loadingTopEngagedTopics, setLoadingTopEngagedTopics] = useState(true);
  const [loadingPendingApprovals, setLoadingPendingApprovals] = useState(true);
  const [loadingChatCategories, setLoadingChatCategories] = useState(true);
  const [loadingEscalationStatus, setLoadingEscalationStatus] = useState(true);
  const [loadingPeerEducators, setLoadingPeerEducators] = useState(true);
  const [loadingAppointmentData, setLoadingAppointmentData] = useState(true);
  const [loadingContentEngagementTrend, setLoadingContentEngagementTrend] = useState(true);
  const [loadingContentPublishingTrend, setLoadingContentPublishingTrend] = useState(true);
  const [loadingChatActivityTrend, setLoadingChatActivityTrend] = useState(true);
  
  const [todaySchedulePage, setTodaySchedulePage] = useState(1);
  const todayScheduleItemsPerPage = 10;

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleApprove = async (approvalId) => {
    try {
      await approvalService.updateApprovalStatus(approvalId, "Approved", {
        approvedAt: serverTimestamp(),
        approvedBy: auth.currentUser?.uid || "Admin"
      });
      setShowApproveModal(false);
      setCurrentApproval(null);
      fetchDashboardData();
    } catch (error) {
      console.error("Error approving content:", error);
      alert("Failed to approve content. Please try again.");
    }
  };

  const handleReject = (approval) => {
    setCurrentApproval(approval);
    setShowRejectModal(true);
  };

  const handleRejectConfirm = async (rejectionReason) => {
    if (!currentApproval) return;
    try {
      await approvalService.updateApprovalStatus(currentApproval.id, "Rejected", {
        rejectedAt: serverTimestamp(),
        rejectedBy: auth.currentUser?.uid || "Admin",
        rejectReason: rejectionReason
      });
      setShowRejectModal(false);
      setCurrentApproval(null);
      fetchDashboardData();
    } catch (error) {
      console.error("Error rejecting content:", error);
      throw new Error("Failed to reject content. Please try again.");
    }
  };

  const fetchDashboardData = async () => {
    try {
      // Set all loading states to true
      setLoadingStats(true);
      setLoadingActivityTrends(true);
      setLoadingUserDistribution(true);
      setLoadingMedicalCoverage(true);
      setLoadingUserRegistration(true);
      setLoadingGenderDistribution(true);
      setLoadingPatientAssistance(true);
      setLoadingContentStats(true);
      setLoadingTopPerforming(true);
      setLoadingTopEngagedTopics(true);
      setLoadingPendingApprovals(true);
      setLoadingChatCategories(true);
      setLoadingEscalationStatus(true);
      setLoadingPeerEducators(true);
      setLoadingAppointmentData(true);
      setLoadingContentEngagementTrend(true);
      setLoadingContentPublishingTrend(true);
      setLoadingChatActivityTrend(true);
      
      const now = Date.now();
      
      // Check if we have valid cached data
      if (cache.timestamp && (now - cache.timestamp) < DASHBOARD_CACHE_DURATION) {
        // Process cached data
        if (cache.users) {
          const allUsers = cache.users;
          const usersOnly = allUsers.filter(user => user.accountType === "User" || user.accountType === "Staff");
          const patients = usersOnly.filter(user => user.accountType === "User");
          const staff = usersOnly.filter(user => user.accountType === "Staff");
          
          const patientCount = patients.length;
          const medicalProfessionals = staff.filter(user => user.roles && user.roles.length > 0 && user.roles[0] === "Medical Professional").length;
          const contentCurators = staff.filter(user => user.roles && user.roles.length > 0 && user.roles[0] === "Content Curator").length;
          const peerEducators = staff.filter(user => user.roles && user.roles.length > 0 && user.roles[0] === "Peer Educator").length;
          
          const totalUsers = patientCount + medicalProfessionals + contentCurators + peerEducators;
          const deactivatedAccounts = usersOnly.filter(user => user.status && user.status.toLowerCase() === "deactivated").length;
          const activeUsers = usersOnly.filter(user => user.status && user.status.toLowerCase() === "active").length;
          const inactiveUsers = usersOnly.filter(user => user.status && user.status.toLowerCase() === "inactive").length;

          // ── Month-over-month growth ───────────────────────────────────────
          const _now = new Date();
          const _startOfThisMonth = new Date(_now.getFullYear(), _now.getMonth(), 1);
          const _startOfNextMonth = new Date(_now.getFullYear(), _now.getMonth() + 1, 1);

          // Total users at the end of last month (users created before this month started)
          const totalUsersLastMonth = usersOnly.filter(user => {
            const _ca = user.createdAt?.toDate ? user.createdAt.toDate() : user.createdAt ? new Date(user.createdAt.seconds * 1000) : null;
            return _ca && _ca < _startOfThisMonth;
          }).length;

          // Total users at the end of this month (users created before next month starts)
          const totalUsersThisMonth = usersOnly.filter(user => {
            const _ca = user.createdAt?.toDate ? user.createdAt.toDate() : user.createdAt ? new Date(user.createdAt.seconds * 1000) : null;
            return _ca && _ca < _startOfNextMonth;
          }).length;

          // Calculate growth percentage: ((this month total - last month total) / last month total) * 100
          const userGrowthPercent = totalUsersLastMonth === 0
            ? (totalUsersThisMonth > 0 ? 100 : null)
            : parseFloat((((totalUsersThisMonth - totalUsersLastMonth) / totalUsersLastMonth) * 100).toFixed(1));

          // Calculate gender distribution
          let female = 0, male = 0;
          patients.forEach(user => {
            const sexValue = user.sex || user.personalInformation?.sex || '';
            const normalizedSex = String(sexValue).trim().toLowerCase();
            if (normalizedSex === "female") female++;
            else if (normalizedSex === "male") male++;
          });
          
          // Calculate patient assistance demographics
          let minor = 0, senior = 0, pwd = 0, nrnw = 0, other = 0;
          patients.forEach(user => {
            const assistance = user.assistance;
            if (assistance && assistance.assistanceRequired === "yes" && assistance.reasonForAssistance) {
              const reasons = Array.isArray(assistance.reasonForAssistance) ? assistance.reasonForAssistance : [assistance.reasonForAssistance];
              reasons.forEach(reason => {
                if (reason === "minor") minor++;
                else if (reason === "senior") senior++;
                else if (reason === "pwd") pwd++;
                else if (reason === "nrnw") nrnw++;
                else other++;
              });
            }
          });
          
          // Calculate medical coverage
          let hivTesting = 0, reproductiveConsultation = 0, familyPlanning = 0;
          if (cache.assignServices && cache.assignServices.length > 0) {
            cache.assignServices.forEach(doc => {
              const service = doc.service;
              if (service === "STI Testing / Screening") hivTesting++;
              else if (service === "Reproductive Health Consultation") reproductiveConsultation++;
              else if (service === "Family Planning / Contraceptive Services") familyPlanning++;
            });
          } else {
            staff.forEach(user => {
              const services = user.assignServices || [];
              services.forEach(service => {
                if (service === "STI Testing / Screening") hivTesting++;
                else if (service === "Reproductive Health Consultation") reproductiveConsultation++;
                else if (service === "Family Planning / Contraceptive Services") familyPlanning++;
              });
            });
          }
          
          // Process content stats from cached approvals
          let liveContent = 0, liveAnnouncements = 0, pendingReview = 0, archivedContent = 0;
          if (cache.approvals) {
            cache.approvals.forEach(doc => {
              const status = doc.status;
              const category = doc.category;
              if (status === "Approved") {
                if (category === "Community Outreach Programs") liveAnnouncements++;
                else liveContent++;
              } else if (status === "Pending") pendingReview++;
              else if (status === "Archived") archivedContent++;
            });
          }
          
          // Process top performing content from cached published
          let topPerformingContent = [], topEngagedTopics = [];
          if (cache.published) {
            const contentItems = [];
            const categoryEngagement = {};
            
            cache.published.forEach(doc => {
              const stats = doc.stats || { views: 0, shares: 0, bookmarks: 0 };
              const totalEngagement = (stats.views || 0) + (stats.bookmarks || 0) + (stats.shares || 0);
              
              contentItems.push({
                id: doc.id,
                ...doc,
                totalEngagement,
                views: stats.views || 0,
                bookmarks: stats.bookmarks || 0,
                shares: stats.shares || 0
              });
              
              const category = doc.category || "Uncategorized";
              if (!categoryEngagement[category]) categoryEngagement[category] = 0;
              categoryEngagement[category] += totalEngagement;
            });
            
            contentItems.sort((a, b) => b.totalEngagement - a.totalEngagement);
            topPerformingContent = contentItems.slice(0, 3);
            
            topEngagedTopics = Object.entries(categoryEngagement)
              .map(([category, total]) => ({ topic: category, engagement: total }))
              .sort((a, b) => b.engagement - a.engagement)
              .slice(0, 5);
          }
          
          // Process chat categories from cached chats
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);
          
          const categoryMap = {
            "HIV Awareness": 0,
            "HIV Screening/Testing": 0,
            "Birth Control/Implant": 0,
            "Contraception": 0,
            "Family Planning": 0,
            "Gender-Based Violence": 0,
            "Menstrual Health & Hygiene": 0
          };
          
          if (cache.chats) {
            cache.chats.forEach(doc => {
              const chat = doc;
              const createdAt = chat.createdAt?.toDate();
              if (createdAt && createdAt >= today && createdAt < tomorrow) {
                const topic = chat.inquiryTopic;
                if (topic && categoryMap.hasOwnProperty(topic)) categoryMap[topic]++;
              }
            });
          }
          
          if (cache.escalatedChats) {
            cache.escalatedChats.forEach(doc => {
              const chat = doc;
              const createdAt = chat.createdAt?.toDate();
              if (createdAt && createdAt >= today && createdAt < tomorrow) {
                const topic = chat.escalationTopic;
                if (topic && categoryMap.hasOwnProperty(topic)) categoryMap[topic]++;
              }
            });
          }
          
          const chatCategories = Object.entries(categoryMap).map(([name, value], index) => ({
            name,
            value,
            color: ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4"][index]
          }));
          
          // Process escalation status from cached escalatedChats
          let escalationStatus = [
            { category: "Pending Review", pending: 0, underAssessment: 0, resolved: 0 },
            { category: "Under Assessment", pending: 0, underAssessment: 0, resolved: 0 },
            { category: "Resolved", pending: 0, underAssessment: 0, resolved: 0 }
          ];
          
          if (cache.escalatedChats) {
            cache.escalatedChats.forEach(chat => {
              const status = chat.escalationStatus;
              if (status === "pending-review") escalationStatus[0].pending++;
              else if (status === "under-assessment") escalationStatus[1].underAssessment++;
              else if (status === "resolved") escalationStatus[2].resolved++;
            });
          }
          
          // Calculate resolved chats
          let resolvedChatsCount = 0;
          if (cache.chats) {
            resolvedChatsCount += cache.chats.filter(chat => chat.status === "resolved").length;
          }
          if (cache.escalatedChats) {
            resolvedChatsCount += cache.escalatedChats.filter(chat => chat.escalationStatus === "resolved").length;
          }
          
          setRealStats({
            totalUsers,
            totalUsersLastMonth,
            userGrowthPercent,
            activeUsers,
            inactiveUsers,
            completedAppointments: cache.approvals ? cache.approvals.filter(doc => doc.status === "Approved").length : 0,
            publishedContent: cache.approvals ? cache.approvals.filter(doc => doc.status === "Approved").length : 0,
            deactivatedAccounts,
            resolvedChats: resolvedChatsCount,
            userDistribution: {
              patients: patientCount,
              medicalProfessionals,
              contentCurators,
              peerEducators
            },
            medicalCoverage: {
              hivTesting,
              reproductiveConsultation,
              familyPlanning
            },
            genderDistribution: {
              female,
              male
            },
            patientAssistance: {
              minor,
              senior,
              pwd,
              nrnw,
              other
            },
            contentStats: {
              liveContent,
              liveAnnouncements,
              pendingReview,
              archivedContent
            },
            pendingApprovals: cache.approvals ? cache.approvals.filter(doc => doc.status === "Pending").slice(0, 5).map(doc => ({ id: doc.id, ...doc })) : [],
            topPerformingContent: topPerformingContent || [],
            topEngagedTopics: topEngagedTopics || [],
            chatCategories,
            escalationStatus,
            topMedicalProfessionals: [],
            appointmentStatusOverview: [],
            weeklyAppointmentTrends: [],
            todayAppointments: {
              total: 0,
              pending: 0,
              confirmed: 0,
              completed: 0
            },
            todaySchedule: [],
            serviceDemand: [],
            peakHours: []
          });
          
          // Set all loading states to false for cached data
          setLoadingStats(false);
          setLoadingUserDistribution(false);
          setLoadingMedicalCoverage(false);
          setLoadingGenderDistribution(false);
          setLoadingPatientAssistance(false);
          setLoadingContentStats(false);
          setLoadingTopPerforming(false);
          setLoadingTopEngagedTopics(false);
          setLoadingPendingApprovals(false);
          setLoadingChatCategories(false);
          setLoadingEscalationStatus(false);
          setLoadingAppointmentData(false);
          return;
        }
      }
      
      // Fetch fresh data with optimized parallel queries
      // Execute independent queries in parallel
      const [
        usersSnapshot,
        approvalsSnapshot,
        chatsSnapshot,
        escalatedChatsSnapshot,
        publishedSnapshot,
        assignServicesSnapshot
      ] = await Promise.all([
        getDocs(query(collection(db, "users"), limit(1000))), // Add pagination for users
        getDocs(query(collection(db, "approvals"), limit(500))), // Add pagination for approvals
        getDocs(query(collection(db, "chats"), where("createdAt", ">=", new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)), limit(500))), // Last 7 days only
        getDocs(query(collection(db, "escalatedChats"), limit(200))), // Add pagination for escalated chats
        getDocs(query(collection(db, "published"), limit(100))), // Add pagination for published
        getDocs(query(collection(db, "assignServices"), limit(100))) // Add pagination for assignServices
      ]);
      
      // Extract data from snapshots
      const allUsers = usersSnapshot.docs.map(doc => doc.data());
      
      // Update cache
      setCache({
        timestamp: Date.now(),
        users: allUsers,
        approvals: approvalsSnapshot.docs.map(doc => doc.data()),
        chats: chatsSnapshot.docs.map(doc => doc.data()),
        escalatedChats: escalatedChatsSnapshot.docs.map(doc => doc.data()),
        published: publishedSnapshot.docs.map(doc => doc.data()),
        assignServices: assignServicesSnapshot.docs.map(doc => doc.data())
      });
      
      // Filter by accountType - exclude Admin accounts
      const usersOnly = allUsers.filter(user => user.accountType === "User" || user.accountType === "Staff");
      const patients = usersOnly.filter(user => user.accountType === "User");
      const staff = usersOnly.filter(user => user.accountType === "Staff");
    
      
      // Count by role - handle staff with multiple roles by using first role as primary
      const patientCount = patients.length;
      const medicalProfessionals = staff.filter(user => user.roles && user.roles.length > 0 && user.roles[0] === "Medical Professional").length;
      const contentCurators = staff.filter(user => user.roles && user.roles.length > 0 && user.roles[0] === "Content Curator").length;
      const peerEducators = staff.filter(user => user.roles && user.roles.length > 0 && user.roles[0] === "Peer Educator").length;
      
      const totalUsers = patientCount + medicalProfessionals + contentCurators + peerEducators;

      // ── Month-over-month growth ───────────────────────────────────────────
      const _now = new Date();
      const _startOfThisMonth = new Date(_now.getFullYear(), _now.getMonth(), 1);
      const _startOfNextMonth = new Date(_now.getFullYear(), _now.getMonth() + 1, 1);

      // Total users at the end of last month (users created before this month started)
      const totalUsersLastMonth = usersOnly.filter(user => {
        const _ca = user.createdAt?.toDate ? user.createdAt.toDate() : user.createdAt ? new Date(user.createdAt.seconds * 1000) : null;
        return _ca && _ca < _startOfThisMonth;
      }).length;

      // Total users at the end of this month (users created before next month starts)
      const totalUsersThisMonth = usersOnly.filter(user => {
        const _ca = user.createdAt?.toDate ? user.createdAt.toDate() : user.createdAt ? new Date(user.createdAt.seconds * 1000) : null;
        return _ca && _ca < _startOfNextMonth;
      }).length;

      // Calculate growth percentage: ((this month total - last month total) / last month total) * 100
      const userGrowthPercent = totalUsersLastMonth === 0
        ? (totalUsersThisMonth > 0 ? 100 : null)
        : parseFloat((((totalUsersThisMonth - totalUsersLastMonth) / totalUsersLastMonth) * 100).toFixed(1));

      // Count deactivated accounts (exclude admin accounts) - case insensitive
      const deactivatedAccounts = usersOnly.filter(user => 
        user.status && user.status.toLowerCase() === "deactivated"
      ).length;
      
      // Count active users (exclude admin accounts) - case insensitive
      const activeUsers = usersOnly.filter(user => 
        user.status && user.status.toLowerCase() === "active"
      ).length;
      
      // Count inactive users (exclude admin accounts) - case insensitive
      const inactiveUsers = usersOnly.filter(user => 
        user.status && user.status.toLowerCase() === "inactive"
      ).length;
      
      // ── All stats below are computed from data already fetched above ────────
      // This avoids every secondary getDocs call that was causing 60s load times.

      // ── Gender distribution ───────────────────────────────────────────────
      let female = 0, male = 0;
      patients.forEach(user => {
        const sexValue = user.sex || user.personalInformation?.sex || '';
        const normalizedSex = String(sexValue).trim().toLowerCase();
        if (normalizedSex === "female") female++;
        else if (normalizedSex === "male") male++;
      });

      // ── Patient assistance demographics ───────────────────────────────────
      let minor = 0, senior = 0, pwd = 0, nrnw = 0, other = 0;
      patients.forEach(user => {
        const assistance = user.assistance;
        if (assistance?.assistanceRequired === "yes" && assistance?.reasonForAssistance) {
          const reasons = Array.isArray(assistance.reasonForAssistance)
            ? assistance.reasonForAssistance
            : [assistance.reasonForAssistance];
          reasons.forEach(reason => {
            if (reason === "minor") minor++;
            else if (reason === "senior") senior++;
            else if (reason === "pwd") pwd++;
            else if (reason === "nrnw") nrnw++;
            else other++;
          });
        }
      });

      const cachedApprovals   = approvalsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      const cachedChats7d     = chatsSnapshot.docs.map(d => d.data());
      const cachedEscalated   = escalatedChatsSnapshot.docs.map(d => d.data());
      const cachedPublished   = publishedSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      const cachedAssignSvc   = assignServicesSnapshot.docs.map(d => d.data());

      // Fetch appointments once — used for all appointment-related stats below
      let cachedAppointments = [];
      try {
        const apptSnap = await getDocs(query(collection(db, "appointments"), limit(2000)));
        cachedAppointments = apptSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (e) {
        console.error("Error fetching appointments:", e);
      }

      // ── Completed appointments count ─────────────────────────────────────
      const completedAppointments = cachedAppointments.filter(a => a.status === "COMPLETED").length;

      // ── Published content count ──────────────────────────────────────────
      const publishedContent = cachedApprovals.filter(a => a.status === "Approved").length;

      // ── Resolved chats count ─────────────────────────────────────────────
      const resolvedChatsCount =
        cachedChats7d.filter(c => c.status === "resolved").length +
        cachedEscalated.filter(c => c.escalationStatus === "resolved").length;

      // ── Today's chat categories ──────────────────────────────────────────
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const categoryMap = {
        "HIV Awareness": 0,
        "HIV Screening/Testing": 0,
        "Birth Control/Implant": 0,
        "Contraception": 0,
        "Family Planning": 0,
        "Gender-Based Violence": 0,
        "Menstrual Health & Hygiene": 0
      };

      // Use the already-fetched 7-day chats — filter to today client-side
      cachedChats7d.forEach(chat => {
        const createdAt = chat.createdAt?.toDate?.();
        if (createdAt && createdAt >= today && createdAt < tomorrow) {
          const topic = chat.inquiryTopic;
          if (topic && Object.prototype.hasOwnProperty.call(categoryMap, topic)) categoryMap[topic]++;
        }
      });
      cachedEscalated.forEach(chat => {
        const createdAt = chat.createdAt?.toDate?.();
        if (createdAt && createdAt >= today && createdAt < tomorrow) {
          const topic = chat.escalationTopic;
          if (topic && Object.prototype.hasOwnProperty.call(categoryMap, topic)) categoryMap[topic]++;
        }
      });

      const chatCategories = Object.entries(categoryMap).map(([name, value], index) => ({
        name,
        value,
        color: ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4"][index]
      }));
      const totalChatsToday = Object.values(categoryMap).reduce((s, v) => s + v, 0);
      const hasChatDataToday = totalChatsToday > 0;

      // ── Escalation status ─────────────────────────────────────────────────
      const escalationStatus = [
        { category: "Pending Review",    pending: 0, underAssessment: 0, resolved: 0 },
        { category: "Under Assessment",  pending: 0, underAssessment: 0, resolved: 0 },
        { category: "Resolved",          pending: 0, underAssessment: 0, resolved: 0 }
      ];
      cachedEscalated.forEach(chat => {
        const s = chat.escalationStatus;
        if (s === "pending-review")    escalationStatus[0].pending++;
        else if (s === "under-assessment") escalationStatus[1].underAssessment++;
        else if (s === "resolved")         escalationStatus[2].resolved++;
      });

      // ── Content stats ─────────────────────────────────────────────────────
      let liveContent = 0, liveAnnouncements = 0, pendingReview = 0, archivedContent = 0;
      cachedApprovals.forEach(d => {
        if (d.status === "Approved") {
          d.category === "Community Outreach Programs" ? liveAnnouncements++ : liveContent++;
        } else if (d.status === "Pending")  pendingReview++;
        else if (d.status === "Archived")   archivedContent++;
      });

      // ── Pending approvals (latest 5) ──────────────────────────────────────
      const pendingApprovals = cachedApprovals
        .filter(a => a.status === "Pending")
        .sort((a, b) => {
          const at = a.submittedAt?.seconds ?? 0;
          const bt = b.submittedAt?.seconds ?? 0;
          return bt - at;
        })
        .slice(0, 5);

      // ── Top performing content + top engaged topics ───────────────────────
      let topPerformingContent = [];
      let topEngagedTopics = [];
      const contentItems = [];
      const categoryEngagement = {};
      cachedPublished.forEach(data => {
        const stats = data.stats || { views: 0, shares: 0, bookmarks: 0 };
        const totalEngagement = (stats.views || 0) + (stats.bookmarks || 0) + (stats.shares || 0);
        contentItems.push({ ...data, totalEngagement, views: stats.views || 0, bookmarks: stats.bookmarks || 0, shares: stats.shares || 0 });
        const cat = data.category || "Uncategorized";
        categoryEngagement[cat] = (categoryEngagement[cat] || 0) + totalEngagement;
      });
      contentItems.sort((a, b) => b.totalEngagement - a.totalEngagement);
      topPerformingContent = contentItems.slice(0, 3);
      topEngagedTopics = Object.entries(categoryEngagement)
        .map(([topic, engagement]) => ({ topic, engagement }))
        .sort((a, b) => b.engagement - a.engagement)
        .slice(0, 5);

      // ── Medical coverage ──────────────────────────────────────────────────
      const staffUsers = staff;
      let hivTesting = 0, reproductiveConsultation = 0, familyPlanning = 0;
      if (cachedAssignSvc.length > 0) {
        cachedAssignSvc.forEach(d => {
          if (d.service === "STI Testing / Screening") hivTesting++;
          else if (d.service === "Reproductive Health Consultation") reproductiveConsultation++;
          else if (d.service === "Family Planning / Contraceptive Services") familyPlanning++;
        });
      } else {
        staffUsers.forEach(user => {
          (user.assignServices || []).forEach(svc => {
            if (svc === "STI Testing / Screening") hivTesting++;
            else if (svc === "Reproductive Health Consultation") reproductiveConsultation++;
            else if (svc === "Family Planning / Contraceptive Services") familyPlanning++;
          });
        });
      }

      // ── Top medical professionals ─────────────────────────────────────────
      let topMedicalProfessionals = [];
      const apptCounts = {};
      cachedAppointments.filter(a => a.status === "COMPLETED").forEach(a => {
        if (a.proId) apptCounts[a.proId] = (apptCounts[a.proId] || 0) + 1;
      });
      topMedicalProfessionals = staff
        .filter(u => u.role === "Medical Professional" ||
          (u.roles?.includes?.("Medical Professional")) ||
          (u.servicesAssigned === true && u.assignServices?.length > 0))
        .map(u => ({
          name: u.fullName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.displayName || "Unknown",
          completedAppointments: apptCounts[u.uid] || 0,
          assignedServices: (u.assignServices || []).join(", ")
        }))
        .sort((a, b) => b.completedAppointments - a.completedAppointments)
        .slice(0, 5);

      // ── Appointment status overview ───────────────────────────────────────
      let appointmentStatusOverview = [];
      const statusCounts = {};
      cachedAppointments.filter(a => ["PENDING","CONFIRMED","ACCEPTED"].includes(a.status)).forEach(a => {
        const svc = a.serviceName || "Unknown Service";
        if (!statusCounts[svc]) statusCounts[svc] = { pending: 0, confirmed: 0, accepted: 0 };
        if (a.status === "PENDING") statusCounts[svc].pending++;
        else if (a.status === "CONFIRMED") statusCounts[svc].confirmed++;
        else if (a.status === "ACCEPTED") statusCounts[svc].accepted++;
      });
      appointmentStatusOverview = Object.entries(statusCounts)
        .map(([service, counts]) => ({ service, ...counts }))
        .sort((a, b) => a.service.localeCompare(b.service));

      // ── Weekly appointment trends (last 4 completed weeks) ────────────────
      let weeklyAppointmentTrends = [];
      {
        const now = new Date();
        const daysUntilSunday = (7 - now.getDay()) % 7 || 7;
        const endOfLastWeek = new Date(now);
        endOfLastWeek.setDate(now.getDate() - daysUntilSunday);
        endOfLastWeek.setHours(23, 59, 59, 999);
        const startOfPeriod = new Date(endOfLastWeek);
        startOfPeriod.setDate(endOfLastWeek.getDate() - 27);
        startOfPeriod.setHours(0, 0, 0, 0);

        const weeklyData = Array.from({ length: 4 }, (_, i) => {
          const weekStart = new Date(startOfPeriod);
          weekStart.setDate(startOfPeriod.getDate() + i * 7);
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 6);
          weekEnd.setHours(23, 59, 59, 999);
          return {
            week: `${weekStart.toLocaleDateString('en-US',{month:'short',day:'numeric'})} - ${weekEnd.toLocaleDateString('en-US',{month:'short',day:'numeric'})}`,
            weekStart, weekEnd, completed: 0, cancelledByUser: 0, cancelledByMedical: 0
          };
        });

        cachedAppointments
          .filter(a => ["COMPLETED","CANCELLED_BY_PRO","CANCELLED_BY_USER"].includes(a.status) && a.date)
          .forEach(a => {
            const d = new Date(a.date);
            for (const week of weeklyData) {
              if (d >= week.weekStart && d <= week.weekEnd) {
                if (a.status === "COMPLETED") week.completed++;
                else if (a.status === "CANCELLED_BY_USER") week.cancelledByUser++;
                else if (a.status === "CANCELLED_BY_PRO") week.cancelledByMedical++;
                break;
              }
            }
          });
        weeklyAppointmentTrends = weeklyData.map(({ week, completed, cancelledByUser, cancelledByMedical }) =>
          ({ week, completed, cancelledByUser, cancelledByMedical }));
      }

      // ── Today's appointments ──────────────────────────────────────────────
      const todayStr = new Date().toISOString().split('T')[0];
      const todayAppts = cachedAppointments.filter(a => a.date === todayStr);
      const todayAppointments = {
        total: todayAppts.length,
        pending:   todayAppts.filter(a => a.status === "PENDING").length,
        confirmed: todayAppts.filter(a => a.status === "CONFIRMED").length,
        completed: todayAppts.filter(a => a.status === "COMPLETED").length
      };
      let todaySchedule = todayAppts.map(a => ({
        patient: a.patientName || "Unknown",
        service: a.serviceName || "Unknown Service",
        time: a.time || "Unknown",
        status: a.status || "Unknown"
      })).sort((a, b) => {
        const toMin = t => {
          const [time, period] = t.split(' ');
          const [h, m] = time.split(':').map(Number);
          return (period === 'PM' && h !== 12 ? h + 12 : h) * 60 + m;
        };
        return toMin(a.time) - toMin(b.time);
      });

      // ── Service demand + peak hours ───────────────────────────────────────
      const svcCounts = {};
      const peakHoursMap = {};
      cachedAppointments.forEach(a => {
        const svc = a.serviceName || "Unknown Service";
        svcCounts[svc] = (svcCounts[svc] || 0) + 1;
        if (a.time && a.date) {
          const day = new Date(a.date).toLocaleDateString('en-US', { weekday: 'short' });
          const normalizedHour = a.time.replace(':00 ', 'AM').replace(':30 ', 'AM')
            .replace(':00 PM', 'PM').replace(':30 PM', 'PM');
          const key = `${day}-${normalizedHour}`;
          peakHoursMap[key] = (peakHoursMap[key] || 0) + 1;
        }
      });
      const serviceDemand = Object.entries(svcCounts)
        .map(([service, demand]) => ({ service, demand }))
        .sort((a, b) => b.demand - a.demand);
      const dayOrder = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
      const peakHours = Object.entries(peakHoursMap)
        .map(([key, value]) => { const [day, hour] = key.split('-'); return { day, hour, value }; })
        .sort((a, b) => (dayOrder[a.day] ?? 9) - (dayOrder[b.day] ?? 9) || a.hour.localeCompare(b.hour));

      setRealStats({
        totalUsers,
        totalUsersLastMonth,
        userGrowthPercent,
        activeUsers,
        inactiveUsers,
        completedAppointments,
        publishedContent,
        deactivatedAccounts,
        resolvedChats: resolvedChatsCount,
        userDistribution: {
          patients: patientCount,
          medicalProfessionals,
          contentCurators,
          peerEducators
        },
        medicalCoverage: {
          hivTesting,
          reproductiveConsultation,
          familyPlanning,

        },
        genderDistribution: {
          female,
          male
        },
        patientAssistance: {
          minor,
          senior,
          pwd,
          nrnw,
          other
        },
        contentStats: {
          liveContent,
          liveAnnouncements,
          pendingReview,
          archivedContent
        },
        pendingApprovals: pendingApprovals || [],
        topPerformingContent: topPerformingContent || [],
        topEngagedTopics: topEngagedTopics || [],
        chatCategories,
        hasChatDataToday,
        escalationStatus,
        topMedicalProfessionals: topMedicalProfessionals || [],
        appointmentStatusOverview: appointmentStatusOverview || [],
        weeklyAppointmentTrends: weeklyAppointmentTrends || [],
        todayAppointments,
        todaySchedule: todaySchedule || [],
        serviceDemand: serviceDemand || [],
        peakHours: peakHours || []
      });

      // Set stats-related loading to false
      setLoadingStats(false);
      setLoadingUserDistribution(false);
      setLoadingMedicalCoverage(false);
      setLoadingGenderDistribution(false);
      setLoadingPatientAssistance(false);
      setLoadingContentStats(false);
      setLoadingTopPerforming(false);
      setLoadingTopEngagedTopics(false);
      setLoadingPendingApprovals(false);
      setLoadingChatCategories(false);
      setLoadingEscalationStatus(false);
      setLoadingAppointmentData(false);

      // Run all trend fetches in parallel instead of sequentially
      await Promise.all([
        fetchActivityTrends(),
        fetchUserRegistrationTrend(),
        fetchContentEngagementTrend(),
        fetchContentPublishingTrend(),
        fetchChatActivityTrend(),
        fetchTopPeerEducators()
      ]);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      // Set all loading to false on error
      setLoadingStats(false);
      setLoadingActivityTrends(false);
      setLoadingUserDistribution(false);
      setLoadingMedicalCoverage(false);
      setLoadingUserRegistration(false);
      setLoadingGenderDistribution(false);
      setLoadingPatientAssistance(false);
      setLoadingContentStats(false);
      setLoadingTopPerforming(false);
      setLoadingTopEngagedTopics(false);
      setLoadingPendingApprovals(false);
      setLoadingChatCategories(false);
      setLoadingEscalationStatus(false);
      setLoadingPeerEducators(false);
      setLoadingAppointmentData(false);
      setLoadingContentEngagementTrend(false);
      setLoadingContentPublishingTrend(false);
      setLoadingChatActivityTrend(false);
    }
  };

  const fetchActivityTrends = async () => {
    try {
      // Calculate the start of the most recent completed week (4 weeks back)
      const now = new Date();
      const currentDayOfWeek = now.getDay(); // 0 = Sunday, 6 = Saturday
      const daysUntilSunday = (7 - currentDayOfWeek) % 7 || 7; // Days until next Sunday
      
      // End of the most recent completed week (end of last week)
      const endOfLastWeek = new Date(now);
      endOfLastWeek.setDate(now.getDate() - daysUntilSunday);
      endOfLastWeek.setHours(23, 59, 59, 999);
      
      // Start of the 4-week period (4 weeks before end of last week)
      const startOfPeriod = new Date(endOfLastWeek);
      startOfPeriod.setDate(endOfLastWeek.getDate() - 27); // 28 days = 4 weeks
      startOfPeriod.setHours(0, 0, 0, 0);

      // Initialize weekly data structure
      const weeklyData = [];
      for (let i = 0; i < 4; i++) {
        const weekStart = new Date(startOfPeriod);
        weekStart.setDate(startOfPeriod.getDate() + (i * 7));
        
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);
        
        const weekLabel = `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
        
        weeklyData.push({
          week: weekLabel,
          weekStart,
          weekEnd,
          appointmentsCompleted: 0,
          chatsResolved: 0,
          contentPublished: 0,
          newUsers: 0
        });
      }

      // Fetch appointments completed in the 4-week period
      const appointmentsQuery = query(
        collection(db, "appointments"),
        where("status", "==", "COMPLETED"),
        where("date", ">=", startOfPeriod),
        where("date", "<=", endOfLastWeek),
        limit(1000)
      );
      const appointmentsSnapshot = await getDocs(appointmentsQuery);
      
      appointmentsSnapshot.forEach(doc => {
        const appointment = doc.data();
        const appointmentDate = appointment.date?.toDate ? appointment.date.toDate() : new Date(appointment.date?.seconds * 1000);
        
        for (const week of weeklyData) {
          if (appointmentDate >= week.weekStart && appointmentDate <= week.weekEnd) {
            week.appointmentsCompleted++;
            break;
          }
        }
      });

      // Fetch resolved chats in the 4-week period
      const chatsQuery = query(
        collection(db, "chats"),
        where("createdAt", ">=", startOfPeriod),
        where("createdAt", "<=", endOfLastWeek),
        limit(1000)
      );
      const chatsSnapshot = await getDocs(chatsQuery);
      
      chatsSnapshot.forEach(doc => {
        const chat = doc.data();
        const isResolved = chat.status === "resolved" || 
                          (chat.status === "escalated" && chat.escalationStatus === "resolved");
        
        if (isResolved) {
          // Use updatedAt or resolvedAt for resolution date
          const resolvedDate = chat.resolvedAt?.toDate ? chat.resolvedAt.toDate() :
                                chat.updatedAt?.toDate ? chat.updatedAt.toDate() :
                                new Date(chat.updatedAt?.seconds * 1000);
          
          for (const week of weeklyData) {
            if (resolvedDate >= week.weekStart && resolvedDate <= week.weekEnd) {
              week.chatsResolved++;
              break;
            }
          }
        }
      });

      // Fetch published content in the 4-week period
      const publishedQuery = query(
        collection(db, "published"),
        where("publishedAt", ">=", startOfPeriod),
        where("publishedAt", "<=", endOfLastWeek),
        limit(1000)
      );
      const publishedSnapshot = await getDocs(publishedQuery);
      
      publishedSnapshot.forEach(doc => {
        const content = doc.data();
        const publishedDate = content.publishedAt?.toDate ? content.publishedAt.toDate() :
                               content.createdAt?.toDate ? content.createdAt.toDate() :
                               new Date(content.createdAt?.seconds * 1000);
        
        for (const week of weeklyData) {
          if (publishedDate >= week.weekStart && publishedDate <= week.weekEnd) {
            week.contentPublished++;
            break;
          }
        }
      });

      // Fetch new users in the 4-week period
      const usersQuery = query(
        collection(db, "users"),
        where("createdAt", ">=", startOfPeriod),
        where("createdAt", "<=", endOfLastWeek),
        limit(1000)
      );
      const usersSnapshot = await getDocs(usersQuery);
      
      usersSnapshot.forEach(doc => {
        const user = doc.data();
        const createdDate = user.createdAt?.toDate ? user.createdAt.toDate() :
                            new Date(user.createdAt?.seconds * 1000);
        
        for (const week of weeklyData) {
          if (createdDate >= week.weekStart && createdDate <= week.weekEnd) {
            week.newUsers++;
            break;
          }
        }
      });

      // Format data for chart
      const chartData = weeklyData.map(week => ({
        week: week.week,
        appointments: week.appointmentsCompleted,
        chats: week.chatsResolved,
        contentPublished: week.contentPublished,
        newUsers: week.newUsers
      }));

      setActivityTrendsData(chartData);
      setLoadingActivityTrends(false);
    } catch (error) {
      console.error("Error fetching activity trends:", error);
      setLoadingActivityTrends(false);
    }
  };

  const fetchUserRegistrationTrend = async () => {
    try {
      // Calculate the start of the 12-month period (12 months ago from today)
      const now = new Date();
      const twelveMonthsAgo = new Date(now);
      twelveMonthsAgo.setMonth(now.getMonth() - 11);
      twelveMonthsAgo.setDate(1);
      twelveMonthsAgo.setHours(0, 0, 0, 0);

      // Initialize monthly data structure
      const monthlyData = [];
      for (let i = 0; i < 12; i++) {
        const monthStart = new Date(twelveMonthsAgo);
        monthStart.setMonth(twelveMonthsAgo.getMonth() + i);
        
        const monthEnd = new Date(monthStart);
        monthEnd.setMonth(monthStart.getMonth() + 1);
        monthEnd.setDate(0);
        monthEnd.setHours(23, 59, 59, 999);
        
        const monthLabel = monthStart.toLocaleDateString('en-US', { month: 'short' });
        
        monthlyData.push({
          month: monthLabel,
          monthStart,
          monthEnd,
          newUsers: 0
        });
      }

      // Fetch users created in the 12-month period
      const usersQuery = query(
        collection(db, "users"),
        where("createdAt", ">=", twelveMonthsAgo),
        where("createdAt", "<=", now),
        limit(5000)
      );
      const usersSnapshot = await getDocs(usersQuery);
      
      usersSnapshot.forEach(doc => {
        const user = doc.data();
        const createdDate = user.createdAt?.toDate ? user.createdAt.toDate() :
                            new Date(user.createdAt?.seconds * 1000);
        
        for (const month of monthlyData) {
          if (createdDate >= month.monthStart && createdDate <= month.monthEnd) {
            month.newUsers++;
            break;
          }
        }
      });

      // Format data for chart
      const chartData = monthlyData.map(month => ({
        month: month.month,
        newUsers: month.newUsers
      }));

      setUserRegistrationData(chartData);
      setLoadingUserRegistration(false);
    } catch (error) {
      console.error("Error fetching user registration trend:", error);
      setLoadingUserRegistration(false);
    }
  };

  const fetchContentEngagementTrend = async () => {
    try {
      // Calculate the start of the most recent completed week (4 weeks back)
      const now = new Date();
      const currentDayOfWeek = now.getDay(); // 0 = Sunday, 6 = Saturday
      const daysUntilSunday = (7 - currentDayOfWeek) % 7 || 7; // Days until next Sunday
      
      // End of the most recent completed week (end of last week)
      const endOfLastWeek = new Date(now);
      endOfLastWeek.setDate(now.getDate() - daysUntilSunday);
      endOfLastWeek.setHours(23, 59, 59, 999);
      
      // Start of the 4-week period (4 weeks before end of last week)
      const startOfPeriod = new Date(endOfLastWeek);
      startOfPeriod.setDate(endOfLastWeek.getDate() - 27); // 28 days = 4 weeks
      startOfPeriod.setHours(0, 0, 0, 0);

      // Initialize weekly data structure
      const weeklyData = [];
      for (let i = 0; i < 4; i++) {
        const weekStart = new Date(startOfPeriod);
        weekStart.setDate(startOfPeriod.getDate() + (i * 7));
        
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);
        
        const weekLabel = `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
        
        weeklyData.push({
          week: weekLabel,
          weekStart,
          weekEnd,
          views: 0,
          bookmarks: 0,
          shares: 0
        });
      }

      // Helper function to format date as YYYY-MM-DD
      const formatDateKey = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      };

      // Fetch analytics documents for the 4-week period
      // We need to query each day's document in the analytics collection
      const analyticsDocs = [];
      
      // Iterate through each day in the 4-week period and fetch analytics
      const currentDate = new Date(startOfPeriod);
      while (currentDate <= endOfLastWeek) {
        const dateKey = formatDateKey(currentDate);
        const analyticsRef = doc(db, 'analytics', dateKey);
        
        try {
          const analyticsDoc = await getDoc(analyticsRef);
          if (analyticsDoc.exists()) {
            analyticsDocs.push({
              date: new Date(currentDate),
              data: analyticsDoc.data()
            });
          }
        } catch (error) {
          // Skip if document doesn't exist or error occurs
        }
        
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // Group daily analytics into weekly totals
      analyticsDocs.forEach(doc => {
        const docDate = doc.date;
        
        for (const week of weeklyData) {
          if (docDate >= week.weekStart && docDate <= week.weekEnd) {
            week.views += doc.data.views || 0;
            week.bookmarks += doc.data.bookmarks || 0;
            week.shares += doc.data.shares || 0;
            break;
          }
        }
      });

      // Format data for chart
      const chartData = weeklyData.map(week => ({
        week: week.week,
        views: week.views,
        bookmarks: week.bookmarks,
        shares: week.shares
      }));

      setContentEngagementData(chartData);
      setLoadingContentEngagementTrend(false);
    } catch (error) {
      console.error("Error fetching content engagement trend:", error);
      setLoadingContentEngagementTrend(false);
    }
  };

  const fetchContentPublishingTrend = async () => {
    try {
      // Calculate the start of the 12-month period (12 months ago from today)
      const now = new Date();
      const twelveMonthsAgo = new Date(now);
      twelveMonthsAgo.setMonth(now.getMonth() - 11);
      twelveMonthsAgo.setDate(1);
      twelveMonthsAgo.setHours(0, 0, 0, 0);

      // Initialize monthly data structure
      const monthlyData = [];
      for (let i = 0; i < 12; i++) {
        const monthStart = new Date(twelveMonthsAgo);
        monthStart.setMonth(twelveMonthsAgo.getMonth() + i);
        
        const monthEnd = new Date(monthStart);
        monthEnd.setMonth(monthStart.getMonth() + 1);
        monthEnd.setDate(0);
        monthEnd.setHours(23, 59, 59, 999);
        
        const monthLabel = monthStart.toLocaleDateString('en-US', { month: 'short' });
        
        monthlyData.push({
          month: monthLabel,
          monthStart,
          monthEnd,
          published: 0
        });
      }

      // Fetch published content in the 12-month period
      const publishedQuery = query(
        collection(db, "published"),
        where("publishedAt", ">=", twelveMonthsAgo),
        where("publishedAt", "<=", now),
        limit(5000)
      );
      const publishedSnapshot = await getDocs(publishedQuery);
      
      publishedSnapshot.forEach(doc => {
        const content = doc.data();
        const publishedDate = content.publishedAt?.toDate ? content.publishedAt.toDate() :
                              content.createdAt?.toDate ? content.createdAt.toDate() :
                              new Date(content.createdAt?.seconds * 1000);
        
        for (const month of monthlyData) {
          if (publishedDate >= month.monthStart && publishedDate <= month.monthEnd) {
            month.published++;
            break;
          }
        }
      });

      // Format data for chart
      const chartData = monthlyData.map(month => ({
        month: month.month,
        published: month.published
      }));

      setContentPublishingData(chartData);
      setLoadingContentPublishingTrend(false);
    } catch (error) {
      console.error("Error fetching content publishing trend:", error);
      setLoadingContentPublishingTrend(false);
    }
  };

  const fetchChatActivityTrend = async () => {
    try {
      // Calculate the start of the most recent completed week (4 weeks back)
      const now = new Date();
      const currentDayOfWeek = now.getDay(); // 0 = Sunday, 6 = Saturday
      const daysUntilSunday = (7 - currentDayOfWeek) % 7 || 7; // Days until next Sunday
      
      // End of the most recent completed week (end of last week)
      const endOfLastWeek = new Date(now);
      endOfLastWeek.setDate(now.getDate() - daysUntilSunday);
      endOfLastWeek.setHours(23, 59, 59, 999);
      
      // Start of the 4-week period (4 weeks before end of last week)
      const startOfPeriod = new Date(endOfLastWeek);
      startOfPeriod.setDate(endOfLastWeek.getDate() - 27); // 28 days = 4 weeks
      startOfPeriod.setHours(0, 0, 0, 0);

      // Initialize weekly data structure
      const weeklyData = [];
      for (let i = 0; i < 4; i++) {
        const weekStart = new Date(startOfPeriod);
        weekStart.setDate(startOfPeriod.getDate() + (i * 7));
        
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);
        
        const weekLabel = `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
        
        weeklyData.push({
          week: weekLabel,
          weekStart,
          weekEnd,
          assigned: 0,
          resolved: 0
        });
      }

      // Fetch chats in the 4-week period
      const chatsQuery = query(
        collection(db, "chats"),
        where("createdAt", ">=", startOfPeriod),
        where("createdAt", "<=", endOfLastWeek),
        limit(1000)
      );
      const chatsSnapshot = await getDocs(chatsQuery);
      
      chatsSnapshot.forEach(doc => {
        const chat = doc.data();
        const chatDate = chat.createdAt?.toDate ? chat.createdAt.toDate() :
                          new Date(chat.createdAt?.seconds * 1000);
        
        // Count assigned chats using assignedAt field
        if (chat.assignedAt) {
          const assignedDate = chat.assignedAt?.toDate ? chat.assignedAt.toDate() :
                               new Date(chat.assignedAt?.seconds * 1000);
          
          for (const week of weeklyData) {
            if (assignedDate >= week.weekStart && assignedDate <= week.weekEnd) {
              week.assigned++;
              break;
            }
          }
        }
        
        // Count resolved chats (status is "resolved" OR status is "escalated" with escalationStatus "resolved")
        const isResolved = chat.status === "resolved" || 
                          (chat.status === "escalated" && chat.escalationStatus === "resolved");
        
        if (isResolved) {
          // Use updatedAt or resolvedAt for resolution date
          const resolvedDate = chat.resolvedAt?.toDate ? chat.resolvedAt.toDate() :
                                chat.updatedAt?.toDate ? chat.updatedAt.toDate() :
                                new Date(chat.updatedAt?.seconds * 1000);
          
          for (const week of weeklyData) {
            if (resolvedDate >= week.weekStart && resolvedDate <= week.weekEnd) {
              week.resolved++;
              break;
            }
          }
        }
      });

      // Format data for chart
      const chartData = weeklyData.map(week => ({
        week: week.week,
        assigned: week.assigned,
        resolved: week.resolved
      }));

      setChatActivityData(chartData);
      setLoadingChatActivityTrend(false);
    } catch (error) {
      console.error("Error fetching chat activity trend:", error);
      setLoadingChatActivityTrend(false);
    }
  };

  const fetchTopPeerEducators = async () => {
    try {
      // Calculate the latest completed week
      const now = new Date();
      const currentDayOfWeek = now.getDay(); // 0 = Sunday, 6 = Saturday
      const daysUntilSunday = (7 - currentDayOfWeek) % 7 || 7; // Days until next Sunday
      
      // End of the most recent completed week (end of last week)
      const endOfLastWeek = new Date(now);
      endOfLastWeek.setDate(now.getDate() - daysUntilSunday);
      endOfLastWeek.setHours(23, 59, 59, 999);
      
      // Start of the most recent completed week (7 days before end of last week)
      const startOfLastWeek = new Date(endOfLastWeek);
      startOfLastWeek.setDate(endOfLastWeek.getDate() - 6);
      startOfLastWeek.setHours(0, 0, 0, 0);

      // Set the week range for display
      const weekLabel = `${startOfLastWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endOfLastWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
      setCurrentWeekRange(weekLabel);

      // Initialize peer educator tracking
      const peerEducatorStats = {};

      // Fetch resolved chats in the most recent completed week
      // Note: We query by createdAt with date range to limit documents, then filter by resolution date in JavaScript
      // This ensures we only count chats resolved within the target week while minimizing Firestore reads
      const chatsQuery = query(
        collection(db, "chats"),
        where("createdAt", ">=", startOfLastWeek),
        where("createdAt", "<=", endOfLastWeek),
        limit(1000)
      );
      const chatsSnapshot = await getDocs(chatsQuery);
      
      chatsSnapshot.forEach(doc => {
        const chat = doc.data();
        
        // Count resolved chats (status is "resolved" OR status is "escalated" with escalationStatus "resolved")
        const isResolved = chat.status === "resolved" || 
                          (chat.status === "escalated" && chat.escalationStatus === "resolved");
        
        if (isResolved && chat.assignedTo) {
          // Use updatedAt or resolvedAt for resolution date
          const resolvedDate = chat.resolvedAt?.toDate ? chat.resolvedAt.toDate() :
                                chat.updatedAt?.toDate ? chat.updatedAt.toDate() :
                                new Date(chat.updatedAt?.seconds * 1000);
          
          // Only count if resolved within the target week
          if (resolvedDate >= startOfLastWeek && resolvedDate <= endOfLastWeek) {
            if (!peerEducatorStats[chat.assignedTo]) {
              peerEducatorStats[chat.assignedTo] = 0;
            }
            peerEducatorStats[chat.assignedTo]++;
          }
        }
      });

      // Convert to array and sort by resolved count
      const sortedPeerEducators = Object.entries(peerEducatorStats)
        .map(([userId, resolvedCount]) => ({ userId, resolvedCount }))
        .sort((a, b) => b.resolvedCount - a.resolvedCount)
        .slice(0, 5); // Top 5 peer educators

      // Fetch user names for the top peer educators (batched for efficiency)
      const peerEducatorNames = {};
      for (const peer of sortedPeerEducators) {
        try {
          const userDoc = await getDoc(doc(db, "users", peer.userId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const fullName = userData.fullName ||
                            `${userData.firstName || ''} ${userData.lastName || ''}`.trim() ||
                            userData.displayName ||
                            userData.name ||
                            "Unknown";
            peerEducatorNames[peer.userId] = fullName;
          } else {
            peerEducatorNames[peer.userId] = "Unknown";
          }
        } catch (error) {
          peerEducatorNames[peer.userId] = "Unknown";
        }
      }

      // Format data for display
      const topPeerEducators = sortedPeerEducators.map(peer => ({
        name: peerEducatorNames[peer.userId],
        resolvedChats: peer.resolvedCount
      }));

      setTopPeerEducatorsData(topPeerEducators);
      setLoadingPeerEducators(false);
    } catch (error) {
      console.error("Error fetching top peer educators:", error);
      setLoadingPeerEducators(false);
    }
  };

  const getDashboardScrollContainer = () => document.querySelector('.mp-workspace');

  const restoreDashboardScroll = (scrollTop, scrollLeft) => {
    const scrollContainer = getDashboardScrollContainer();
    if (scrollContainer) {
      scrollContainer.scrollTop = scrollTop;
      scrollContainer.scrollLeft = scrollLeft;
      return;
    }
    window.scrollTo(scrollLeft, scrollTop);
  };

  const captureDashboardChart = async (chartId) => {
    const element = document.querySelector(`[data-export-chart="${chartId}"]`);
    if (!element) return null;

    const scrollContainer = getDashboardScrollContainer();
    const scrollX = scrollContainer ? -scrollContainer.scrollLeft : -window.scrollX;
    const scrollY = scrollContainer ? -scrollContainer.scrollTop : -window.scrollY;
    const scale = Math.max(3, window.devicePixelRatio || 2);

    try {
      const canvas = await html2canvas(element, {
        scale,
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
        allowTaint: true,
        scrollX,
        scrollY,
        windowWidth: document.documentElement.clientWidth,
        windowHeight: document.documentElement.clientHeight,
        onclone: (_doc, clonedElement) => {
          clonedElement.style.transform = 'none';
          clonedElement.style.boxShadow = 'none';
          clonedElement.style.overflow = 'visible';
          clonedElement.querySelectorAll('.chart-header h3, .chart-header-full h3').forEach((title) => {
            title.style.background = 'none';
            title.style.backgroundClip = 'border-box';
            title.style.webkitBackgroundClip = 'border-box';
            title.style.webkitTextFillColor = '#1e293b';
            title.style.color = '#1e293b';
          });
          clonedElement.querySelectorAll('svg.recharts-surface').forEach((svg) => {
            const rect = svg.getBoundingClientRect();
            if (rect.width && rect.height) {
              svg.setAttribute('width', String(rect.width));
              svg.setAttribute('height', String(rect.height));
            }
          });
        },
      });
      return canvas.toDataURL('image/png');
    } catch (error) {
      console.error(`Chart capture failed (${chartId}):`, error);
      return null;
    }
  };

  const loadImageDimensions = (dataUrl) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });

  const captureChartsForTab = async (chartIds) => {
    const scrollContainer = getDashboardScrollContainer();
    const savedScrollTop = scrollContainer?.scrollTop ?? window.scrollY;
    const savedScrollLeft = scrollContainer?.scrollLeft ?? window.scrollX;

    const images = {};
    try {
      for (const chartId of chartIds) {
        images[chartId] = await captureDashboardChart(chartId);
      }
    } finally {
      restoreDashboardScroll(savedScrollTop, savedScrollLeft);
    }
    return images;
  };

  const handleExport = async (format) => {
    const scrollContainer = getDashboardScrollContainer();
    const savedScrollTop = scrollContainer?.scrollTop ?? window.scrollY;
    const savedScrollLeft = scrollContainer?.scrollLeft ?? window.scrollX;

    setIsExporting(true);
    try {
      const reportDate = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      
      switch (format) {
        case 'pdf':
          await exportToPDF(activeTab, reportDate);
          break;
        case 'excel':
          await exportToExcel(activeTab, reportDate);
          break;
        case 'csv':
          await exportToCSV(activeTab, reportDate);
          break;
        default:
          throw new Error('Invalid export format');
      }
      setShowExportModal(false);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      restoreDashboardScroll(savedScrollTop, savedScrollLeft);
      setIsExporting(false);
    }
  };

  const loadImageAsDataUrl = (src) => new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(src);
    img.src = src;
  });

  const exportToPDF = async (tab, reportDate) => {
    // Landscape A4: 297 x 210 mm
    const pdf = new jsPDF('l', 'mm', 'a4');
    const PW = pdf.internal.pageSize.getWidth();   // 297
    const PH = pdf.internal.pageSize.getHeight();  // 210
    const ML = 15;
    const MR = 15;
    const CW = PW - ML - MR;
    const HEADER_H = 14;
    const GAP = 6;
    const logoDataUrl = await loadImageAsDataUrl(logoAuraVit);
    const formattedTime = new Date().toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
    const reportTitle = 'AuraVit Administrative Analytics Report';

    const TAB_CHARTS = {
      overview: ['activity-trends', 'user-role-distribution'],
      users: ['medical-coverage', 'user-registration', 'gender-distribution', 'patient-assistance'],
      content: ['content-engagement', 'content-publishing', 'top-engaged-topics'],
      chats: ['chat-activity', 'escalation-status', 'chat-categories'],
      appointments: ['appointment-status', 'appointment-trends', 'service-demand', 'peak-hours'],
    };

    const chartImages = await captureChartsForTab(TAB_CHARTS[tab] || []);

    const addHeader = () => {
      pdf.setFillColor(245, 248, 255);
      pdf.rect(0, 0, PW, HEADER_H, 'F');

      // Logo — left, scaled to fit inside header height with 2mm padding
      if (logoDataUrl) {
        try { pdf.addImage(logoDataUrl, 'PNG', ML, 2, 0, HEADER_H - 4); } catch(e) {}
      }

      // Title — centre
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(29, 78, 216);
      pdf.text(reportTitle, PW / 2, 9, { align: 'center' });

      // Date + tab — right side
      pdf.setFontSize(7.5);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(75, 85, 99);
      pdf.text(
        `${tab.charAt(0).toUpperCase() + tab.slice(1)} Tab  ·  ${reportDate} ${formattedTime}`,
        PW - MR, 9, { align: 'right' }
      );

      pdf.setDrawColor(229, 231, 235);
      pdf.setLineWidth(0.4);
      pdf.line(ML, HEADER_H, PW - MR, HEADER_H);
    };

    const applyHeadersAndFooters = () => {
      const pageCount = pdf.getNumberOfPages();
      for (let i = 1; i <= pageCount; i += 1) {
        pdf.setPage(i);
        addHeader();
      }
    };

    const saveReport = (filename) => {
      applyHeadersAndFooters();
      pdf.save(filename);
    };

    const newPage = () => { pdf.addPage(); return HEADER_H + 10; };

    const label = (text, x, y) => {
      pdf.setFontSize(9); pdf.setTextColor(30, 58, 138); pdf.setFont(undefined, 'bold');
      pdf.text(text, x, y);
    };

    const kpiRow = (cards, y) => {
      const n = cards.length;
      const cardW = (CW - GAP * (n - 1)) / n;
      cards.forEach((c, i) => {
        const x = ML + i * (cardW + GAP);
        pdf.setFillColor(30, 58, 138);
        pdf.roundedRect(x, y, cardW, 24, 2, 2, 'F');
        pdf.setFontSize(8); pdf.setTextColor(180, 210, 255); pdf.setFont(undefined, 'normal');
        pdf.text(String(c.label), x + cardW / 2, y + 8, { align: 'center' });
        pdf.setFontSize(18); pdf.setTextColor(255, 255, 255); pdf.setFont(undefined, 'bold');
        pdf.text(String(c.value), x + cardW / 2, y + 20, { align: 'center' });
      });
      return y + 24 + GAP;
    };

    const placeChart = async (chartId, x, y, maxW, maxH, fallbackFn, fallbackArgs = []) => {
      let dataUrl = chartImages[chartId];
      if (!dataUrl && fallbackFn) {
        dataUrl = fallbackFn(...fallbackArgs);
      }
      if (!dataUrl) return { bottom: y, height: 0 };

      const dims = await loadImageDimensions(dataUrl);
      if (!dims) return { bottom: y, height: 0 };

      const aspect = dims.width / dims.height;
      let w = maxW;
      let h = w / aspect;
      if (h > maxH) {
        h = maxH;
        w = h * aspect;
      }

      try {
        pdf.addImage(dataUrl, 'PNG', x, y, w, h);
      } catch (e) {
        console.error(e);
      }

      return { bottom: y + h + GAP, height: h, width: w };
    };

    let y = HEADER_H + 10;

    // ═══════════════════════════════════════════════════════════════════════════
    // OVERVIEW TAB  (1 page)
    //   Row 1: 4 KPI cards
    //   Row 2: Platform Activity Trends (left) · User Role Distribution (right)
    // ═══════════════════════════════════════════════════════════════════════════
    if (tab === 'overview') {
      y = kpiRow([
        { label: 'Total Users',            value: realStats.totalUsers.toLocaleString() },
        { label: 'Completed Appointments', value: realStats.completedAppointments.toLocaleString() },
        { label: 'Published Content',      value: realStats.publishedContent.toLocaleString() },
        { label: 'Resolved Conversations', value: realStats.resolvedChats.toLocaleString() },
      ], y);

      const colW = (CW - GAP) / 2;
      const chartH = PH - y - 12;

      const trends = await placeChart(
        'activity-trends',
        ML,
        y,
        colW,
        chartH,
        createActivityTrendsChart,
        [activityTrendsData, 640, 420]
      );

      const roleData = [
        { label: 'Patients',              value: realStats.userDistribution.patients,            color: '#3b82f6' },
        { label: 'Medical Professionals', value: realStats.userDistribution.medicalProfessionals, color: '#10b981' },
        { label: 'Content Curators',      value: realStats.userDistribution.contentCurators,     color: '#f59e0b' },
        { label: 'Peer Educators',        value: realStats.userDistribution.peerEducators,       color: '#8b5cf6' },
      ];
      const roleChart = await placeChart(
        'user-role-distribution',
        ML + colW + GAP,
        y,
        colW,
        chartH,
        createPieChart,
        [roleData, 420, 320]
      );
      y = Math.max(trends.bottom, roleChart.bottom);

      saveReport(`AuraVit_overview_Report_${reportDate.replace(/ /g, '_')}.pdf`);
      return;
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // USERS TAB  (2 pages)
    //   Page 1: 4 KPI cards · Medical Coverage bar (left) · Registration line (right)
    //   Page 2: Gender donut (left) · Patient Assistance donut (right)
    // ═══════════════════════════════════════════════════════════════════════════
    if (tab === 'users') {
      y = kpiRow([
        { label: 'Total Users',          value: realStats.totalUsers.toLocaleString() },
        { label: 'Active Users',         value: realStats.activeUsers.toLocaleString() },
        { label: 'Inactive Users',       value: realStats.inactiveUsers.toLocaleString() },
        { label: 'Deactivated Accounts', value: realStats.deactivatedAccounts.toLocaleString() },
      ], y);

      const colW = (CW - GAP) / 2;
      const chartH = PH - y - 20;

      const medChart = await placeChart(
        'medical-coverage',
        ML,
        y,
        colW,
        chartH,
        createBarChart,
        [[
          { label: 'HIV Testing',               value: realStats.medicalCoverage.hivTesting },
          { label: 'Reproductive Consultation', value: realStats.medicalCoverage.reproductiveConsultation },
          { label: 'Family Planning',           value: realStats.medicalCoverage.familyPlanning },
        ], 640, 420]
      );
      const regChart = await placeChart(
        'user-registration',
        ML + colW + GAP,
        y,
        colW,
        chartH,
        createSingleLineChart,
        [
          userRegistrationData.map(d => ({ value: d.newUsers, label: d.month })),
          '#8b5cf6',
          'New Users',
          640,
          420,
        ]
      );
      y = Math.max(medChart.bottom, regChart.bottom);

      y = newPage();
      const pieH = PH - y - 20;
      const genderChart = await placeChart(
        'gender-distribution',
        ML,
        y,
        (CW - GAP) / 2,
        pieH,
        createPieChart,
        [[
          { label: 'Female', value: realStats.genderDistribution.female, color: '#ec4899' },
          { label: 'Male',   value: realStats.genderDistribution.male,   color: '#3b82f6' },
        ], 420, 320]
      );
      const assistChart = await placeChart(
        'patient-assistance',
        ML + (CW - GAP) / 2 + GAP,
        y,
        (CW - GAP) / 2,
        pieH,
        createPieChart,
        [[
          { label: 'Minor',  value: realStats.patientAssistance.minor,  color: '#f59e0b' },
          { label: 'Senior', value: realStats.patientAssistance.senior, color: '#10b981' },
          { label: 'PWD',    value: realStats.patientAssistance.pwd,    color: '#8b5cf6' },
          { label: 'NRNW',   value: realStats.patientAssistance.nrnw,   color: '#ef4444' },
          { label: 'Other',  value: realStats.patientAssistance.other,  color: '#64748b' },
        ], 420, 320]
      );
      y = Math.max(genderChart.bottom, assistChart.bottom);

      saveReport(`AuraVit_users_Report_${reportDate.replace(/ /g, '_')}.pdf`);
      return;
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // CONTENT TAB  (3 pages)
    //   Page 1: 4 KPI cards · Weekly Engagement (left) · Monthly Publishing (right)
    //   Page 2: Top Performing Content (left table) · Most Engaged Topics (right bar)
    //   Page 3: Content Awaiting Approval (full-width table)
    // ═══════════════════════════════════════════════════════════════════════════
    if (tab === 'content') {
      y = kpiRow([
        { label: 'Live Content',       value: realStats.contentStats.liveContent },
        { label: 'Pending Review',     value: realStats.contentStats.pendingReview },
        { label: 'Live Announcements', value: realStats.contentStats.liveAnnouncements },
        { label: 'Archived Content',   value: realStats.contentStats.archivedContent },
      ], y);

      const colW = (CW - GAP) / 2;
      const chartH = PH - y - 20;

      const engagementChart = await placeChart(
        'content-engagement',
        ML,
        y,
        colW,
        chartH,
        createContentEngagementChart,
        [contentEngagementData, 640, 420]
      );
      const publishingChart = await placeChart(
        'content-publishing',
        ML + colW + GAP,
        y,
        colW,
        chartH,
        createAreaChart,
        [contentPublishingData, 640, 420]
      );
      y = Math.max(engagementChart.bottom, publishingChart.bottom);

      y = newPage();

      if (realStats.topPerformingContent && realStats.topPerformingContent.length > 0) {
        autoTable(pdf, {
          startY: y,
          tableWidth: colW,
          margin: { left: ML },
          head: [['Title', 'Views', 'Bookmarks', 'Shares']],
          body: realStats.topPerformingContent.slice(0, 5).map(item => [
            (item.title || 'N/A').substring(0, 38),
            item.views || 0, item.bookmarks || 0, item.shares || 0,
          ]),
          theme: 'grid',
          headStyles: { fillColor: [30, 58, 138], fontSize: 8 },
          styles: { fontSize: 8 },
          columnStyles: { 0: { cellWidth: colW - 45 }, 1: { cellWidth: 13 }, 2: { cellWidth: 18 }, 3: { cellWidth: 13 } },
        });
      }

      await placeChart(
        'top-engaged-topics',
        ML + colW + GAP,
        y,
        colW,
        100,
        createHorizontalBarChart,
        [realStats.topEngagedTopics, 640, 420]
      );

      if (realStats.pendingApprovals && realStats.pendingApprovals.length > 0) {
        y = newPage();
        label('Content Awaiting Approval', ML, y);
        y += 5;
        autoTable(pdf, {
          startY: y,
          margin: { left: ML, right: MR },
          head: [['Title', 'Creator', 'Category', 'Date Submitted']],
          body: realStats.pendingApprovals.slice(0, 15).map(item => [
            (item.title || 'N/A').substring(0, 50),
            item.author || 'N/A',
            item.category || 'N/A',
            item.submittedAt?.toDate
              ? item.submittedAt.toDate().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
              : 'N/A',
          ]),
          theme: 'grid',
          headStyles: { fillColor: [30, 58, 138], fontSize: 9 },
          styles: { fontSize: 8.5 },
        });
      }

      saveReport(`AuraVit_content_Report_${reportDate.replace(/ /g, '_')}.pdf`);
      return;
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // CHATS TAB  (2 pages)
    //   Page 1: Top Peer Educators (left table) · Weekly Chat Activity (right line)
    //   Page 2: Escalated Case Status (left bar) · Today's Chat Categories (right donut)
    // ═══════════════════════════════════════════════════════════════════════════
    if (tab === 'chats') {
      const colW = (CW - GAP) / 2;

      if (topPeerEducatorsData && topPeerEducatorsData.length > 0) {
        autoTable(pdf, {
          startY: y,
          tableWidth: colW,
          margin: { left: ML },
          head: [['Name', 'Resolved Chats']],
          body: topPeerEducatorsData.map(e => [e.name, e.resolvedChats]),
          theme: 'grid',
          headStyles: { fillColor: [30, 58, 138], fontSize: 9 },
          styles: { fontSize: 9 },
          columnStyles: { 0: { cellWidth: colW * 0.7 }, 1: { cellWidth: colW * 0.25 } },
        });
      }

      const chatChart = await placeChart(
        'chat-activity',
        ML + colW + GAP,
        y,
        colW,
        PH - y - 20,
        createGenericMultiLineChart,
        [
          chatActivityData,
          [
            { key: 'assigned', color: '#3b82f6', label: 'Assigned' },
            { key: 'resolved', color: '#10b981', label: 'Resolved' },
          ],
          640,
          420,
        ]
      );
      y = chatChart.bottom;

      y = newPage();
      const escChart = await placeChart(
        'escalation-status',
        ML,
        y,
        colW,
        PH - y - 20,
        createStackedBarChart,
        [
          realStats.escalationStatus,
          'category',
          [
            { key: 'pending',         color: '#f59e0b', label: 'Pending Review' },
            { key: 'underAssessment', color: '#3b82f6', label: 'Under Assessment' },
            { key: 'resolved',        color: '#10b981', label: 'Resolved' },
          ],
          640,
          420,
        ]
      );
      const catChart = await placeChart(
        'chat-categories',
        ML + colW + GAP,
        y,
        colW,
        PH - y - 20,
        createPieChart,
        [realStats.chatCategories.map(c => ({ label: c.name, value: c.value, color: c.color })), 420, 320]
      );
      y = Math.max(escChart.bottom, catChart.bottom);

      saveReport(`AuraVit_chats_Report_${reportDate.replace(/ /g, '_')}.pdf`);
      return;
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // APPOINTMENTS TAB  (4 pages)
    //   Page 1: Top Medical Professionals (full width)
    //   Page 2: Appointment Status Overview · Weekly Appointment Trends
    //   Page 3: Today's Overview stats + schedule table
    //   Page 4: Service Demand · Heatmap
    // ═══════════════════════════════════════════════════════════════════════════
    if (tab === 'appointments') {
      const colW = (CW - GAP) / 2;

      label('Top Medical Professionals', ML, y);
      y += 5;

      if (realStats.topMedicalProfessionals && realStats.topMedicalProfessionals.length > 0) {
        autoTable(pdf, {
          startY: y,
          margin: { left: ML, right: MR },
          head: [['Name', 'Completed', 'Service']],
          body: realStats.topMedicalProfessionals.map(p => [
            p.name, p.completedAppointments, (p.assignedServices || '').substring(0, 40),
          ]),
          theme: 'grid',
          headStyles: { fillColor: [30, 58, 138], fontSize: 9 },
          styles: { fontSize: 8.5 },
          columnStyles: {
            0: { cellWidth: CW * 0.35 },
            1: { cellWidth: CW * 0.15 },
            2: { cellWidth: CW * 0.5 },
          },
        });
      }

      y = newPage();
      const chartsH = PH - y - 15;

      const statusChart = await placeChart(
        'appointment-status',
        ML,
        y,
        colW,
        chartsH,
        createStackedBarChart,
        [
          realStats.appointmentStatusOverview,
          'service',
          [
            { key: 'pending',   color: '#f59e0b', label: 'Pending Approval' },
            { key: 'confirmed', color: '#3b82f6', label: 'Confirmed' },
            { key: 'accepted',  color: '#10b981', label: 'Accepted' },
          ],
          640,
          420,
        ]
      );
      const trendsChart = await placeChart(
        'appointment-trends',
        ML + colW + GAP,
        y,
        colW,
        chartsH,
        createMultiLineChart,
        [realStats.weeklyAppointmentTrends, 640, 420]
      );
      y = Math.max(statusChart.bottom, trendsChart.bottom);

      y = newPage();
      label("Today's Appointment Overview", ML, y);
      y += 5;
      const todayStats = [
        { label: 'Total Today', value: realStats.todayAppointments.total },
        { label: 'Pending',     value: realStats.todayAppointments.pending },
        { label: 'Confirmed',   value: realStats.todayAppointments.confirmed },
        { label: 'Completed',   value: realStats.todayAppointments.completed },
      ];
      const miniW = (CW - GAP * 3) / 4;
      todayStats.forEach((s, i) => {
        const sx = ML + i * (miniW + GAP);
        pdf.setFillColor(239, 246, 255);
        pdf.roundedRect(sx, y, miniW, 18, 2, 2, 'F');
        pdf.setFontSize(7); pdf.setTextColor(100, 116, 139); pdf.setFont(undefined, 'normal');
        pdf.text(s.label, sx + miniW / 2, y + 6, { align: 'center' });
        pdf.setFontSize(14); pdf.setTextColor(30, 58, 138); pdf.setFont(undefined, 'bold');
        pdf.text(String(s.value), sx + miniW / 2, y + 15, { align: 'center' });
      });
      y += 22;

      if (realStats.todaySchedule && realStats.todaySchedule.length > 0) {
        autoTable(pdf, {
          startY: y,
          margin: { left: ML, right: MR },
          head: [['Patient', 'Service', 'Time', 'Status']],
          body: realStats.todaySchedule.slice(0, 10).map(a => [a.patient, a.service, a.time, a.status]),
          theme: 'grid',
          headStyles: { fillColor: [30, 58, 138], fontSize: 9 },
          styles: { fontSize: 8.5 },
        });
      }

      y = newPage();
      const demandH = PH - y - 15;
      const demandChart = await placeChart(
        'service-demand',
        ML,
        y,
        colW,
        demandH,
        createHorizontalBarChart,
        [
          realStats.serviceDemand.map(d => ({ topic: d.service, engagement: d.demand })),
          640,
          420,
        ]
      );
      const peakChart = await placeChart(
        'peak-hours',
        ML + colW + GAP,
        y,
        colW,
        demandH,
        createHeatmapChart,
        [realStats.peakHours, 640, 420]
      );
      y = Math.max(demandChart.bottom, peakChart.bottom);

      saveReport(`AuraVit_appointments_Report_${reportDate.replace(/ /g, '_')}.pdf`);
      return;
    }

    saveReport(`AuraVit_${tab}_Report_${reportDate.replace(/ /g, '_')}.pdf`);
  };

  // ── Canvas chart helpers ────────────────────────────────────────────────────
  // All functions below are defined BEFORE exportToExcel so they are in scope
  // when exportToPDF (above) calls them.

  // Hex string → { r, g, b }
  const hexToRgb = (hex) => ({
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
  });

  // Draw 4 horizontal grid lines + y-axis tick labels onto an existing ctx
  const _drawGrid = (ctx, pL, pT, pLeg, cW, cH, maxV) => {
    for (let i = 0; i <= 4; i++) {
      const gy = pT + pLeg + (cH / 4) * i;
      ctx.strokeStyle = '#e2e8f0'; ctx.lineWidth = 0.5;
      ctx.beginPath(); ctx.moveTo(pL, gy); ctx.lineTo(pL + cW, gy); ctx.stroke();
      ctx.fillStyle = '#64748b'; ctx.font = '8px Arial'; ctx.textAlign = 'right';
      ctx.fillText(Math.round(maxV - (maxV / 4) * i), pL - 3, gy + 3);
    }
    ctx.strokeStyle = '#cbd5e1'; ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(pL, pT + pLeg);
    ctx.lineTo(pL, pT + pLeg + cH);
    ctx.lineTo(pL + cW, pT + pLeg + cH);
    ctx.stroke();
  };

  // Vertical bar chart — data: [{ label, value }]
  const createBarChart = (data, width, height) => {
    const canvas = document.createElement('canvas');
    canvas.width = width * 2; canvas.height = height * 2;
    const ctx = canvas.getContext('2d'); ctx.scale(2, 2);

    const pL = 45, pR = 15, pT = 12, pB = 38, pLeg = 0;
    const cW = width - pL - pR, cH = height - pT - pB;
    const maxV = Math.max(...data.map(d => d.value || 0)) || 1;
    const slotW = cW / data.length, barW = slotW * 0.6;

    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, width, height);
    _drawGrid(ctx, pL, pT, pLeg, cW, cH, maxV);

    data.forEach((item, i) => {
      const x = pL + i * slotW + (slotW - barW) / 2;
      const bH = Math.max(1, ((item.value || 0) / maxV) * cH);
      const y = pT + cH - bH;
      const grad = ctx.createLinearGradient(x, y, x, y + bH);
      grad.addColorStop(0, '#3b82f6'); grad.addColorStop(1, '#1d4ed8');
      ctx.fillStyle = grad; ctx.fillRect(x, y, barW, bH);
      ctx.fillStyle = '#64748b'; ctx.font = '8px Arial'; ctx.textAlign = 'center';
      ctx.fillText((item.label || String(i)).substring(0, 14), x + barW / 2, pT + cH + 14);
      ctx.fillStyle = '#1e293b'; ctx.font = '8px Arial';
      ctx.fillText(item.value || 0, x + barW / 2, y - 3);
    });
    return canvas.toDataURL('image/png');
  };

  // Single-series line chart — data: [{ value, label }]
  const createSingleLineChart = (data, color, seriesName, width, height) => {
    const canvas = document.createElement('canvas');
    canvas.width = width * 2; canvas.height = height * 2;
    const ctx = canvas.getContext('2d'); ctx.scale(2, 2);

    const pL = 45, pR = 15, pT = 12, pB = 38, pLeg = 16;
    const cW = width - pL - pR, cH = height - pT - pB - pLeg;
    const maxV = Math.max(...data.map(d => d.value || 0)) || 1;
    const sp = cW / (Math.max(data.length - 1, 1));

    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = color; ctx.fillRect(pL, pT + 3, 10, 8);
    ctx.fillStyle = '#1e293b'; ctx.font = '7.5px Arial'; ctx.textAlign = 'left';
    ctx.fillText(seriesName, pL + 13, pT + 9);
    _drawGrid(ctx, pL, pT, pLeg, cW, cH, maxV);

    ctx.strokeStyle = color; ctx.lineWidth = 2; ctx.beginPath();
    data.forEach((item, i) => {
      const x = pL + i * sp, y = pT + pLeg + cH - ((item.value || 0) / maxV) * cH;
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.stroke();
    data.forEach((item, i) => {
      const x = pL + i * sp, y = pT + pLeg + cH - ((item.value || 0) / maxV) * cH;
      ctx.fillStyle = color; ctx.beginPath(); ctx.arc(x, y, 3, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#64748b'; ctx.font = '8px Arial'; ctx.textAlign = 'center';
      ctx.fillText((item.label || '').substring(0, 10), x, pT + pLeg + cH + 14);
    });
    return canvas.toDataURL('image/png');
  };

  // Generic multi-line chart — lines: [{ key, color, label }]
  const createGenericMultiLineChart = (data, lines, width, height) => {
    const canvas = document.createElement('canvas');
    canvas.width = width * 2; canvas.height = height * 2;
    const ctx = canvas.getContext('2d'); ctx.scale(2, 2);

    const pL = 45, pR = 15, pT = 10, pB = 38;
    const pLeg = Math.ceil(lines.length / 2) * 14 + 4;
    const cW = width - pL - pR, cH = height - pT - pB - pLeg;
    const maxV = Math.max(...data.flatMap(d => lines.map(l => d[l.key] || 0))) || 1;
    const sp = cW / (Math.max(data.length - 1, 1));

    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, width, height);

    lines.forEach((line, i) => {
      const col = i % 2, row = Math.floor(i / 2);
      const lx = pL + col * (cW / 2), ly = pT + row * 13;
      ctx.fillStyle = line.color; ctx.fillRect(lx, ly + 2, 10, 8);
      ctx.fillStyle = '#1e293b'; ctx.font = '7.5px Arial'; ctx.textAlign = 'left';
      ctx.fillText(line.label, lx + 13, ly + 8.5);
    });

    _drawGrid(ctx, pL, pT, pLeg, cW, cH, maxV);

    lines.forEach((line) => {
      ctx.strokeStyle = line.color; ctx.lineWidth = 2; ctx.beginPath();
      data.forEach((item, i) => {
        const x = pL + i * sp, y = pT + pLeg + cH - ((item[line.key] || 0) / maxV) * cH;
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      });
      ctx.stroke();
      data.forEach((item, i) => {
        const x = pL + i * sp, y = pT + pLeg + cH - ((item[line.key] || 0) / maxV) * cH;
        ctx.fillStyle = line.color; ctx.beginPath(); ctx.arc(x, y, 3, 0, Math.PI * 2); ctx.fill();
      });
    });

    data.forEach((item, i) => {
      const x = pL + i * sp;
      ctx.fillStyle = '#64748b'; ctx.font = '8px Arial'; ctx.textAlign = 'center';
      ctx.fillText((item.week || item.label || String(i)).substring(0, 10), x, pT + pLeg + cH + 14);
    });
    return canvas.toDataURL('image/png');
  };

  // Stacked vertical bar chart — bars: [{ key, color, label }], xKey: label field name
  const createStackedBarChart = (data, xKey, bars, width, height) => {
    const canvas = document.createElement('canvas');
    canvas.width = width * 2; canvas.height = height * 2;
    const ctx = canvas.getContext('2d'); ctx.scale(2, 2);

    const pL = 45, pR = 15, pT = 10, pB = 38;
    const pLeg = Math.ceil(bars.length / 3) * 14 + 4;
    const cW = width - pL - pR, cH = height - pT - pB - pLeg;
    const maxV = Math.max(...data.map(d => bars.reduce((s, b) => s + (d[b.key] || 0), 0))) || 1;
    const slotW = cW / data.length, barW = slotW * 0.65;

    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, width, height);

    bars.forEach((bar, i) => {
      const col = i % 3, row = Math.floor(i / 3);
      const lx = pL + col * (cW / 3), ly = pT + row * 13;
      ctx.fillStyle = bar.color; ctx.fillRect(lx, ly + 2, 10, 8);
      ctx.fillStyle = '#1e293b'; ctx.font = '7.5px Arial'; ctx.textAlign = 'left';
      ctx.fillText(bar.label, lx + 13, ly + 8.5);
    });

    _drawGrid(ctx, pL, pT, pLeg, cW, cH, maxV);

    data.forEach((item, i) => {
      const x = pL + i * slotW + (slotW - barW) / 2;
      let stackY = pT + pLeg + cH;
      bars.forEach((bar) => {
        const val = item[bar.key] || 0;
        const bH = (val / maxV) * cH;
        if (bH > 0) { ctx.fillStyle = bar.color; ctx.fillRect(x, stackY - bH, barW, bH); stackY -= bH; }
      });
      ctx.fillStyle = '#64748b'; ctx.font = '8px Arial'; ctx.textAlign = 'center';
      ctx.fillText((item[xKey] || String(i)).substring(0, 12), x + barW / 2, pT + pLeg + cH + 14);
    });
    return canvas.toDataURL('image/png');
  };

  // Area chart — data: [{ month, published }]
  const createAreaChart = (data, width, height) => {
    const canvas = document.createElement('canvas');
    canvas.width = width * 2; canvas.height = height * 2;
    const ctx = canvas.getContext('2d'); ctx.scale(2, 2);

    const pL = 45, pR = 15, pT = 10, pB = 38, pLeg = 16;
    const cW = width - pL - pR, cH = height - pT - pB - pLeg;
    const maxV = Math.max(...data.map(d => d.published || 0)) || 1;
    const sp = cW / (Math.max(data.length - 1, 1));

    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = '#3b82f6'; ctx.fillRect(pL, pT + 3, 10, 8);
    ctx.fillStyle = '#1e293b'; ctx.font = '7.5px Arial'; ctx.textAlign = 'left';
    ctx.fillText('Published', pL + 13, pT + 9);
    _drawGrid(ctx, pL, pT, pLeg, cW, cH, maxV);

    // Fill area
    ctx.beginPath();
    data.forEach((item, i) => {
      const x = pL + i * sp, y = pT + pLeg + cH - ((item.published || 0) / maxV) * cH;
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.lineTo(pL + (data.length - 1) * sp, pT + pLeg + cH);
    ctx.lineTo(pL, pT + pLeg + cH);
    ctx.closePath();
    ctx.fillStyle = 'rgba(59,130,246,0.25)'; ctx.fill();

    // Line + dots
    ctx.strokeStyle = '#3b82f6'; ctx.lineWidth = 2; ctx.beginPath();
    data.forEach((item, i) => {
      const x = pL + i * sp, y = pT + pLeg + cH - ((item.published || 0) / maxV) * cH;
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.stroke();
    data.forEach((item, i) => {
      const x = pL + i * sp, y = pT + pLeg + cH - ((item.published || 0) / maxV) * cH;
      ctx.fillStyle = '#3b82f6'; ctx.beginPath(); ctx.arc(x, y, 3, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#64748b'; ctx.font = '8px Arial'; ctx.textAlign = 'center';
      ctx.fillText((item.month || String(i)).substring(0, 5), x, pT + pLeg + cH + 14);
    });
    return canvas.toDataURL('image/png');
  };

  // Heatmap — data: [{ day, hour, value }]
  const createHeatmapChart = (data, width, height) => {
    const canvas = document.createElement('canvas');
    canvas.width = width * 2; canvas.height = height * 2;
    const ctx = canvas.getContext('2d'); ctx.scale(2, 2);
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, width, height);
    if (!data || data.length === 0) return canvas.toDataURL('image/png');

    const days  = [...new Set(data.map(d => d.day))];
    const hours = [...new Set(data.map(d => d.hour))];
    const maxV  = Math.max(...data.map(d => d.value || 0)) || 1;
    const pL = 28, pT = 8, pR = 8, pB = 8;
    const cellW = (width - pL - pR) / Math.max(hours.length, 1);
    const cellH = (height - pT - pB) / Math.max(days.length + 1, 1);

    ctx.fillStyle = '#64748b'; ctx.font = '7px Arial'; ctx.textAlign = 'center';
    hours.forEach((h, hi) => ctx.fillText(h, pL + hi * cellW + cellW / 2, pT + cellH * 0.65));

    days.forEach((day, di) => {
      const cy = pT + cellH + di * cellH;
      ctx.fillStyle = '#1e293b'; ctx.font = '7.5px Arial'; ctx.textAlign = 'right';
      ctx.fillText(day, pL - 3, cy + cellH * 0.65);
      hours.forEach((hour, hi) => {
        const cell = data.find(d => d.day === day && d.hour === hour);
        const val = cell ? cell.value : 0;
        const intensity = val / maxV;
        ctx.fillStyle = `rgba(59,130,246,${intensity.toFixed(2)})`;
        ctx.fillRect(pL + hi * cellW + 1, cy + 1, cellW - 2, cellH - 2);
        if (val > 0) {
          ctx.fillStyle = intensity > 0.5 ? '#ffffff' : '#1e293b';
          ctx.font = '6.5px Arial'; ctx.textAlign = 'center';
          ctx.fillText(val, pL + hi * cellW + cellW / 2, cy + cellH * 0.65);
        }
      });
    });
    return canvas.toDataURL('image/png');
  };

  // Platform Activity Trends — 4 lines: newUsers, appointments, chats, contentPublished
  const createActivityTrendsChart = (data, width, height) => {
    const lines = [
      { key: 'newUsers',         color: '#3b82f6', label: 'New Users' },
      { key: 'appointments',     color: '#10b981', label: 'Appts Completed' },
      { key: 'chats',            color: '#f59e0b', label: 'Chats Resolved' },
      { key: 'contentPublished', color: '#8b5cf6', label: 'Content Published' },
    ];
    return createGenericMultiLineChart(data, lines, width, height);
  };

  // Weekly Appointment Trends — 3 lines: completed, cancelledByUser, cancelledByMedical
  const createMultiLineChart = (data, width, height) => {
    const lines = [
      { key: 'completed',          color: '#10b981', label: 'Completed' },
      { key: 'cancelledByUser',    color: '#ef4444', label: 'Cancelled by User' },
      { key: 'cancelledByMedical', color: '#f59e0b', label: 'Cancelled by Medical' },
    ];
    return createGenericMultiLineChart(data, lines, width, height);
  };

  // Weekly Content Engagement — 3 lines: views, bookmarks, shares
  const createContentEngagementChart = (data, width, height) => {
    const lines = [
      { key: 'views',     color: '#3b82f6', label: 'Views' },
      { key: 'bookmarks', color: '#10b981', label: 'Bookmarks' },
      { key: 'shares',    color: '#f59e0b', label: 'Shares' },
    ];
    return createGenericMultiLineChart(data, lines, width, height);
  };

  // Donut chart — innerRadius 60%, paddingAngle 5°
  const createPieChart = (data, width, height) => {
    const canvas = document.createElement('canvas');
    canvas.width = width * 2; canvas.height = height * 2;
    const ctx = canvas.getContext('2d'); ctx.scale(2, 2);

    const cx = width / 2, cy = height / 2;
    const outerR = Math.min(width, height) / 2 - 6;
    const innerR = outerR * 0.6;
    const total = data.reduce((s, d) => s + (d.value || 0), 0) || 1;
    const gap = (5 * Math.PI) / 180;

    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, width, height);

    let startAngle = -Math.PI / 2;
    data.forEach((item) => {
      const raw = ((item.value || 0) / total) * 2 * Math.PI;
      const slice = Math.max(raw - gap, 0.001);
      ctx.beginPath();
      ctx.arc(cx, cy, outerR, startAngle + gap / 2, startAngle + gap / 2 + slice);
      ctx.arc(cx, cy, innerR, startAngle + gap / 2 + slice, startAngle + gap / 2, true);
      ctx.closePath();
      ctx.fillStyle = item.color; ctx.fill();
      startAngle += raw;
    });
    return canvas.toDataURL('image/png');
  };

  // Horizontal bar chart — data: [{ topic, engagement }]
  const createHorizontalBarChart = (data, width, height) => {
    const canvas = document.createElement('canvas');
    canvas.width = width * 2; canvas.height = height * 2;
    const ctx = canvas.getContext('2d'); ctx.scale(2, 2);

    const pL = 95, pR = 30, pT = 15, pB = 25;
    const cW = width - pL - pR, cH = height - pT - pB;
    const maxV = Math.max(...data.map(d => d.engagement || d.value || d.count || 0)) || 1;
    const rowH = cH / data.length, barH = rowH * 0.6, barOff = rowH * 0.2;

    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, width, height);

    for (let i = 0; i <= 5; i++) {
      const gx = pL + (cW / 5) * i;
      ctx.strokeStyle = '#e2e8f0'; ctx.lineWidth = 0.5;
      ctx.beginPath(); ctx.moveTo(gx, pT); ctx.lineTo(gx, pT + cH); ctx.stroke();
      ctx.fillStyle = '#64748b'; ctx.font = '8px Arial'; ctx.textAlign = 'center';
      ctx.fillText(Math.round((maxV / 5) * i), gx, pT + cH + 12);
    }

    data.forEach((item, i) => {
      const y = pT + i * rowH + barOff;
      const val = item.engagement || item.value || item.count || 0;
      const bW = Math.max(1, (val / maxV) * cW);
      const grad = ctx.createLinearGradient(pL, y, pL + bW, y);
      grad.addColorStop(0, '#3b82f6'); grad.addColorStop(1, '#1d4ed8');
      ctx.fillStyle = grad; ctx.fillRect(pL, y, bW, barH);
      ctx.fillStyle = '#64748b'; ctx.font = '8.5px Arial'; ctx.textAlign = 'right';
      ctx.fillText((item.topic || item.name || item.label || String(i)).substring(0, 18), pL - 5, y + barH / 2 + 3);
      ctx.fillStyle = '#64748b'; ctx.font = '8px Arial'; ctx.textAlign = 'left';
      ctx.fillText(val, pL + bW + 3, y + barH / 2 + 3);
    });

    ctx.strokeStyle = '#e2e8f0'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(pL, pT); ctx.lineTo(pL, pT + cH); ctx.lineTo(pL + cW, pT + cH); ctx.stroke();
    return canvas.toDataURL('image/png');
  };

  const exportToExcel = async (tab, reportDate) => {
    const workbook = XLSX.utils.book_new();
    
    if (tab === 'overview') {
      // Overview Tab Sheets
      const summaryData = [
        ['OVERVIEW REPORT'],
        ['Report Date', reportDate],
        [],
        ['SUMMARY METRICS'],
        ['Metric', 'Value'],
        ['Total Users', realStats.totalUsers],
        ['Completed Appointments', realStats.completedAppointments],
        ['Published Content Items', realStats.publishedContent],
        ['Resolved Conversations', realStats.resolvedChats],
      ];
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Overview');
      
      // Platform Activity Trend
      if (activityTrendsData && activityTrendsData.length > 0) {
        const activityData = [
          ['Platform Activity Trend'],
          ['Week', 'New Users', 'Appointments Completed', 'Chats Resolved', 'Content Published'],
          ...activityTrendsData.map(d => [d.week || 'N/A', d.newUsers || 0, d.appointments || 0, d.chats || 0, d.contentPublished || 0]),
        ];
        const activitySheet = XLSX.utils.aoa_to_sheet(activityData);
        XLSX.utils.book_append_sheet(workbook, activitySheet, 'Activity Trend');
      }
      
      // User Role Distribution
      const roleData = [
        ['User Role Distribution'],
        ['Role', 'Count'],
        ['Patients', realStats.userDistribution.patients],
        ['Medical Professionals', realStats.userDistribution.medicalProfessionals],
        ['Content Curators', realStats.userDistribution.contentCurators],
        ['Peer Educators', realStats.userDistribution.peerEducators],
      ];
      const roleSheet = XLSX.utils.aoa_to_sheet(roleData);
      XLSX.utils.book_append_sheet(workbook, roleSheet, 'User Roles');
      
    } else if (tab === 'users') {
      // Users Tab Sheets
      const summaryData = [
        ['USERS REPORT'],
        ['Report Date', reportDate],
        [],
        ['USER SUMMARY'],
        ['Metric', 'Value'],
        ['Total Users', realStats.totalUsers],
        ['Active Users', realStats.activeUsers],
        ['Inactive Users', realStats.inactiveUsers],
        ['Deactivated Accounts', realStats.deactivatedAccounts],
      ];
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Users Summary');
      
      // Medical Professional Coverage
      const medicalData = [
        ['Medical Professional Coverage'],
        ['Service Type', 'Count'],
        ['HIV Testing', realStats.medicalCoverage.hivTesting],
        ['Reproductive Consultation', realStats.medicalCoverage.reproductiveConsultation],
        ['Family Planning', realStats.medicalCoverage.familyPlanning],
      ];
      const medicalSheet = XLSX.utils.aoa_to_sheet(medicalData);
      XLSX.utils.book_append_sheet(workbook, medicalSheet, 'Medical Coverage');
      
      // New User Registration Trend
      if (userRegistrationData && userRegistrationData.length > 0) {
        const registrationData = [
          ['New User Registration Trend'],
          ['Month', 'New Users'],
          ...userRegistrationData.map(d => [d.month || 'N/A', d.newUsers || 0]),
        ];
        const registrationSheet = XLSX.utils.aoa_to_sheet(registrationData);
        XLSX.utils.book_append_sheet(workbook, registrationSheet, 'Registration Trend');
      }
      
      // Patient Gender Distribution
      const genderData = [
        ['Patient Gender Distribution'],
        ['Gender', 'Count'],
        ['Female', realStats.genderDistribution.female],
        ['Male', realStats.genderDistribution.male],
      ];
      const genderSheet = XLSX.utils.aoa_to_sheet(genderData);
      XLSX.utils.book_append_sheet(workbook, genderSheet, 'Gender Distribution');
      
      // Patient Assistance Demographics
      const assistanceData = [
        ['Patient Assistance Demographics'],
        ['Category', 'Count'],
        ['Minor', realStats.patientAssistance.minor],
        ['Senior', realStats.patientAssistance.senior],
        ['PWD (Person With Disability)', realStats.patientAssistance.pwd],
        ['NRNW (No Recorded Name/Work)', realStats.patientAssistance.nrnw],
        ['Other', realStats.patientAssistance.other],
      ];
      const assistanceSheet = XLSX.utils.aoa_to_sheet(assistanceData);
      XLSX.utils.book_append_sheet(workbook, assistanceSheet, 'Assistance Demographics');
      
    } else if (tab === 'content') {
      // Content Tab Sheets
      const summaryData = [
        ['CONTENT REPORT'],
        ['Report Date', reportDate],
        [],
        ['CONTENT SUMMARY'],
        ['Metric', 'Value'],
        ['Live Content', realStats.contentStats.liveContent],
        ['Live Announcements', realStats.contentStats.liveAnnouncements],
        ['Pending Review', realStats.contentStats.pendingReview],
        ['Archived Content', realStats.contentStats.archivedContent],
      ];
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Content Summary');
      
      // Weekly Content Engagement
      if (contentEngagementData && contentEngagementData.length > 0) {
        const engagementData = [
          ['Weekly Content Engagement'],
          ['Week', 'Views', 'Bookmarks', 'Shares'],
          ...contentEngagementData.map(d => [d.week || 'N/A', d.views || 0, d.bookmarks || 0, d.shares || 0]),
        ];
        const engagementSheet = XLSX.utils.aoa_to_sheet(engagementData);
        XLSX.utils.book_append_sheet(workbook, engagementSheet, 'Weekly Engagement');
      }
      
      // Top Performing Content
      if (realStats.topPerformingContent && realStats.topPerformingContent.length > 0) {
        const topPerformingData = [
          ['Top Performing Content'],
          ['Title', 'Views', 'Bookmarks', 'Shares'],
          ...realStats.topPerformingContent.slice(0, 10).map(d => [d.title || 'N/A', d.views || 0, d.bookmarks || 0, d.shares || 0]),
        ];
        const topPerformingSheet = XLSX.utils.aoa_to_sheet(topPerformingData);
        XLSX.utils.book_append_sheet(workbook, topPerformingSheet, 'Top Performing');
      }
      
      // Most Engaged Topics
      if (realStats.topEngagedTopics && realStats.topEngagedTopics.length > 0) {
        const topicsData = [
          ['Most Engaged Topics'],
          ['Topic', 'Engagement'],
          ...realStats.topEngagedTopics.map(d => [d.topic || d.label || 'N/A', d.engagement || d.value || 0]),
        ];
        const topicsSheet = XLSX.utils.aoa_to_sheet(topicsData);
        XLSX.utils.book_append_sheet(workbook, topicsSheet, 'Top Topics');
      }
      
      // Content Awaiting Approval
      if (realStats.pendingApprovals && realStats.pendingApprovals.length > 0) {
        const awaitingData = [
          ['Content Awaiting Approval'],
          ['Title', 'Creator', 'Category', 'Submitted Date'],
          ...realStats.pendingApprovals.map(d => [
            d.title || 'N/A',
            d.author || 'N/A',
            d.category || 'N/A',
            d.submittedAt?.toDate ? d.submittedAt.toDate().toLocaleDateString() : d.submittedAt || 'N/A',
          ]),
        ];
        const awaitingSheet = XLSX.utils.aoa_to_sheet(awaitingData);
        XLSX.utils.book_append_sheet(workbook, awaitingSheet, 'Awaiting Approval');
      }
      
      // Monthly Content Publishing Activity
      if (contentPublishingData && contentPublishingData.length > 0) {
        const publishingData = [
          ['Monthly Content Publishing Activity'],
          ['Month', 'Published Items'],
          ...contentPublishingData.map(d => [d.month || 'N/A', d.published || 0]),
        ];
        const publishingSheet = XLSX.utils.aoa_to_sheet(publishingData);
        XLSX.utils.book_append_sheet(workbook, publishingSheet, 'Publishing Activity');
      }
      
    } else if (tab === 'chats') {
      // Chats Tab Sheets
      // Top Peer Educators
      if (topPeerEducatorsData && topPeerEducatorsData.length > 0) {
        const peersData = [
          ['Top Peer Educators'],
          ['Name', 'Resolved Chats'],
          ...topPeerEducatorsData.map(d => [d.name || 'N/A', d.resolvedChats || 0]),
        ];
        const peersSheet = XLSX.utils.aoa_to_sheet(peersData);
        XLSX.utils.book_append_sheet(workbook, peersSheet, 'Top Educators');
      }
      
      // Weekly Chat Activity
      if (chatActivityData && chatActivityData.length > 0) {
        const activityData = [
          ['Weekly Chat Activity'],
          ['Week', 'Assigned', 'Resolved'],
          ...chatActivityData.map(d => [d.week || 'N/A', d.assigned || 0, d.resolved || 0]),
        ];
        const activitySheet = XLSX.utils.aoa_to_sheet(activityData);
        XLSX.utils.book_append_sheet(workbook, activitySheet, 'Weekly Activity');
      }
      
      // Escalated Case Status
      if (realStats.escalationStatus && realStats.escalationStatus.length > 0) {
        const escalationData = [
          ['Escalated Case Status'],
          ['Category', 'Pending Review', 'Under Assessment', 'Resolved'],
          ...realStats.escalationStatus.map(d => [d.category || 'N/A', d.pending || 0, d.underAssessment || 0, d.resolved || 0]),
        ];
        const escalationSheet = XLSX.utils.aoa_to_sheet(escalationData);
        XLSX.utils.book_append_sheet(workbook, escalationSheet, 'Escalation Status');
      }
      
      // Today's Chat Categories
      if (realStats.chatCategories && realStats.chatCategories.length > 0) {
        const categoriesData = [
          ['Today\'s Chat Categories'],
          ['Category', 'Count'],
          ...realStats.chatCategories.map(d => [d.name || 'N/A', d.value || 0]),
        ];
        const categoriesSheet = XLSX.utils.aoa_to_sheet(categoriesData);
        XLSX.utils.book_append_sheet(workbook, categoriesSheet, 'Categories');
      }
      
    } else if (tab === 'appointments') {
      // Appointments Tab Sheets
      // Top Medical Professionals
      if (realStats.topMedicalProfessionals && realStats.topMedicalProfessionals.length > 0) {
        const profsData = [
          ['Top Medical Professionals'],
          ['Name', 'Completed Appointments', 'Service'],
          ...realStats.topMedicalProfessionals.map(d => [d.name || 'N/A', d.completedAppointments || 0, d.assignedServices || 'N/A']),
        ];
        const profsSheet = XLSX.utils.aoa_to_sheet(profsData);
        XLSX.utils.book_append_sheet(workbook, profsSheet, 'Top Professionals');
      }
      
      // Appointment Status Overview
      if (realStats.appointmentStatusOverview && realStats.appointmentStatusOverview.length > 0) {
        const statusData = [
          ['Appointment Status Overview'],
          ['Service', 'Pending', 'Confirmed', 'Accepted'],
          ...realStats.appointmentStatusOverview.map(d => [d.service || 'N/A', d.pending || 0, d.confirmed || 0, d.accepted || 0]),
        ];
        const statusSheet = XLSX.utils.aoa_to_sheet(statusData);
        XLSX.utils.book_append_sheet(workbook, statusSheet, 'Status Overview');
      }
      
      // Weekly Appointment Trends
      if (realStats.weeklyAppointmentTrends && realStats.weeklyAppointmentTrends.length > 0) {
        const trendsData = [
          ['Weekly Appointment Trends'],
          ['Week', 'Completed', 'Cancelled by User', 'Cancelled by Medical'],
          ...realStats.weeklyAppointmentTrends.map(d => [d.week || 'N/A', d.completed || 0, d.cancelledByUser || 0, d.cancelledByMedical || 0]),
        ];
        const trendsSheet = XLSX.utils.aoa_to_sheet(trendsData);
        XLSX.utils.book_append_sheet(workbook, trendsSheet, 'Weekly Trends');
      }
      
      // Today's Appointment Overview
      const todayData = [
        ['Today\'s Appointment Overview'],
        ['Metric', 'Count'],
        ['Total', realStats.todayAppointments.total],
        ['Pending', realStats.todayAppointments.pending],
        ['Confirmed', realStats.todayAppointments.confirmed],
        ['Completed', realStats.todayAppointments.completed],
      ];
      const todaySheet = XLSX.utils.aoa_to_sheet(todayData);
      XLSX.utils.book_append_sheet(workbook, todaySheet, 'Today\'s Overview');
      
      // Service Demand Distribution
      if (realStats.serviceDemand && realStats.serviceDemand.length > 0) {
        const demandData = [
          ['Service Demand Distribution'],
          ['Service', 'Demand'],
          ...realStats.serviceDemand.map(d => [d.service || 'N/A', d.demand || 0]),
        ];
        const demandSheet = XLSX.utils.aoa_to_sheet(demandData);
        XLSX.utils.book_append_sheet(workbook, demandSheet, 'Service Demand');
      }
      
      // Appointment Demand by Time (Peak Hours)
      if (realStats.peakHours && realStats.peakHours.length > 0) {
        const peakData = [
          ['Appointment Demand by Time'],
          ['Day', 'Hour', 'Demand'],
          ...realStats.peakHours.map(d => [d.day || 'N/A', d.hour || 'N/A', d.value || 0]),
        ];
        const peakSheet = XLSX.utils.aoa_to_sheet(peakData);
        XLSX.utils.book_append_sheet(workbook, peakSheet, 'Demand by Time');
      }
    }
    
    XLSX.writeFile(workbook, `AuraVit_${tab}_Report_${reportDate.replace(/ /g, '_')}.xlsx`);
  };

  const exportToCSV = async (tab, reportDate) => {
    let csvData = [];
    
    if (tab === 'overview') {
      csvData = [
        ['OVERVIEW REPORT'],
        ['Report Date', reportDate],
        [],
        ['SUMMARY METRICS'],
        ['Metric', 'Value'],
        ['Total Users', realStats.totalUsers],
        ['Completed Appointments', realStats.completedAppointments],
        ['Published Content Items', realStats.publishedContent],
        ['Resolved Conversations', realStats.resolvedChats],
        [],
        ['PLATFORM ACTIVITY TREND'],
        ['Week', 'New Users', 'Appointments Completed', 'Chats Resolved', 'Content Published'],
      ];
      if (activityTrendsData && activityTrendsData.length > 0) {
        csvData.push(...activityTrendsData.map(d => [d.week || 'N/A', d.newUsers || 0, d.appointments || 0, d.chats || 0, d.contentPublished || 0]));
      }
      csvData.push([]);
      csvData.push(['USER ROLE DISTRIBUTION']);
      csvData.push(['Role', 'Count']);
      csvData.push(['Patients', realStats.userDistribution.patients]);
      csvData.push(['Medical Professionals', realStats.userDistribution.medicalProfessionals]);
      csvData.push(['Content Curators', realStats.userDistribution.contentCurators]);
      csvData.push(['Peer Educators', realStats.userDistribution.peerEducators]);
      
    } else if (tab === 'users') {
      csvData = [
        ['USERS REPORT'],
        ['Report Date', reportDate],
        [],
        ['USER SUMMARY'],
        ['Metric', 'Value'],
        ['Total Users', realStats.totalUsers],
        ['Active Users', realStats.activeUsers],
        ['Inactive Users', realStats.inactiveUsers],
        ['Deactivated Accounts', realStats.deactivatedAccounts],
        [],
        ['MEDICAL PROFESSIONAL COVERAGE'],
        ['Service Type', 'Count'],
        ['HIV Testing', realStats.medicalCoverage.hivTesting],
        ['Reproductive Consultation', realStats.medicalCoverage.reproductiveConsultation],
        ['Family Planning', realStats.medicalCoverage.familyPlanning],
        [],
        ['NEW USER REGISTRATION TREND'],
        ['Month', 'New Users'],
      ];
      if (userRegistrationData && userRegistrationData.length > 0) {
        csvData.push(...userRegistrationData.map(d => [d.month || 'N/A', d.newUsers || 0]));
      }
      csvData.push([]);
      csvData.push(['PATIENT GENDER DISTRIBUTION']);
      csvData.push(['Gender', 'Count']);
      csvData.push(['Female', realStats.genderDistribution.female]);
      csvData.push(['Male', realStats.genderDistribution.male]);
      csvData.push([]);
      csvData.push(['PATIENT ASSISTANCE DEMOGRAPHICS']);
      csvData.push(['Category', 'Count']);
      csvData.push(['Minor', realStats.patientAssistance.minor]);
      csvData.push(['Senior', realStats.patientAssistance.senior]);
      csvData.push(['PWD (Person With Disability)', realStats.patientAssistance.pwd]);
      csvData.push(['NRNW (No Recorded Name/Work)', realStats.patientAssistance.nrnw]);
      csvData.push(['Other', realStats.patientAssistance.other]);
      
    } else if (tab === 'content') {
      csvData = [
        ['CONTENT REPORT'],
        ['Report Date', reportDate],
        [],
        ['CONTENT SUMMARY'],
        ['Metric', 'Value'],
        ['Live Content', realStats.contentStats.liveContent],
        ['Live Announcements', realStats.contentStats.liveAnnouncements],
        ['Pending Review', realStats.contentStats.pendingReview],
        ['Archived Content', realStats.contentStats.archivedContent],
        [],
        ['WEEKLY CONTENT ENGAGEMENT'],
        ['Week', 'Views', 'Bookmarks', 'Shares'],
      ];
      if (contentEngagementData && contentEngagementData.length > 0) {
        csvData.push(...contentEngagementData.map(d => [d.week || 'N/A', d.views || 0, d.bookmarks || 0, d.shares || 0]));
      }
      csvData.push([]);
      csvData.push(['TOP PERFORMING CONTENT']);
      csvData.push(['Title', 'Views', 'Bookmarks', 'Shares']);
      if (realStats.topPerformingContent && realStats.topPerformingContent.length > 0) {
        csvData.push(...realStats.topPerformingContent.slice(0, 10).map(d => [d.title || 'N/A', d.views || 0, d.bookmarks || 0, d.shares || 0]));
      }
      csvData.push([]);
      csvData.push(['MOST ENGAGED TOPICS']);
      csvData.push(['Topic', 'Engagement']);
      if (realStats.topEngagedTopics && realStats.topEngagedTopics.length > 0) {
        csvData.push(...realStats.topEngagedTopics.map(d => [d.topic || d.label || 'N/A', d.engagement || d.value || 0]));
      }
      csvData.push([]);
      csvData.push(['CONTENT AWAITING APPROVAL']);
      csvData.push(['Title', 'Creator', 'Category', 'Submitted Date']);
      if (realStats.pendingApprovals && realStats.pendingApprovals.length > 0) {
        csvData.push(...realStats.pendingApprovals.map(d => [
          d.title || 'N/A',
          d.author || 'N/A',
          d.category || 'N/A',
          d.submittedAt?.toDate ? d.submittedAt.toDate().toLocaleDateString() : d.submittedAt || 'N/A',
        ]));
      }
      csvData.push([]);
      csvData.push(['MONTHLY CONTENT PUBLISHING ACTIVITY']);
      csvData.push(['Month', 'Published Items']);
      if (contentPublishingData && contentPublishingData.length > 0) {
        csvData.push(...contentPublishingData.map(d => [d.month || 'N/A', d.published || 0]));
      }
      
    } else if (tab === 'chats') {
      csvData = [
        ['CHATS REPORT'],
        ['Report Date', reportDate],
        [],
        ['TOP PEER EDUCATORS'],
        ['Name', 'Resolved Chats'],
      ];
      if (topPeerEducatorsData && topPeerEducatorsData.length > 0) {
        csvData.push(...topPeerEducatorsData.map(d => [d.name || 'N/A', d.resolvedChats || 0]));
      }
      csvData.push([]);
      csvData.push(['WEEKLY CHAT ACTIVITY']);
      csvData.push(['Week', 'Assigned', 'Resolved']);
      if (chatActivityData && chatActivityData.length > 0) {
        csvData.push(...chatActivityData.map(d => [d.week || 'N/A', d.assigned || 0, d.resolved || 0]));
      }
      csvData.push([]);
      csvData.push(['ESCALATED CASE STATUS']);
      csvData.push(['Category', 'Pending Review', 'Under Assessment', 'Resolved']);
      if (realStats.escalationStatus && realStats.escalationStatus.length > 0) {
        csvData.push(...realStats.escalationStatus.map(d => [d.category || 'N/A', d.pending || 0, d.underAssessment || 0, d.resolved || 0]));
      }
      csvData.push([]);
      csvData.push(['TODAY\'S CHAT CATEGORIES']);
      csvData.push(['Category', 'Count']);
      if (realStats.chatCategories && realStats.chatCategories.length > 0) {
        csvData.push(...realStats.chatCategories.map(d => [d.name || 'N/A', d.value || 0]));
      }
      
    } else if (tab === 'appointments') {
      csvData = [
        ['APPOINTMENTS REPORT'],
        ['Report Date', reportDate],
        [],
        ['TOP MEDICAL PROFESSIONALS'],
        ['Name', 'Completed Appointments', 'Service'],
      ];
      if (realStats.topMedicalProfessionals && realStats.topMedicalProfessionals.length > 0) {
        csvData.push(...realStats.topMedicalProfessionals.map(d => [d.name || 'N/A', d.completedAppointments || 0, d.assignedServices || 'N/A']));
      }
      csvData.push([]);
      csvData.push(['APPOINTMENT STATUS OVERVIEW']);
      csvData.push(['Service', 'Pending', 'Confirmed', 'Accepted']);
      if (realStats.appointmentStatusOverview && realStats.appointmentStatusOverview.length > 0) {
        csvData.push(...realStats.appointmentStatusOverview.map(d => [d.service || 'N/A', d.pending || 0, d.confirmed || 0, d.accepted || 0]));
      }
      csvData.push([]);
      csvData.push(['WEEKLY APPOINTMENT TRENDS']);
      csvData.push(['Week', 'Completed', 'Cancelled by User', 'Cancelled by Medical']);
      if (realStats.weeklyAppointmentTrends && realStats.weeklyAppointmentTrends.length > 0) {
        csvData.push(...realStats.weeklyAppointmentTrends.map(d => [d.week || 'N/A', d.completed || 0, d.cancelledByUser || 0, d.cancelledByMedical || 0]));
      }
      csvData.push([]);
      csvData.push(['TODAY\'S APPOINTMENT OVERVIEW']);
      csvData.push(['Metric', 'Count']);
      csvData.push(['Total', realStats.todayAppointments.total]);
      csvData.push(['Pending', realStats.todayAppointments.pending]);
      csvData.push(['Confirmed', realStats.todayAppointments.confirmed]);
      csvData.push(['Completed', realStats.todayAppointments.completed]);
      csvData.push([]);
      csvData.push(['SERVICE DEMAND DISTRIBUTION']);
      csvData.push(['Service', 'Demand']);
      if (realStats.serviceDemand && realStats.serviceDemand.length > 0) {
        csvData.push(...realStats.serviceDemand.map(d => [d.service || 'N/A', d.demand || 0]));
      }
      csvData.push([]);
      csvData.push(['APPOINTMENT DEMAND BY TIME']);
      csvData.push(['Day', 'Hour', 'Demand']);
      if (realStats.peakHours && realStats.peakHours.length > 0) {
        csvData.push(...realStats.peakHours.map(d => [d.day || 'N/A', d.hour || 'N/A', d.value || 0]));
      }
    }
    
    const csvContent = csvData.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `AuraVit_${tab}_Report_${reportDate.replace(/ /g, '_')}.csv`;
    link.click();
  };

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "users", label: "Users" },
    { id: "content", label: "Content" },
    { id: "chats", label: "Chats" },
    { id: "appointments", label: "Appointments" }
  ];

  // Pagination logic for today's schedule
  const todayScheduleTotalPages = Math.ceil((realStats.todaySchedule?.length || 0) / todayScheduleItemsPerPage);
  const todayScheduleStartIndex = (todaySchedulePage - 1) * todayScheduleItemsPerPage;
  const todayScheduleEndIndex = todayScheduleStartIndex + todayScheduleItemsPerPage;
  const paginatedTodaySchedule = (realStats.todaySchedule || []).slice(todayScheduleStartIndex, todayScheduleEndIndex);

  const handleTodaySchedulePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= todayScheduleTotalPages) {
      setTodaySchedulePage(newPage);
    }
  };

  return (
    <div className="admin-dashboard-container">
      {/* Dashboard Header */}
      <div className="dashboard-header">
        {/* Tabs Navigation */}
        <nav className="admin-tabs-nav">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={activeTab === tab.id ? "admin-tab active" : "admin-tab"}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        <div className="dashboard-header-actions">
          <button className="dashboard-action-btn" onClick={() => setShowExportModal(true)}>
            <FiDownload /> Export Report
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === "overview" && (
          <div className="overview-tab">
            {/* Row 1: 4 Statistic Cards */}
            <div className="stats-grid">
              {/* Container 1: User Growth */}
              <div className="stat-card gradient-1">
                <div className="stat-card-header">
                </div>
                <div className="stat-content">
                  {loadingStats ? (
                    <div className="skeleton-card">
                      <div className="skeleton skeleton-header"></div>
                      <div className="skeleton skeleton-subheader"></div>
                    </div>
                  ) : (
                    <div className="stat-content-header">
                      <h3 className="stat-count" style={{ color: '#ffffff' }}>{realStats.totalUsers.toLocaleString()}</h3>
                      <p className="stat-label">Total<br />Users</p>
                      {realStats.userGrowthPercent !== null && (
                        <p className="stat-change" style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '12px',
                          marginTop: '6px',
                          color: realStats.userGrowthPercent >= 0 ? '#bbf7d0' : '#fecaca',
                          fontWeight: 500
                        }}>
                          {realStats.userGrowthPercent >= 0
                            ? <FiArrowUp size={12} />
                            : <FiArrowDown size={12} />}
                          {Math.abs(realStats.userGrowthPercent)}% from last month
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Container 2: Appointment Activity */}
              <div className="stat-card gradient-2">
                <div className="stat-card-header">
                </div>
                <div className="stat-content">
                  {loadingStats ? (
                    <div className="skeleton-card">
                      <div className="skeleton skeleton-header"></div>
                      <div className="skeleton skeleton-subheader"></div>
                    </div>
                  ) : (
                    <div className="stat-content-header">
                      <h3 className="stat-count" style={{ color: '#ffffff' }}>{realStats.completedAppointments.toLocaleString()}</h3>
                      <p className="stat-label">Completed<br />Appointments</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Container 3: Content Library */}
              <div className="stat-card gradient-3">
                <div className="stat-card-header">
                </div>
                <div className="stat-content">
                  {loadingStats ? (
                    <div className="skeleton-card">
                      <div className="skeleton skeleton-header"></div>
                      <div className="skeleton skeleton-subheader"></div>
                    </div>
                  ) : (
                    <div className="stat-content-header">
                      <h3 className="stat-count" style={{ color: '#ffffff' }}>{realStats.publishedContent.toLocaleString()}</h3>
                      <p className="stat-label">Published<br />Content</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Container 4: Chat Resolution */}
              <div className="stat-card gradient-4">
                <div className="stat-card-header">
                </div>
                <div className="stat-content">
                  {loadingStats ? (
                    <div className="skeleton-card">
                      <div className="skeleton skeleton-header"></div>
                      <div className="skeleton skeleton-subheader"></div>
                    </div>
                  ) : (
                    <div className="stat-content-header">
                      <h3 className="stat-count" style={{ color: '#ffffff' }}>{realStats.resolvedChats.toLocaleString()}</h3>
                      <p className="stat-label">Resolved<br />Conversations</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Row 2: 2 Columns - Charts */}
            <div className="charts-row">
              {/* Container 5: Platform Activity Summary */}
              <div className="chart-container" data-export-chart="activity-trends">
                <div className="chart-header">
                  <h3>Platform Activity Trends</h3>
                  <p>Weekly activity overview</p>
                </div>
                {loadingActivityTrends ? (
                  <div className="skeleton-chart">
                    <div className="skeleton skeleton-header"></div>
                    <div className="skeleton skeleton-subheader"></div>
                    <div className="skeleton skeleton-content"></div>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={activityTrendsData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis 
                        dataKey="week" 
                        stroke="#64748b"
                        tick={{ fontSize: 12 }}
                        interval={0}
                      />
                      <YAxis stroke="#64748b" allowDecimals={false} />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                        }}
                      />
                      <Legend 
                        verticalAlign="bottom" 
                        height={50}
                        wrapperStyle={{ paddingTop: '20px' }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="newUsers" 
                        stroke="#3b82f6" 
                        strokeWidth={2}
                        name="New Users"
                        dot={{ fill: '#3b82f6' }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="appointments" 
                        stroke="#10b981" 
                        strokeWidth={2}
                        name="Appointments Completed"
                        dot={{ fill: '#10b981' }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="chats" 
                        stroke="#f59e0b" 
                        strokeWidth={2}
                        name="Chats Resolved"
                        dot={{ fill: '#f59e0b' }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="contentPublished" 
                        stroke="#8b5cf6" 
                        strokeWidth={2}
                        name="Content Published"
                        dot={{ fill: '#8b5cf6' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Container 6: User Distribution */}
              <div className="chart-container pie-chart-layout" data-export-chart="user-role-distribution">
                <div className="chart-header-full">
                  <h3>User Role Distribution</h3>
                  <p>User breakdown by role</p>
                </div>
                <div className="pie-chart-content">
                  {loadingUserDistribution ? (
                    <>
                      <div className="pie-chart-legend">
                        <h4>User Roles</h4>
                        {[1, 2, 3, 4].map((index) => (
                          <div key={index} className="skeleton-legend-item">
                            <div className="skeleton skeleton-legend-color"></div>
                            <div className="skeleton skeleton-legend-label"></div>
                          </div>
                        ))}
                      </div>
                      <div className="pie-chart-wrapper">
                        <div className="skeleton skeleton-pie"></div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="pie-chart-legend">
                        <h4>User Roles</h4>
                        {[
                          { name: "Patients", value: realStats.userDistribution.patients, color: "#3b82f6" },
                          { name: "Medical Professionals", value: realStats.userDistribution.medicalProfessionals, color: "#10b981" },
                          { name: "Content Curators", value: realStats.userDistribution.contentCurators, color: "#f59e0b" },
                          { name: "Peer Educators", value: realStats.userDistribution.peerEducators, color: "#8b5cf6" }
                        ].map((item, index) => (
                          <div key={index} className="legend-item">
                            <div className="legend-color" style={{ backgroundColor: item.color }}></div>
                            <span className="legend-label">{item.name}</span>
                          </div>
                        ))}
                      </div>
                      <div className="pie-chart-wrapper">
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={[
                                { name: "Patients", value: realStats.userDistribution.patients, color: "#3b82f6" },
                                { name: "Medical Professionals", value: realStats.userDistribution.medicalProfessionals, color: "#10b981" },
                                { name: "Content Curators", value: realStats.userDistribution.contentCurators, color: "#f59e0b" },
                                { name: "Peer Educators", value: realStats.userDistribution.peerEducators, color: "#8b5cf6" }
                              ]}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={100}
                              paddingAngle={5}
                              dataKey="value"
                              label={false}
                            >
                              {[
                                { name: "Patients", value: realStats.userDistribution.patients, color: "#3b82f6" },
                                { name: "Medical Professionals", value: realStats.userDistribution.medicalProfessionals, color: "#10b981" },
                                { name: "Content Curators", value: realStats.userDistribution.contentCurators, color: "#f59e0b" },
                                { name: "Peer Educators", value: realStats.userDistribution.peerEducators, color: "#8b5cf6" }
                              ].map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip 
                              contentStyle={{
                                backgroundColor: '#ffffff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                              }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "users" && (
          <div className="users-tab">
            <div className="users-layout">
              {/* Create Staff Account Button */}
              <div className="users-action-section">
                <button className="create-staff-btn" onClick={() => setShowCreateStaff(true)}>
                  <FiUserPlus /> Create Staff
                </button>
              </div>

              {/* Stats Grid - 4 Cards */}
              <div className="stats-grid">
                {/* Card 1 - Total Users */}
                <div className="stat-card gradient-1">
                  <div className="stat-card-header">
                  </div>
                  <div className="stat-content">
                    {loadingStats ? (
                      <div className="skeleton-card">
                        <div className="skeleton skeleton-header"></div>
                        <div className="skeleton skeleton-subheader"></div>
                      </div>
                    ) : (
                      <div className="stat-content-header">
                        <h3 className="stat-count" style={{ color: '#ffffff' }}>{realStats.totalUsers.toLocaleString()}</h3>
                        <p className="stat-label">Total<br />Users</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Card 2 - Active Users Today */}
                <div className="stat-card gradient-2">
                  <div className="stat-card-header">
                  </div>
                  <div className="stat-content">
                    {loadingStats ? (
                      <div className="skeleton-card">
                        <div className="skeleton skeleton-header"></div>
                        <div className="skeleton skeleton-subheader"></div>
                      </div>
                    ) : (
                      <div className="stat-content-header">
                        <h3 className="stat-count" style={{ color: '#ffffff' }}>{realStats.activeUsers.toLocaleString()}</h3>
                        <p className="stat-label">Active<br />Users</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Card 3 - Inactive Users */}
                <div className="stat-card gradient-3">
                  <div className="stat-card-header">
                  </div>
                  <div className="stat-content">
                    {loadingStats ? (
                      <div className="skeleton-card">
                        <div className="skeleton skeleton-header"></div>
                        <div className="skeleton skeleton-subheader"></div>
                      </div>
                    ) : (
                      <div className="stat-content-header">
                        <h3 className="stat-count" style={{ color: '#ffffff' }}>{realStats.inactiveUsers.toLocaleString()}</h3>
                        <p className="stat-label">Inactive<br />Users</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Card 4 - Deactivated Accounts */}
                <div className="stat-card gradient-4">
                  <div className="stat-card-header">
                  </div>
                  <div className="stat-content">
                    {loadingStats ? (
                      <div className="skeleton-card">
                        <div className="skeleton skeleton-header"></div>
                        <div className="skeleton skeleton-subheader"></div>
                      </div>
                    ) : (
                      <div className="stat-content-header">
                        <h3 className="stat-count" style={{ color: '#ffffff' }}>{realStats.deactivatedAccounts.toLocaleString()}</h3>
                        <p className="stat-label">Deactivated<br />Accounts</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Row 2: Container 2 and Container 3 */}
              <div className="users-charts-row-3">
                <div className="chart-container" data-export-chart="medical-coverage">
                  <div className="chart-header chart-header-spaced">
                    <h3>Medical Professional Coverage</h3>
                    <p>Staff count by service</p>
                  </div>
                  {loadingMedicalCoverage ? (
                    <div className="skeleton-chart">
                      <div className="skeleton skeleton-header"></div>
                      <div className="skeleton skeleton-subheader"></div>
                      <div className="skeleton-bars-vertical">
                        {[1, 2, 3].map((index) => (
                          <div 
                            key={index} 
                            className="skeleton skeleton-bar-vertical" 
                            style={{ height: `${200 - (index * 50)}px`, width: '150px' }}
                          ></div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={[
                        { service: "HIV Testing", coverage: realStats.medicalCoverage.hivTesting },
                        { service: "Reproductive Consultation", coverage: realStats.medicalCoverage.reproductiveConsultation },
                        { service: "Family Planning", coverage: realStats.medicalCoverage.familyPlanning }
                        
                      ]} margin={{ top: 20, right: 20, left: 5, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis 
                          dataKey="service" 
                          stroke="#64748b"
                          tick={{ fontSize: 12 }}
                          interval={0}
                          height={60}
                        />
                        <YAxis stroke="#64748b" allowDecimals={false} />
                        <Tooltip 
                          contentStyle={{
                            backgroundColor: '#ffffff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                          }}
                        />
                        <Bar dataKey="coverage" fill="url(#barGradient)" name="Staff Count" />
                        <defs>
                          <linearGradient id="barGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#3b82f6" />
                            <stop offset="100%" stopColor="#1d4ed8" />
                          </linearGradient>
                        </defs>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>

                <div className="chart-container" data-export-chart="user-registration">
                  <div className="chart-header chart-header-spaced">
                    <h3>New User Registration Trend</h3>
                    <p>Monthly growth</p>
                  </div>
                  {loadingUserRegistration ? (
                    <div className="skeleton-chart">
                      <div className="skeleton skeleton-header"></div>
                      <div className="skeleton skeleton-subheader"></div>
                      <div className="skeleton skeleton-content"></div>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={userRegistrationData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis 
                          dataKey="month" 
                          stroke="#64748b"
                          tick={{ fontSize: 12 }}
                          interval={0}
                        />
                        <YAxis stroke="#64748b" allowDecimals={false} />
                        <Tooltip 
                          contentStyle={{
                            backgroundColor: '#ffffff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                          }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="newUsers" 
                          stroke="#8b5cf6" 
                          strokeWidth={2}
                          name="New Users"
                          dot={{ fill: '#8b5cf6' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              {/* Row: Patient Demographics */}
              <div className="users-charts-row-4">
                <div className="chart-container pie-chart-layout" data-export-chart="gender-distribution">
                  <div className="chart-header-full">
                    <h3>Patient Gender Distribution</h3>
                    <p>Gender breakdown</p>
                  </div>
                  <div className="pie-chart-content">
                    {loadingGenderDistribution ? (
                      <>
                        <div className="pie-chart-legend">
                          <h4>Gender</h4>
                          {[1, 2].map((index) => (
                            <div key={index} className="skeleton-legend-item">
                              <div className="skeleton skeleton-legend-color"></div>
                              <div className="skeleton skeleton-legend-label"></div>
                            </div>
                          ))}
                        </div>
                        <div className="pie-chart-wrapper">
                          <div className="skeleton skeleton-pie"></div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="pie-chart-legend">
                          <h4>Gender</h4>
                          {[
                            { name: "Female", value: realStats.genderDistribution.female, color: "#ec4899" },
                            { name: "Male", value: realStats.genderDistribution.male, color: "#3b82f6" }
                          ].map((item, index) => (
                            <div key={index} className="legend-item">
                              <div className="legend-color" style={{ backgroundColor: item.color }}></div>
                              <span className="legend-label">{item.name}</span>
                            </div>
                          ))}
                        </div>
                        <div className="pie-chart-wrapper">
                          <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                              <Pie
                                data={[
                                  { name: "Female", value: realStats.genderDistribution.female, color: "#ec4899" },
                                  { name: "Male", value: realStats.genderDistribution.male, color: "#3b82f6" }
                                ]}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={100}
                                paddingAngle={5}
                                dataKey="value"
                              >
                                {[
                                  { name: "Female", color: "#ec4899" },
                                  { name: "Male", color: "#3b82f6" }
                                ].map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                              </Pie>
                              <Tooltip
                                contentStyle={{
                                  backgroundColor: '#ffffff',
                                  border: '1px solid #e2e8f0',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                                }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="chart-container pie-chart-layout" data-export-chart="patient-assistance">
                  <div className="chart-header-full">
                    <h3>Patient Assistance Demographics</h3>
                    <p>Assistance type breakdown</p>
                  </div>
                  <div className="pie-chart-content">
                    {loadingPatientAssistance ? (
                      <>
                        <div className="pie-chart-legend">
                          <h4>Assistance Type</h4>
                          {[1, 2, 3, 4, 5].map((index) => (
                            <div key={index} className="skeleton-legend-item">
                              <div className="skeleton skeleton-legend-color"></div>
                              <div className="skeleton skeleton-legend-label"></div>
                            </div>
                          ))}
                        </div>
                        <div className="pie-chart-wrapper">
                          <div className="skeleton skeleton-pie"></div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="pie-chart-legend">
                          <h4>Assistance Type</h4>
                          {[
                            { name: "Minor", value: realStats.patientAssistance.minor, color: "#f59e0b" },
                            { name: "Senior", value: realStats.patientAssistance.senior, color: "#10b981" },
                            { name: "PWD", value: realStats.patientAssistance.pwd, color: "#8b5cf6" },
                            { name: "NRNW", value: realStats.patientAssistance.nrnw, color: "#ef4444" },
                            { name: "Other", value: realStats.patientAssistance.other, color: "#64748b" }
                          ].map((item, index) => (
                            <div key={index} className="legend-item">
                              <div className="legend-color" style={{ backgroundColor: item.color }}></div>
                              <span className="legend-label">{item.name}</span>
                            </div>
                          ))}
                        </div>
                        <div className="pie-chart-wrapper">
                          <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                              <Pie
                                data={[
                                  { name: "Minor", value: realStats.patientAssistance.minor, color: "#f59e0b" },
                                  { name: "Senior", value: realStats.patientAssistance.senior, color: "#10b981" },
                                  { name: "PWD", value: realStats.patientAssistance.pwd, color: "#8b5cf6" },
                                  { name: "NRNW", value: realStats.patientAssistance.nrnw, color: "#ef4444" },
                                  { name: "Other", value: realStats.patientAssistance.other, color: "#64748b" }
                                ]}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={100}
                                paddingAngle={5}
                                dataKey="value"
                              >
                                {[
                                  { name: "Minor", color: "#f59e0b" },
                                  { name: "Senior", color: "#10b981" },
                                  { name: "PWD", color: "#8b5cf6" },
                                  { name: "NRNW", color: "#ef4444" },
                                  { name: "Other", color: "#64748b" }
                                ].map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                              </Pie>
                              <Tooltip
                                contentStyle={{
                                  backgroundColor: '#ffffff',
                                  border: '1px solid #e2e8f0',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                                }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "content" && (
          <div className="content-tab">
            <div className="content-layout">
              {/* Column 1: Container 1 - Engagement Trends (spans rows 1-2) */}
              <div className="content-chart-tall" data-export-chart="content-engagement">
                <div className="chart-header">
                  <h3>Weekly Content Engagement</h3>
                  <p>Views, Bookmarks, Shares</p>
                </div>
                {loadingContentEngagementTrend ? (
                  <div className="skeleton-chart">
                    <div className="skeleton skeleton-header"></div>
                    <div className="skeleton skeleton-subheader"></div>
                    <div className="skeleton skeleton-content"></div>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={contentEngagementData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis 
                        dataKey="week" 
                        stroke="#64748b"
                        tick={{ fontSize: 12 }}
                        interval={0}
                      />
                      <YAxis stroke="#64748b" allowDecimals={false} domain={[0, 'auto']} />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                        }}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="views" stroke="#3b82f6" strokeWidth={2} name="Views" dot={{ fill: '#3b82f6' }} />
                      <Line type="monotone" dataKey="bookmarks" stroke="#10b981" strokeWidth={2} name="Bookmarks" dot={{ fill: '#10b981' }} />
                      <Line type="monotone" dataKey="shares" stroke="#f59e0b" strokeWidth={2} name="Shares" dot={{ fill: '#f59e0b' }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Row 1 Column 2: Card 1 - Live Content */}
              <div className="content-stats-row-1">
                <div className="stat-card gradient-1">
                  <div className="stat-content">
                    {loadingContentStats ? (
                      <div className="skeleton-card">
                        <div className="skeleton skeleton-header"></div>
                        <div className="skeleton skeleton-subheader"></div>
                      </div>
                    ) : (
                      <div className="stat-content-header">
                        <h3 className="stat-count" style={{ color: '#ffffff' }}>{realStats.contentStats.liveContent.toLocaleString()}</h3>
                        <p className="stat-label">Live<br />Content</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Row 1 Column 3: Card 2 - Live Announcements */}
              <div className="content-stats-row-2">
                <div className="stat-card gradient-2">
                  <div className="stat-content">
                    {loadingContentStats ? (
                      <div className="skeleton-card">
                        <div className="skeleton skeleton-header"></div>
                        <div className="skeleton skeleton-subheader"></div>
                      </div>
                    ) : (
                      <div className="stat-content-header">
                        <h3 className="stat-count" style={{ color: '#ffffff' }}>{realStats.contentStats.liveAnnouncements.toLocaleString()}</h3>
                        <p className="stat-label">Live<br />Announcements</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Row 2 Column 2: Card 3 - Pending Review */}
              <div className="content-stats-row-3">
                <div className="stat-card gradient-3">
                  <div className="stat-content">
                    {loadingContentStats ? (
                      <div className="skeleton-card">
                        <div className="skeleton skeleton-header"></div>
                        <div className="skeleton skeleton-subheader"></div>
                      </div>
                    ) : (
                      <div className="stat-content-header">
                        <h3 className="stat-count" style={{ color: '#ffffff' }}>{realStats.contentStats.pendingReview.toLocaleString()}</h3>
                        <p className="stat-label">Pending<br />Review</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Row 2 Column 3: Card 4 - Archived Content */}
              <div className="content-stats-row-4">
                <div className="stat-card gradient-4">
                  <div className="stat-content">
                    {loadingContentStats ? (
                      <div className="skeleton-card">
                        <div className="skeleton skeleton-header"></div>
                        <div className="skeleton skeleton-subheader"></div>
                      </div>
                    ) : (
                      <div className="stat-content-header">
                        <h3 className="stat-count" style={{ color: '#ffffff' }}>{realStats.contentStats.archivedContent.toLocaleString()}</h3>
                        <p className="stat-label">Archived<br />Content</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Row 3: Container 3 and Container 4 */}
              <div className="content-charts-row-3">
                <div className="chart-container">
                  <div className="chart-header">
                    <h3>Top Performing Content</h3>
                    <p>Based on views, bookmarks, shares</p>
                  </div>
                  <div className="leaderboard-list">
                    {loadingTopPerforming ? (
                      <>
                        {[1, 2, 3].map((index) => (
                          <div key={index} className="leaderboard-item">
                            <div className="leaderboard-image skeleton skeleton-pie"></div>
                            <div className="leaderboard-content">
                              <div className="skeleton skeleton-header"></div>
                              <div className="skeleton skeleton-subheader"></div>
                            </div>
                          </div>
                        ))}
                      </>
                    ) : (
                      <>
                        {(realStats.topPerformingContent?.length || 0) > 0 ? (
                          realStats.topPerformingContent.map((item, index) => (
                            <div 
                              key={item.id} 
                              className="leaderboard-item"
                              style={{ cursor: 'pointer' }}
                              onClick={() => navigate(`/admin/content-review/${item.id}`)}
                            >
                              <div className="leaderboard-image">
                                {item.socialImageUrl ? (
                                  <img
                                    src={item.socialImageUrl}
                                    alt={item.title}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      borderRadius: "4px"
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                      e.target.nextSibling.style.display = "flex";
                                    }}
                                  />
                                ) : null}
                                {!item.socialImageUrl && (
                                  <span>{index + 1}</span>
                                )}
                              </div>
                              <div className="leaderboard-content">
                                <h4>{item.title || "N/A"}</h4>
                                <div className="leaderboard-stats">
                                  <span>Views: {item.views.toLocaleString()}</span>
                                  <span>Bookmarks: {item.bookmarks}</span>
                                  <span>Shares: {item.shares}</span>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>
                            No content available
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div className="chart-container chart-container-full" data-export-chart="top-engaged-topics">
                  <div className="chart-header">
                    <h3>Most Engaged Topics</h3>
                    <p>Top categories by engagement</p>
                  </div>
                  {loadingTopEngagedTopics ? (
                    <div className="skeleton-chart">
                      <div className="skeleton skeleton-header"></div>
                      <div className="skeleton skeleton-subheader"></div>
                      <div className="skeleton-bars-horizontal">
                        {[1, 2, 3, 4, 5].map((index) => (
                          <div key={index} className="skeleton-bar-row">
                            <div className="skeleton skeleton-bar-label"></div>
                            <div 
                              className="skeleton skeleton-bar-fill" 
                              style={{ width: `${90 - (index * 15)}%` }}
                            ></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={realStats.topEngagedTopics && realStats.topEngagedTopics.length > 0 ? realStats.topEngagedTopics : []} layout="vertical" margin={{ top: 20, right: 20, left: 10, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis type="number" stroke="#64748b" />
                        <YAxis dataKey="topic" type="category" stroke="#64748b" tick={{ fontSize: 12 }} width={90} />
                        <Tooltip 
                          contentStyle={{
                            backgroundColor: '#ffffff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                          }}
                        />
                        <Bar dataKey="engagement" fill="url(#barGradient)" name="Engagement %" />
                        <defs>
                          <linearGradient id="barGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#3b82f6" />
                            <stop offset="100%" stopColor="#1d4ed8" />
                          </linearGradient>
                        </defs>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              {/* Row 4: Container 5 and Container 6 */}
              <div className="content-charts-row-4">
                <div className="chart-container">
                  <div className="chart-header">
                    <h3>Content Awaiting Approval</h3>
                    <p>Pending submissions</p>
                  </div>
                  <div className="approval-table">
                    {loadingPendingApprovals ? (
                      <div style={{ padding: '20px' }}>
                        {[1, 2, 3].map((index) => (
                          <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                            <div className="skeleton" style={{ width: '30%', height: '40px' }}></div>
                            <div className="skeleton" style={{ width: '20%', height: '40px' }}></div>
                            <div className="skeleton" style={{ width: '20%', height: '40px' }}></div>
                            <div className="skeleton" style={{ width: '15%', height: '40px' }}></div>
                            <div className="skeleton" style={{ width: '15%', height: '40px' }}></div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <>
                        <table>
                          <thead>
                            <tr>
                              <th>Content</th>
                              <th>Creator</th>
                              <th>Category</th>
                              <th>Date Submitted</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(realStats.pendingApprovals?.length || 0) > 0 ? (
                              realStats.pendingApprovals.map((item) => (
                                <tr key={item.id}>
                                  <td>{item.title || "N/A"}</td>
                                  <td>{item.author || "N/A"}</td>
                                  <td>{item.category || "N/A"}</td>
                                  <td>{item.submittedAt ? new Date(item.submittedAt.toDate()).toLocaleDateString() : "N/A"}</td>
                                  <td>
                                    <div className="table-actions">
                                      <button
                                        className="action-btn approve"
                                        data-tooltip="Approve"
                                        onClick={() => {
                                          setCurrentApproval(item);
                                          setShowApproveModal(true);
                                        }}
                                      >
                                        <FiCheck />
                                      </button>
                                      <button
                                        className="action-btn reject"
                                        data-tooltip="Reject"
                                        onClick={() => handleReject(item)}
                                      >
                                        <FiX />
                                      </button>
                                      <button
                                        className="action-btn view"
                                        data-tooltip="View"
                                        onClick={() => navigate(`/admin/content-review/${item.id}`)}
                                      >
                                        <FiEye />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="5" style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>
                                  No pending approvals
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                        <button className="see-all-btn">See All</button>
                      </>
                    )}
                  </div>
                </div>

                <div className="chart-container chart-container-full" data-export-chart="content-publishing">
                  <div className="chart-header">
                    <h3>Monthly Content Publishing Activity</h3>
                    <p>Content published over time</p>
                  </div>
                  {loadingContentPublishingTrend ? (
                    <div className="skeleton-chart">
                      <div className="skeleton skeleton-header"></div>
                      <div className="skeleton skeleton-subheader"></div>
                      <div className="skeleton skeleton-content"></div>
                      <div className="skeleton skeleton-area"></div>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={contentPublishingData} margin={{ top: 20, right: 10, left: -5, bottom: 50 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis 
                          dataKey="month" 
                          stroke="#64748b"
                          tick={{ fontSize: 12 }}
                          interval={0}
                        />
                        <YAxis stroke="#64748b" allowDecimals={false} />
                        <Tooltip 
                          contentStyle={{
                            backgroundColor: '#ffffff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                          }}
                        />
                        <Area type="monotone" dataKey="published" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} name="Published" />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "chats" && (
          <div className="chats-tab">
            <div className="chats-layout">
              {/* Row 1: Container 1, 2 */}
              <div className="chats-row-1">
                <div className="chart-container">
                  <div className="chart-header">
                    <h3>Top Peer Educators</h3>
                    <p>{currentWeekRange || 'Weekly performance'}</p>
                  </div>
                  <div className="peer-educator-table">
                    {loadingPeerEducators ? (
                      <div style={{ padding: '20px' }}>
                        {[1, 2, 3, 4, 5].map((index) => (
                          <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                            <div className="skeleton" style={{ width: '70%', height: '40px' }}></div>
                            <div className="skeleton" style={{ width: '30%', height: '40px' }}></div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <table>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Resolved Chats</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topPeerEducatorsData.length > 0 ? (
                            topPeerEducatorsData.map((educator, index) => (
                              <tr key={index}>
                                <td>{educator.name}</td>
                                <td>{educator.resolvedChats}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="2" style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>
                                No peer educator data available
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>

                <div className="chart-container" data-export-chart="chat-activity">
                  <div className="chart-header">
                    <h3>Weekly Chat Activity</h3>
                  </div>
                  {loadingChatActivityTrend ? (
                    <div className="skeleton-chart">
                      <div className="skeleton skeleton-header"></div>
                      <div className="skeleton skeleton-subheader"></div>
                      <div className="skeleton skeleton-content"></div>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={chatActivityData} margin={{ top: 20, right: 10, left: 0, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis 
                          dataKey="week" 
                          stroke="#64748b"
                          tick={{ fontSize: 12 }}
                          interval={0}
                        />
                        <YAxis stroke="#64748b" allowDecimals={false} />
                        <Tooltip 
                          contentStyle={{
                            backgroundColor: '#ffffff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                          }}
                        />
                        <Legend />
                        
                        <Line type="monotone" dataKey="assigned" stroke="#3b82f6" strokeWidth={2} name="Assigned" dot={{ fill: '#3b82f6' }} />
                        <Line type="monotone" dataKey="resolved" stroke="#10b981" strokeWidth={2} name="Resolved" dot={{ fill: '#10b981' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>


              </div>

              {/* Row 2: Container 4 and 5 */}
              <div className="chats-row-2">
                <div className="chart-container chart-container-full" data-export-chart="escalation-status">
                  <div className="chart-header">
                    <h3>Escalated Case Status</h3>
                    <p>Case breakdown</p>
                  </div>
                  {loadingEscalationStatus ? (
                    <div className="skeleton-chart">
                      <div className="skeleton skeleton-header"></div>
                      <div className="skeleton skeleton-subheader"></div>
                      <div className="skeleton-bars-vertical">
                        {[1, 2, 3].map((index) => (
                          <div 
                            key={index} 
                            className="skeleton skeleton-bar-vertical" 
                            style={{ height: `${200 - (index * 50)}px`, width: '150px' }}
                          ></div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={realStats.escalationStatus} margin={{ top: 20, right: 20, left: 10, bottom: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis 
                          dataKey="category" 
                          stroke="#64748b"
                          tick={{ fontSize: 12 }}
                          interval={0}
                        />
                        <YAxis stroke="#64748b" allowDecimals={false} />
                        <Tooltip 
                          contentStyle={{
                            backgroundColor: '#ffffff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                          }}
                          formatter={(value, name) => {
                            if (value > 0) {
                              return [value, name];
                            }
                            return null;
                          }}
                        />
                        <Legend />
                        <Bar dataKey="pending" stackId="a" fill="#f59e0b" name="Pending Review" />
                        <Bar dataKey="underAssessment" stackId="a" fill="#3b82f6" name="Under Assessment" />
                        <Bar dataKey="resolved" stackId="a" fill="#10b981" name="Resolved" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>

                <div className="chart-container pie-chart-layout" data-export-chart="chat-categories">
                  <div className="chart-header-full">
                    <h3>Today's Chat Categories</h3>
                    <p>Chat distribution</p>
                  </div>
                  <div className="pie-chart-content">
                    {loadingChatCategories ? (
                      <>
                        <div className="pie-chart-legend">
                          <h4>Categories</h4>
                          {[1, 2, 3, 4, 5].map((index) => (
                            <div key={index} className="legend-item">
                              <div className="legend-color skeleton"></div>
                              <span className="legend-label skeleton skeleton-legend-label"></span>
                            </div>
                          ))}
                        </div>
                        <div className="pie-chart-wrapper">
                          <div className="skeleton skeleton-pie"></div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="pie-chart-legend">
                          <h4>Categories</h4>
                          {realStats.chatCategories.map((item, index) => (
                            <div key={index} className="legend-item">
                              <div className="legend-color" style={{ backgroundColor: item.color }}></div>
                              <span className="legend-label">{item.name}</span>
                            </div>
                          ))}
                        </div>
                        <div className="pie-chart-wrapper">
                          {realStats.hasChatDataToday ? (
                            <ResponsiveContainer width="100%" height={300}>
                              <PieChart>
                                <Pie
                                  data={realStats.chatCategories}
                                  cx="50%"
                                  cy="50%"
                                  innerRadius={70}
                                  outerRadius={120}
                                  paddingAngle={5}
                                  dataKey="value"
                                  label={false}
                                >
                                  {realStats.chatCategories.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                  ))}
                                </Pie>
                                <Tooltip 
                                  contentStyle={{
                                    backgroundColor: '#ffffff',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                                  }}
                                />
                              </PieChart>
                            </ResponsiveContainer>
                          ) : (
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              height: '300px',
                              color: '#64748b',
                              fontSize: '16px',
                              fontWeight: '500'
                            }}>
                              No chat data for today yet
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "appointments" && (
          <div className="appointments-tab">
            <div className="appointments-layout">
              {/* Row 1: Container 1, 2, 3 */}
              <div className="appointments-row-1">
                <div className="chart-container">
                  <div className="chart-header">
                    <h3>Top Medical Professionals</h3>
                    <p>Monthly performance</p>
                  </div>
                  <div className="medical-professional-table">
                    {loadingAppointmentData ? (
                      <div style={{ padding: '20px' }}>
                        {[1, 2, 3, 4, 5].map((index) => (
                          <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                            <div className="skeleton" style={{ width: '40%', height: '40px' }}></div>
                            <div className="skeleton" style={{ width: '20%', height: '40px' }}></div>
                            <div className="skeleton" style={{ width: '40%', height: '40px' }}></div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <table>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Completed</th>
                            <th>Service</th>
                          </tr>
                        </thead>
                        <tbody>
                          {realStats.topMedicalProfessionals.map((professional, index) => (
                            <tr key={index}>
                              <td>{professional.name}</td>
                              <td>{professional.completedAppointments}</td>
                              <td>{professional.assignedServices}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>

                <div className="chart-container chart-container-full" data-export-chart="appointment-status">
                  <div className="chart-header">
                    <h3>Appointment Status Overview</h3>
                    <p>By service type</p>
                  </div>
                  {loadingAppointmentData ? (
                    <div className="skeleton-chart">
                      <div className="skeleton skeleton-header"></div>
                      <div className="skeleton skeleton-subheader"></div>
                      <div className="skeleton-bars-vertical">
                        {[1, 2, 3].map((index) => (
                          <div 
                            key={index} 
                            className="skeleton skeleton-bar-vertical" 
                            style={{ height: `${200 - (index * 50)}px` , width: '90px'}}
                          ></div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={realStats.appointmentStatusOverview} margin={{ top: 20, right: 5, left: -5, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis 
                          dataKey="service" 
                          stroke="#64748b"
                          tick={{ fontSize: 12 }}
                          interval={0}
                          height={60}
                          tickFormatter={(value) => {
                            // Handle common service name variations
                            if (value.includes('Reproductive')) {
                              return ['Reproductive Health'];
                            }
                            if (value.includes('STI') || value.includes('HIV')) {
                              return ['STI/HIV Testing'];
                            }
                            if (value.includes('Family Planning')) {
                              return ['Family Planning'];
                            }
                            // Fallback: split long names
                            if (value.length > 15) {
                              const words = value.split(' ');
                              if (words.length > 2) {
                                return [words.slice(0, 2).join(' '), words.slice(2).join(' ')];
                              }
                            }
                            return [value];
                          }}
                        />
                        <YAxis stroke="#64748b" allowDecimals={false} />
                        <Tooltip 
                          contentStyle={{
                            backgroundColor: '#ffffff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                          }}
                        />
                        <Legend />
                        <Bar dataKey="pending" stackId="a" fill="#f59e0b" name="Pending Approval" />
                        <Bar dataKey="confirmed" stackId="a" fill="#3b82f6" name="Confirmed" />
                        <Bar dataKey="accepted" stackId="a" fill="#10b981" name="Accepted" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>

                <div className="chart-container chart-container-full" data-export-chart="appointment-trends">
                  <div className="chart-header">
                    <h3>Weekly Appointment Trends</h3>
                    <p>Completed, Cancelled</p>
                  </div>
                  {loadingAppointmentData ? (
                    <div className="skeleton-chart">
                      <div className="skeleton skeleton-header"></div>
                      <div className="skeleton skeleton-subheader"></div>
                      <div className="skeleton skeleton-content"></div>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={realStats.weeklyAppointmentTrends} margin={{ top: 20, right: 20, left: -5, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis 
                          dataKey="week" 
                          stroke="#64748b"
                          tick={{ fontSize: 12 }}
                          interval={0}
                        />
                        <YAxis stroke="#64748b" allowDecimals={false} />
                        <Tooltip 
                          contentStyle={{
                            backgroundColor: '#ffffff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                          }}
                        />
                        <Legend />
                        <Line type="monotone" dataKey="completed" stroke="#10b981" strokeWidth={2} name="Completed" dot={{ fill: '#10b981' }} />
                        <Line type="monotone" dataKey="cancelledByUser" stroke="#ef4444" strokeWidth={2} name="Cancelled by User" dot={{ fill: '#ef4444' }} />
                        <Line type="monotone" dataKey="cancelledByMedical" stroke="#f59e0b" strokeWidth={2} name="Cancelled by Medical" dot={{ fill: '#f59e0b' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              {/* Row 2: Container 4 - Full Width */}
              <div className="appointments-row-2">
                <div className="chart-container appointments-overview">
                  <div className="chart-header">
                    <h3>Today's Appointment Overview</h3>
                    <p>Daily summary</p>
                  </div>
                  <div className="today-appointments-stats">
                    {loadingAppointmentData ? (
                      <>
                        {[1, 2, 3, 4].map((index) => (
                          <div key={index} className="today-stat-item">
                            <div className="skeleton skeleton-header"></div>
                            <div className="skeleton skeleton-subheader"></div>
                          </div>
                        ))}
                      </>
                    ) : (
                      <>
                        <div className="today-stat-item">
                          <h4>{realStats.todayAppointments.total}</h4>
                          <p>Appointments Today</p>
                        </div>
                        <div className="today-stat-item">
                          <h4>{realStats.todayAppointments.pending}</h4>
                          <p>Pending</p>
                        </div>
                        <div className="today-stat-item">
                          <h4>{realStats.todayAppointments.confirmed}</h4>
                          <p>Confirmed</p>
                        </div>
                        <div className="today-stat-item">
                          <h4>{realStats.todayAppointments.completed}</h4>
                          <p>Completed</p>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="today-schedule">
                    <h4>Today's Schedule</h4>
                    {loadingAppointmentData ? (
                      <div style={{ padding: '20px' }}>
                        {[1, 2, 3, 4, 5].map((index) => (
                          <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                            <div className="skeleton" style={{ width: '30%', height: '40px' }}></div>
                            <div className="skeleton" style={{ width: '25%', height: '40px' }}></div>
                            <div className="skeleton" style={{ width: '15%', height: '40px' }}></div>
                            <div className="skeleton" style={{ width: '30%', height: '40px' }}></div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <>
                        <table>
                          <thead>
                            <tr>
                              <th>Patient</th>
                              <th>Service</th>
                              <th>Time</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedTodaySchedule.length > 0 ? (
                              paginatedTodaySchedule.map((appointment, index) => (
                                <tr key={index}>
                                  <td>{appointment.patient}</td>
                                  <td>{appointment.service}</td>
                                  <td>{appointment.time}</td>
                                  <td>
                                    <span className={`status-badge ${appointment.status.toLowerCase()}`}>
                                      {appointment.status}
                                    </span>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="4" style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                                  No appointments scheduled for today
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                        <div className="pagination-container">
                          <div className="pagination-info">
                            <span>
                              Showing {Math.min(todayScheduleStartIndex + 1, realStats.todaySchedule?.length || 0)}�{Math.min(todayScheduleEndIndex, realStats.todaySchedule?.length || 0)} of {realStats.todaySchedule?.length || 0} appointments
                            </span>
                          </div>
                      <div className="pagination-controls">
                        <button
                          className="pagination-btn pagination-btn--prev"
                          onClick={() => handleTodaySchedulePageChange(todaySchedulePage - 1)}
                          disabled={todaySchedulePage === 1}
                        >
                          <FiChevronLeft />
                          Previous
                        </button>

                        <div className="pagination-numbers">
                          {Array.from({ length: Math.max(todayScheduleTotalPages, 1) }, (_, i) => i + 1).map((pageNumber) => (
                            <button
                              key={pageNumber}
                              className={`pagination-number ${todaySchedulePage === pageNumber ? 'pagination-number--active' : ''}`}
                              onClick={() => handleTodaySchedulePageChange(pageNumber)}
                            >
                              {pageNumber}
                            </button>
                          ))}
                        </div>

                        <button
                          className="pagination-btn pagination-btn--next"
                          onClick={() => handleTodaySchedulePageChange(todaySchedulePage + 1)}
                          disabled={todaySchedulePage === Math.max(todayScheduleTotalPages, 1)}
                        >
                          Next
                          <FiChevronRight />
                        </button>
                      </div>
                    </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Row 3: Container 5 and 6 */}
              <div className="appointments-row-3">
                <div className="chart-container chart-container-full" data-export-chart="service-demand">
                  <div className="chart-header">
                    <h3>Service Demand Distribution</h3>
                    <p>Demand by service type</p>
                  </div>
                  {loadingAppointmentData ? (
                    <div className="skeleton-chart">
                      <div className="skeleton skeleton-header"></div>
                      <div className="skeleton skeleton-subheader"></div>
                      <div className="skeleton-bars-horizontal">
                        {[1, 2, 3].map((index) => (
                          <div key={index} className="skeleton-bar-row">
                            <div className="skeleton skeleton-bar-label"></div>
                            <div 
                              className="skeleton skeleton-bar-fill" 
                              style={{ width: `${90 - (index * 15)}%` }}
                            ></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={realStats.serviceDemand} layout="vertical" margin={{ top: 20, right: 10, left: 5, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis type="number" stroke="#64748b" />
                        <YAxis dataKey="service" type="category" stroke="#64748b" tick={{ fontSize: 12 }} width={90} />
                        <Tooltip 
                          contentStyle={{
                            backgroundColor: '#ffffff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                          }}
                        />
                        <Bar dataKey="demand" fill="#3b82f6" name="Demand" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>

                <div className="chart-container chart-container-full" data-export-chart="peak-hours">
                  <div className="chart-header">
                    <h3>Appointment Demand by Time</h3>
                    <p>Peak hours heatmap</p>
                  </div>
                  <div className="heatmap-container">
                    {loadingAppointmentData ? (
                      <div className="heatmap-grid">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30].map((index) => (
                          <div key={index} className="heatmap-cell skeleton skeleton-heatmap"></div>
                        ))}
                      </div>
                    ) : (
                      <div className="heatmap-grid">
                        {realStats.peakHours.map((item, index) => {
                          const intensity = item.value / 15;
                          return (
                            <div 
                              key={index} 
                              className="heatmap-cell"
                              style={{
                                backgroundColor: `rgba(59, 130, 246, ${intensity})`,
                                color: intensity > 0.5 ? 'white' : '#1e293b'
                              }}
                            >
                              <span className="heatmap-label">{item.day}</span>
                              <span className="heatmap-value">{item.hour}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {showCreateStaff && (
          <CreateStaff 
            onCancel={() => {
              setShowCreateStaff(false);
              fetchDashboardData(); // Refresh data after creating staff
            }} 
            onSuccess={() => {
              setShowCreateStaff(false);
              fetchDashboardData(); // Refresh data after creating staff
              window.dispatchEvent(new CustomEvent("admin-switch-tab", { detail: { tab: "accounts" } }));
            }} 
          />
        )}

        {/* Approve Modal */}
        <ApproveModal
          isOpen={showApproveModal}
          onClose={() => {
            setShowApproveModal(false);
            setCurrentApproval(null);
          }}
          onConfirm={() => handleApprove(currentApproval?.id)}
          approval={currentApproval}
        />

        {/* Rejection Modal */}
        <RejectionModal
          isOpen={showRejectModal}
          onClose={() => {
            setShowRejectModal(false);
            setCurrentApproval(null);
          }}
          onConfirm={handleRejectConfirm}
          approval={currentApproval}
        />

        {/* Export Modal */}
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExport={handleExport}
          isExporting={isExporting}
          activeTab={activeTab}
        />
      </div>
    </div>
  );
}

// Modal Components
function ConfirmPasswordModal({ isOpen, onClose, onConfirm, title }) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.trim()) {
      setIsLoading(true);
      setError("");

      try {
        await onConfirm(password);
        // Only clear password and loading if successful
        setPassword("");
        setIsLoading(false);
      } catch (err) {
        // Don't clear password on error, keep it for user to retry
        setError("Incorrect password");
        setIsLoading(false);
      }
    }
  };

  const handleClose = () => {
    setPassword("");
    setError("");
    setShowPassword(false);
    setIsLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <h3 className="modal-title-white">{title}</h3>
            <button className="modal-close" onClick={handleClose}>
              <FiX />
            </button>
          </div>
        
        <p className="modal-subtitle">Enter admin password to create account</p>
        
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="password-field">
            <FiLock className="password-icon" />
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (error) setError("");
              }}
              placeholder="Enter admin password"
              className={`password-input ${error ? "error" : ""}`}
              autoFocus
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FiEye /> : <FiEyeOff />}
            </button>
          </div>
          
          {error && (
            <div className="password-error">
              <span className="error-icon">?</span>
              {error}
            </div>
          )}
          
          <div className="modal-actions">
            <button 
              type="button" 
              className="modal-btn modal-btn-cancel" 
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`modal-btn modal-btn-confirm ${isLoading ? "loading" : ""}`}
              disabled={isLoading}
            >
              {isLoading ? "Confirming..." : "Confirm"}
            </button>
          </div>
        </form>
      </div>
    </div>
    </Portal>
  );
}

function NotificationModal({ message, type, onClose }) {
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message, onClose]);

  if (!message) return null;

  return (
    <Portal>
      <div
        className={`feedback-modal ${type}`}
      style={{
        position: "fixed",
        top: "20px",
        right: "20px",
        padding: "12px 20px",
        borderRadius: "8px",
        color: "#fff",
        backgroundColor: type === "success" ? "#36C667" : "#e74c3c",
        boxShadow: "0 4px 6px rgba(0,0,0,0.2)",
        zIndex: 9999,
        transition: "opacity 0.3s",
      }}
    >
      {message}
    </div>
    </Portal>
  );
}

function CreateStaff({ onCancel, onSuccess }) {
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    contact: "",
    licenseNumber: "",
  });

  const [errors, setErrors] = useState({});
  const [creating, setCreating] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  
  const STEPS = [
    "Basic Information",
    "Staff Details",
    "Review & Confirm"
  ];
  
  // Modal states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const API_BASE =
    process.env.NODE_ENV === "development"
      ? "http://localhost:5030/auravit-bb843/us-central1"
      : "https://us-central1-auravit-bb843.cloudfunctions.net";

  // eslint-disable-next-line no-unused-vars
  const getSecondaryAuth = () => {
    const base = getApp();
    const secondaryName = "secondary-auth-staff-create";
    let secondaryApp;
    try {
      secondaryApp = getApp(secondaryName);
    } catch {
      secondaryApp = initializeApp(base.options, secondaryName);
    }
    return getAuth(secondaryApp);
  };

  // eslint-disable-next-line no-unused-vars
  const nextCounterId = async (counterDocId, prefix) => {
    const ref = doc(db, "counters", counterDocId);
    try {
      const n = await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        const current = snap.exists() ? Number(snap.data()?.count || 0) : 0;
        const next = current + 1;
        tx.set(ref, { count: next }, { merge: true });
        return next;
      });
      return `${prefix}${String(n).padStart(4, "0")}`;
    } catch (err) {
      console.error("Counter increment failed:", err);
      // Fallback to timestamp-based unique ID if transaction fails
      return `${prefix}${Date.now().toString().slice(-4)}`;
    }
  };

  const emailExists = async (email) => {
    const snap = await getDocs(query(collection(db, "users"), where("email", "==", email), limit(1)));
    return !snap.empty;
  };

  const contactExists = async (last10) => {
    const snap = await getDocs(query(collection(db, "users"), where("phoneLast10", "==", last10), limit(1)));
    return !snap.empty;
  };

  const licenseExists = async (licenseNumber) => {
    const snap = await getDocs(
      query(collection(db, "users"), where("medicalLicenseNumber", "==", licenseNumber), limit(1))
    );
    return !snap.empty;
  };

  const toggleRole = (role) => {
    setRoles((prev) => {
      const isAdding = !prev.includes(role);
      const next = isAdding ? [...prev, role] : prev.filter((r) => r !== role);
      if (role === "Medical Professional" && isAdding) {
        setForm((p) => ({ ...p, licenseNumber: "" }));
      }
      
      // Clear role error when at least one role is selected
      if (next.length > 0) {
        setErrors((prev) => ({
          ...prev,
          roles: ""
        }));
      }
      
      return next;
    });
  };

  const isMedicalProfessional = roles.includes("Medical Professional");

  // Regex validations
  const validateFullName = (name) => /^[A-Za-z\s\-']{1,50}$/.test(name);
  const validateFullNameHasTwoParts = (name) => {
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2;
  };
  const validateEmail = (email) => {
    // Simplified email validation that allows common email formats
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };
  const validateContact = (contact) => /^9\d{9}$/.test(contact);
  const validateLicense = (license) =>
    /^\d{7}$/.test(license);

  // Handle input changes
  const handleChange = (field, value) => {
    // Restrict full name input - allow letters, spaces, hyphens, and apostrophes
    if (field === "fullName") {
      value = value.replace(/[^A-Za-z\s\-']/g, "").slice(0, 50);
    }

    // Restrict email input - disable spaces only, allow @ symbol and max 254 characters
    if (field === "email") {
      value = value.replace(/\s/g, "").slice(0, 254);
    }

    // Restrict contact input
    if (field === "contact") {
      value = value.replace(/[^0-9]/g, "").slice(0, 10);
    }

    // Restrict license input
    if (field === "licenseNumber") {
      value = value.replace(/[^0-9]/g, "").slice(0, 7);
    }

    setForm((prev) => ({ ...prev, [field]: value }));

    // Live validation
    setErrors((prev) => {
      const newErrors = { ...prev };

      // Clear uniqueness errors when user changes the field
      if (field === "email" && newErrors.email === "Email already taken") {
        newErrors.email = "";
      }
      if (field === "contact" && newErrors.contact === "Contact number is already taken") {
        newErrors.contact = "";
      }
      if (field === "licenseNumber" && newErrors.licenseNumber === "License number is already taken") {
        newErrors.licenseNumber = "";
      }

      // Perform normal validation for the field
      newErrors[field] =
        (field === "fullName" && !value)
          ? "Full Name is required"
          : (field === "fullName" && !validateFullName(value))
            ? "Only letters, spaces, hyphens, and apostrophes allowed (max 50 characters)"
            : (field === "email" && !value)
              ? "Email is required"
              : (field === "email" && value && !validateEmail(value))
                ? "Invalid email format"
              : (field === "contact" && !value)
                ? "Contact Number is required"
                : (field === "contact" && !validateContact(value))
                  ? "Must start with 9 and be 10 digits"
                  : (field === "licenseNumber" && isMedicalProfessional && !value)
                    ? "License Number required"
                    : (field === "licenseNumber" && isMedicalProfessional && !validateLicense(value))
                      ? "7 digits only"
                      : "";

      return newErrors;
    });

    // Live validation for role selection
    if (roles.length === 0) {
      setErrors((prev) => ({
        ...prev,
        roles: "At least one role must be selected"
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        roles: ""
      }));
    }
  };

  // Check if form is valid
  // eslint-disable-next-line no-unused-vars
  const isFormValid = () => {
    const requiredFields = ["fullName", "email", "contact"];
    if (isMedicalProfessional) requiredFields.push("licenseNumber");

    const fieldsValid = requiredFields.every((field) => !errors[field] && form[field]);
    const rolesValid = roles.length > 0 && !errors.roles;

    return fieldsValid && rolesValid;
  };

  const handleCreate = async () => {
    // Trigger validation for all required fields
    const newErrors = {};

    // Validate full name
    if (!form.fullName) {
      newErrors.fullName = "Full Name is required";
    } else if (!validateFullName(form.fullName)) {
      newErrors.fullName = "Only letters, spaces, hyphens, and apostrophes allowed (max 50 characters)";
    } else if (!validateFullNameHasTwoParts(form.fullName)) {
      newErrors.fullName = "Full Name must contain at least two parts (first name and last name)";
    }

    // Validate email
    if (!form.email) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(form.email)) {
      newErrors.email = "Invalid email format";
    }

    // Validate contact
    if (!form.contact) {
      newErrors.contact = "Contact Number is required";
    } else if (!validateContact(form.contact)) {
      newErrors.contact = "Must start with 9 and be 10 digits";
    }

    // Validate license if medical professional
    if (isMedicalProfessional) {
      if (!form.licenseNumber) {
        newErrors.licenseNumber = "License Number required";
      } else if (!validateLicense(form.licenseNumber)) {
        newErrors.licenseNumber = "Must be 7 digits";
      }
    }

    // Validate role selection
    if (roles.length === 0) {
      newErrors.roles = "Select at least one role";
    }

    // Set all errors at once
    setErrors(newErrors);

    // Only proceed if no errors
    if (Object.keys(newErrors).length === 0) {
      setShowPasswordModal(true);
    }
  };

  const handlePasswordConfirm = async (adminPassword) => {
    const adminEmail = auth.currentUser?.email || JSON.parse(localStorage.getItem("user") || "{}")?.email;
    if (!adminEmail || !adminPassword) return;
    setCreating(true);
    try {
      const user = auth.currentUser;
      if (!user?.email) throw new Error("Not logged in");

      const cred = EmailAuthProvider.credential(user.email, adminPassword);
      await reauthenticateWithCredential(user, cred);

      // Authentication successful, close modal and proceed with creation
      setShowPasswordModal(false);

      const generateTempPassword = () => {
        const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
        const lowercase = "abcdefghijklmnopqrstuvwxyz";
        const numbers = "0123456789";
        const special = "!@#$%^&*";

        // Ensure at least one character from each category
        let password = "";
        password += uppercase[Math.floor(Math.random() * uppercase.length)];
        password += lowercase[Math.floor(Math.random() * lowercase.length)];
        password += numbers[Math.floor(Math.random() * numbers.length)];
        password += special[Math.floor(Math.random() * special.length)];

        // Fill remaining 8 characters with random mix of all categories
        const allChars = uppercase + lowercase + numbers + special;
        for (let i = 0; i < 8; i++) {
          password += allChars[Math.floor(Math.random() * allChars.length)];
        }

        // Shuffle the password to randomize character positions
        return password.split('').sort(() => Math.random() - 0.5).join('');
      };

      const fullName = String(form.fullName || "").trim();
      const staffEmail = String(form.email || "").trim().toLowerCase();
      const phoneLast10 = String(form.contact || "").replace(/\D/g, "").slice(-10);
      const phone = phoneLast10 ? `+63${phoneLast10}` : "";
      const adminSystemId = JSON.parse(localStorage.getItem("user") || "{}")?.systemId || user?.uid || "Admin";
      const tempPassword = generateTempPassword();
      const staffId = await generateStaffId(); // Generate staff ID on frontend
      const idToken = await user.getIdToken(true);

      const response = await fetch(`${API_BASE}/createStaffAccount`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          email: staffEmail,
          phone,
          phoneLast10,
          fullName,
          roles,
          licenseNumber: roles.includes("Medical Professional") ? String(form.licenseNumber || "").trim() : "",
          adminSystemId,
          tempPassword,
          staffId, // Pass the generated staff ID to backend
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || "Staff creation failed");
      }

      const staffUid = payload?.uid;
      const systemId = payload?.systemId || staffUid; // Use uid as fallback for systemId
      if (!staffUid || !staffId) {
        throw new Error("Failed to save staff data. Please try again.");
      }

      // Explicitly save/ensure data is in Firestore from frontend as a safety measure
      try {
        await setDoc(
          doc(db, "users", staffUid),
          {
            systemId,
            staffId,
            uid: staffUid,
            accountType: "Staff",
            email: staffEmail,
            phone,
            phoneLast10,
            fullName,
            roles,
            role: roles[0] || "",
            medicalLicenseNumber: roles.includes("Medical Professional")
              ? String(form.licenseNumber || "").trim()
              : "",
            accountCreatorSystemId: adminSystemId,
            status: "active",
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      } catch {
        throw new Error("Failed to save staff data. Please try again.");
      }

      // Just refresh the accounts list.
      window.dispatchEvent(new Event("admin-accounts-refresh"));
      setForm({ fullName: "", email: "", contact: "", licenseNumber: "" });
      setRoles([]);
      setCreating(false);
      setShowSuccessModal(true);
    } catch (err) {
      // Authentication failed, keep modal open and set creating to false
      setCreating(false);
      throw err; // Re-throw to be caught by the modal's error handling
    }
  };

  const handleNextStep = async () => {
    if (currentStep === 0) {
      // Validate Basic Information
      const newErrors = {};
      if (!form.fullName) newErrors.fullName = "Full Name is required";
      else if (!validateFullName(form.fullName)) newErrors.fullName = "Only letters, spaces, hyphens, and apostrophes allowed (max 50 characters)";
      else if (!validateFullNameHasTwoParts(form.fullName)) newErrors.fullName = "Full Name must contain at least two parts (first name and last name)";

      if (!form.email) newErrors.email = "Email is required";
      else if (!validateEmail(form.email)) newErrors.email = "Invalid email format";

      if (!form.contact) newErrors.contact = "Contact Number is required";
      else if (!validateContact(form.contact)) newErrors.contact = "Must start with 9 and be 10 digits";

      setErrors(newErrors);

      // Only proceed with uniqueness checks if basic validation passes
      if (Object.keys(newErrors).length === 0) {
        try {
          const staffEmail = String(form.email || "").trim().toLowerCase();
          const phoneLast10 = String(form.contact || "").replace(/\D/g, "").slice(-10);

          const uniquenessErrors = {};

          // Check email existence
          const emailCheck = await fetch(`${API_BASE}/checkStaffEmailExists`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: staffEmail }),
          });
          const emailCheckData = await emailCheck.json().catch(() => ({}));
          if (!emailCheck.ok) {
            throw new Error(emailCheckData?.error || "Unable to validate staff email");
          }
          if (emailCheckData?.exists) uniquenessErrors.email = "Email already taken";
          if (await emailExists(staffEmail)) uniquenessErrors.email = "Email already taken";

          // Check contact existence (checks all accounts - staff and user)
          if (phoneLast10 && await contactExists(phoneLast10)) uniquenessErrors.contact = "Contact number is already taken";

          if (Object.keys(uniquenessErrors).length > 0) {
            setErrors((prev) => ({ ...prev, ...uniquenessErrors }));
            return; // Don't proceed to step 2 if uniqueness checks fail
          }

          setCurrentStep(1);
          // Clear uniqueness errors when moving to step 2
          setErrors((prev) => ({
            ...prev,
            email: "",
            contact: ""
          }));
        } catch (err) {
          // If uniqueness check fails due to connectivity, still allow proceeding
          console.error("Uniqueness check failed:", err);
          setCurrentStep(1);
          // Clear uniqueness errors when moving to step 2
          setErrors((prev) => ({
            ...prev,
            email: "",
            contact: ""
          }));
        }
      }
    } else if (currentStep === 1) {
      // Validate Staff Details
      const newErrors = {};
      if (roles.length === 0) newErrors.roles = "Select at least one role";
      if (isMedicalProfessional && !form.licenseNumber) newErrors.licenseNumber = "License Number required";
      else if (isMedicalProfessional && !validateLicense(form.licenseNumber)) newErrors.licenseNumber = "Must be 7 digits";

      setErrors(newErrors);

      // Only proceed with license uniqueness check if basic validation passes
      if (Object.keys(newErrors).length === 0) {
        try {
          const uniquenessErrors = {};

          // Check license number existence if medical professional
          if (isMedicalProfessional) {
            const lic = String(form.licenseNumber || "").trim();
            if (await licenseExists(lic)) uniquenessErrors.licenseNumber = "License number is already taken";
          }

          if (Object.keys(uniquenessErrors).length > 0) {
            setErrors((prev) => ({ ...prev, ...uniquenessErrors }));
            return; // Don't proceed to step 2 if uniqueness checks fail
          }

          setCurrentStep(2);
          // Clear uniqueness errors when moving to step 3
          setErrors((prev) => ({
            ...prev,
            email: "",
            contact: "",
            licenseNumber: ""
          }));
        } catch (err) {
          // If uniqueness check fails due to connectivity, still allow proceeding
          console.error("License uniqueness check failed:", err);
          setCurrentStep(2);
          // Clear uniqueness errors when moving to step 3
          setErrors((prev) => ({
            ...prev,
            email: "",
            contact: "",
            licenseNumber: ""
          }));
        }
      }
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  return (
    <React.Fragment>
      <Portal>
        <div className="modal-backdrop" onClick={onCancel}>
        <div 
          style={{ 
            maxWidth: '680px', 
            width: '94%', 
            maxHeight: '85vh', 
            borderRadius: '24px', 
            overflow: 'hidden', 
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.2)', 
            position: 'relative',
            background: '#ffffff'
          }}
          onClick={e => e.stopPropagation()}
        >
          <button 
            type="button" 
            onClick={onCancel} 
            style={{ 
              position: 'absolute',
              top: '14px',
              right: '14px',
              zIndex: 20,
              width: '38px', 
              height: '38px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.18)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'white',
              fontSize: '22px',
              lineHeight: 1,
              transition: 'all 0.2s ease',
              backdropFilter: 'blur(6px)'
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.32)'}
            onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.18)'}
          >
            <FiX />
          </button>

          <div style={{ 
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #1e40af 100%)',
            padding: '24px 24px 18px 24px',
            color: 'white',
            width: '100%',
            boxSizing: 'border-box'
          }}>
            <div style={{ marginBottom: '16px' }}>
              <h2 style={{ margin: 0, fontSize: '21px', fontWeight: 700, letterSpacing: '-0.3px' }}>Create Staff Account</h2>
              <p style={{ margin: '6px 0 0 0', fontSize: '14px', opacity: 0.92, fontWeight: 400 }}>Add a new staff member to the system</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ 
                background: 'rgba(255,255,255,0.2)', 
                padding: '6px 14px', 
                borderRadius: '9999px',
                fontSize: '12.5px',
                fontWeight: 600,
                letterSpacing: '0.02em'
              }}>
                Step {currentStep + 1} of {STEPS.length}
              </div>
              <div style={{ fontSize: '14px', fontWeight: 600, opacity: 0.98 }}>
                {STEPS[currentStep]}
              </div>
            </div>
          </div>

          <div style={{ padding: '16px 24px 0 24px' }}>
            <div style={{ display: 'flex', gap: '5px', height: '5px' }}>
              {STEPS.map((step, idx) => (
                <div 
                  key={idx} 
                  style={{
                    flex: 1,
                    height: '100%',
                    borderRadius: '9999px',
                    backgroundColor: idx <= currentStep ? '#3b82f6' : '#e2e8f0',
                    transition: 'background-color 0.3s ease'
                  }}
                />
              ))}
            </div>
          </div>

          <div style={{ 
            padding: '18px 24px 20px 24px', 
            overflowY: 'auto', 
            maxHeight: '52vh'
          }}>
            {/* Step 1: Basic Information */}
            {currentStep === 0 && (
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: '0 0 18px 0' }}>Basic Information</h3>
                
                <div style={{ marginBottom: '18px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#334155', marginBottom: '10px' }}>Full Name</label>
                  <input
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      borderRadius: '14px',
                      border: errors.fullName ? '2px solid #dc2626' : '2px solid #e2e8f0',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Enter full name"
                    value={form.fullName}
                    onChange={(e) => handleChange("fullName", e.target.value)}
                    onFocus={(e) => {
                      e.target.style.borderColor = errors.fullName ? '#dc2626' : '#3b82f6';
                      e.target.style.boxShadow = errors.fullName ? '0 0 0 4px rgba(220, 38, 38, 0.1)' : '0 0 0 4px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = errors.fullName ? '#dc2626' : '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  {errors.fullName && (
                    <p style={{ 
                      fontSize: '13px', 
                      color: '#dc2626', 
                      marginTop: '6px', 
                      fontWeight: '500',
                      marginBottom: '0'
                    }}>
                      {errors.fullName}
                    </p>
                  )}
                </div>

                <div style={{ marginBottom: '18px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#334155', marginBottom: '10px' }}>Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === ' ') {
                        e.preventDefault();
                      }
                    }}
                    placeholder="Enter email address"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      borderRadius: '14px',
                      border: errors.email ? '2px solid #dc2626' : '2px solid #e2e8f0',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = errors.email ? '#dc2626' : '#3b82f6';
                      e.target.style.boxShadow = errors.email ? '0 0 0 4px rgba(220, 38, 38, 0.1)' : '0 0 0 4px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = errors.email ? '#dc2626' : '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  {errors.email && (
                    <p style={{ 
                      fontSize: '13px', 
                      color: '#dc2626', 
                      marginTop: '6px', 
                      fontWeight: '500',
                      marginBottom: '0'
                    }}>
                      {errors.email}
                    </p>
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#334155', marginBottom: '10px' }}>Contact Number</label>
                  <div style={{ 
                    display: 'flex', 
                    gap: '8px',
                    width: '100%'
                  }}>
                    <span style={{ 
                      background: '#f1f5f9', 
                      padding: '14px 16px', 
                      borderRadius: '14px', 
                      border: '2px solid #e2e8f0',
                      fontWeight: '600',
                      color: '#475569'
                    }}>+63</span>
                    <input
                      type="text"
                      name="contact"
                      value={form.contact}
                      onChange={(e) => handleChange("contact", e.target.value)}
                      placeholder="9123456789"
                      style={{
                        flex: '1',
                        padding: '14px 16px',
                        borderRadius: '14px',
                        border: errors.contact ? '2px solid #dc2626' : '2px solid #e2e8f0',
                        fontSize: '14px',
                        outline: 'none',
                        transition: 'all 0.2s ease',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = errors.contact ? '#dc2626' : '#3b82f6';
                        e.target.style.boxShadow = errors.contact ? '0 0 0 4px rgba(220, 38, 38, 0.1)' : '0 0 0 4px rgba(59, 130, 246, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = errors.contact ? '#dc2626' : '#e2e8f0';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                  {errors.contact && (
                    <p style={{ 
                      fontSize: '13px', 
                      color: '#dc2626', 
                      marginTop: '6px', 
                      fontWeight: '500',
                      marginBottom: '0'
                    }}>
                      {errors.contact}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Staff Details */}
            {currentStep === 1 && (
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: '0 0 18px 0' }}>Staff Details</h3>
                
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    marginBottom: '10px' 
                  }}>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#334155' }}>
                      Role
                    </span>
                    <small style={{ fontSize: '12px', color: '#94a3b8' }}>Select one or more</small>
                  </div>

                  <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '10px',
                    marginBottom: '10px'
                  }}>
                    {["Content Curator", "Peer Educator", "Medical Professional"].map(
                      (role) => (
                        <label
                          key={role}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '12px 16px',
                            borderRadius: '14px',
                            border: roles.includes(role) ? '2px solid #3b82f6' : '2px solid #e2e8f0',
                            background: roles.includes(role) ? 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)' : '#ffffff',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: roles.includes(role) ? '#1e40af' : '#334155'
                          }}
                          onMouseEnter={(e) => {
                            if (!roles.includes(role)) {
                              e.currentTarget.style.background = '#f8fafc';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!roles.includes(role)) {
                              e.currentTarget.style.background = '#ffffff';
                            }
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={roles.includes(role)}
                            onChange={() => toggleRole(role)}
                            style={{ 
                              width: '18px', 
                              height: '18px',
                              accentColor: '#3b82f6',
                              margin: 0
                            }}
                          />
                          {role}
                        </label>
                      )
                    )}
                  </div>
                  {errors.roles && (
                    <p style={{ 
                      fontSize: '13px', 
                      color: '#dc2626', 
                      marginTop: '6px', 
                      fontWeight: '500',
                      marginBottom: '0'
                    }}>
                      {errors.roles}
                    </p>
                  )}
                </div>

                {isMedicalProfessional && (
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#334155', marginBottom: '10px' }}>License Number</label>
                    <input
                      placeholder="Enter 7-digit license number"
                      value={form.licenseNumber}
                      onChange={(e) => handleChange("licenseNumber", e.target.value)}
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        borderRadius: '14px',
                        border: errors.licenseNumber ? '2px solid #dc2626' : '2px solid #e2e8f0',
                        fontSize: '14px',
                        outline: 'none',
                        transition: 'all 0.2s ease',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = errors.licenseNumber ? '#dc2626' : '#3b82f6';
                        e.target.style.boxShadow = errors.licenseNumber ? '0 0 0 4px rgba(220, 38, 38, 0.1)' : '0 0 0 4px rgba(59, 130, 246, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = errors.licenseNumber ? '#dc2626' : '#e2e8f0';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                    {errors.licenseNumber && (
                      <p style={{ 
                        fontSize: '13px', 
                        color: '#dc2626', 
                        marginTop: '6px', 
                        fontWeight: '500',
                        marginBottom: '0'
                      }}>
                        {errors.licenseNumber}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Review & Confirm */}
            {currentStep === 2 && (
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: '0 0 18px 0' }}>Review & Confirm</h3>
                
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#475569', margin: '0 0 10px 0' }}>Basic Information</h4>
                  <div style={{ 
                    background: '#f8fafc', 
                    borderRadius: '12px', 
                    padding: '16px',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '12px'
                  }}>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', marginBottom: '4px' }}>Full Name</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{form.fullName}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', marginBottom: '4px' }}>Email</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{form.email}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', marginBottom: '4px' }}>Contact Number</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>+63 {form.contact}</div>
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#475569', margin: '0 0 10px 0' }}>Staff Details</h4>
                  <div style={{ 
                    background: '#f8fafc', 
                    borderRadius: '12px', 
                    padding: '16px'
                  }}>
                    <div style={{ marginBottom: isMedicalProfessional ? '12px' : '0' }}>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', marginBottom: '4px' }}>Roles</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{roles.join(', ')}</div>
                    </div>
                    {isMedicalProfessional && (
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', marginBottom: '4px' }}>License Number</div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{form.licenseNumber}</div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#475569', margin: '0 0 10px 0' }}>System</h4>
                  <div style={{ 
                    background: '#f8fafc', 
                    borderRadius: '12px', 
                    padding: '16px'
                  }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', marginBottom: '4px' }}>Account Creator</div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>Admin-001</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div style={{ padding: '16px 24px 24px 24px', display: 'flex', gap: '12px', justifyContent: currentStep === 0 ? 'flex-end' : 'space-between' }}>
            {currentStep > 0 && (
              <button 
                onClick={handlePrevStep}
                style={{
                  padding: '14px 32px',
                  borderRadius: '16px',
                  border: 'none',
                  background: '#f1f5f9',
                  color: '#475569',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#e2e8f0';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#f1f5f9';
                }}
              >
                Back
              </button>
            )}
            
            <div style={{ display: 'flex', gap: '12px', marginLeft: currentStep === 0 ? 'auto' : '0' }}>
              <button 
                onClick={onCancel}
                style={{
                  padding: '14px 32px',
                  borderRadius: '16px',
                  border: 'none',
                  background: '#f1f5f9',
                  color: '#475569',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#e2e8f0';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#f1f5f9';
                }}
              >
                Cancel
              </button>
              
              {currentStep < 2 ? (
                <button
                  onClick={handleNextStep}
                  style={{
                    padding: '14px 32px',
                    borderRadius: '16px',
                    border: 'none',
                    background: '#f1f5f9',
                    color: '#475569',
                    fontSize: '15px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)';
                    e.target.style.color = '#ffffff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#f1f5f9';
                    e.target.style.color = '#475569';
                  }}
                >
                  Next
                </button>
              ) : (
                <button
                  disabled={creating}
                  onClick={handleCreate}
                  style={{
                    padding: '14px 32px',
                    borderRadius: '16px',
                    border: 'none',
                    background: creating
                      ? '#94a3b8'
                      : '#f1f5f9',
                    color: creating ? '#ffffff' : '#475569',
                    fontSize: '15px',
                    fontWeight: '700',
                    cursor: creating ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!creating) {
                      e.target.style.background = 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)';
                      e.target.style.color = '#ffffff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!creating) {
                      e.target.style.background = '#f1f5f9';
                      e.target.style.color = '#475569';
                    }
                  }}
                >
                  {creating ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FiRefreshCw className="spinning" />
                      Creating...
                    </span>
                  ) : (
                    "Create Account"
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      </Portal>

      {/* Modals */}
      <ConfirmPasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        onConfirm={handlePasswordConfirm}
        title="Confirm Admin Password"
      />
      <StaffCreatedSuccessModal
        isOpen={showSuccessModal}
        onViewAccounts={() => {
          setShowSuccessModal(false);
          if (onSuccess) onSuccess();
        }}
      />
    </React.Fragment>
  );
}

function StaffCreatedSuccessModal({ isOpen, onViewAccounts }) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <Portal>
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.55)",
          backdropFilter: "blur(4px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
        }}
      >
        <div
          style={{
            background: "#ffffff",
            borderRadius: "24px",
            padding: "48px 40px 40px",
            maxWidth: "420px",
            width: "92%",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
            textAlign: "center",
            position: "relative",
          }}
        >
          {/* Success icon */}
          <div
            style={{
              width: "72px",
              height: "72px",
              borderRadius: "50%",
              background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px",
              boxShadow: "0 8px 24px rgba(34,197,94,0.35)",
            }}
          >
            <FiCheckCircle style={{ color: "#ffffff", fontSize: "34px" }} />
          </div>

          <h2
            style={{
              fontSize: "22px",
              fontWeight: 700,
              color: "#0f172a",
              margin: "0 0 10px 0",
              letterSpacing: "-0.3px",
            }}
          >
            Staff Account Created
          </h2>
          <p
            style={{
              fontSize: "14px",
              color: "#64748b",
              margin: "0 0 32px 0",
              lineHeight: 1.6,
            }}
          >
            The new staff account has been successfully created. You can view and manage it in the Accounts table.
          </p>

          <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
            <button
              onClick={onViewAccounts}
              style={{
                padding: "12px 28px",
                borderRadius: "14px",
                border: "none",
                background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                color: "#ffffff",
                fontSize: "14px",
                fontWeight: 700,
                cursor: "pointer",
                transition: "opacity 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.88")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
            >
              <FiUsers style={{ fontSize: "15px" }} />
              View Accounts
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}

export default AdminDashboard;